import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, CheckCircle, XCircle, Loader2, Users, Tag, FolderTree, Store, Trash2, Plus, MapPin, Edit2, Settings, UserPlus, Eye, EyeOff, Upload, Download, UserCheck, Phone, Mail, Globe, MessageSquare, Image, FileImage, Hash, KeyRound, BarChart3, Search, ArrowUpAZ, ArrowDownAZ, ImageIcon, RefreshCw, AlertTriangle, AlertCircle, ChevronLeft, ChevronRight, FileText, CreditCard, Calendar, Clock, Copy, Package, ArrowRight, ExternalLink, GripVertical, Tent, PartyPopper, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import SimpleRichTextEditor from './SimpleRichTextEditor';
import { useAutoLogout } from '../hooks/useAutoLogout';
import { fetchWithRetry } from '../lib/fetchWithRetry';
import { toast } from 'sonner';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { trackStallCreated, trackDashboardOpened, trackBackendActivity, getAnalyticsSummary, getStoredEvents, clearStoredEvents, getAllLinkClickAnalytics } from '../lib/analytics';
import { getImageUrl, getThumbnailUrl, bustImageCache } from '../lib/imageUtils';
import { AdminMediaTab } from './admin';
import { AdminVendorsTab } from './admin';
import { AdminOptionsTab } from './admin';
import { EmailDashboardModal } from './admin/EmailDashboardModal';
import { ProvincesModal } from './admin/ProvincesModal';
import { SortModePicker, EditableName } from './admin/ListManagerControls';
import CheckboxListWithSearch from './shared/CheckboxListWithSearch';
import { AdminAnalyticsTab } from './admin';
import { AdminStallsTab } from './admin';
import { AdminPayFastTab } from './admin';

// Panel that renders the failed rows from a bulk upload — visible inside the upload modal.
// Includes a "Download errors as CSV" button so the admin can hand it back to whoever generated the file.
const BulkUploadErrorsPanel = ({ errors, fileNameHint = 'failed-rows' }) => {
  if (!errors || errors.length === 0) return null;

  const downloadCsv = () => {
    const header = 'Row,Email/Name,Reason\n';
    const rows = errors.map(e => {
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      return `${e.row},${esc(e.email)},${esc(e.reason)}`;
    }).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileNameHint}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 rounded-lg p-4 mt-4"
      data-testid="bulk-upload-errors-panel"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h4 className="font-bold text-red-800 dark:text-red-300 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-6 h-6 bg-red-500 text-white rounded-full text-xs">!</span>
          {errors.length} row{errors.length !== 1 ? 's' : ''} failed
        </h4>
        <button
          type="button"
          onClick={downloadCsv}
          data-testid="download-errors-csv"
          className="text-xs font-medium bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-full"
        >
          Download errors as CSV
        </button>
      </div>

      <p className="text-xs text-red-700 dark:text-red-400 mb-3">
        Fix the rows below in your CSV and re-upload just those rows. Successful rows have already been saved.
      </p>

      <div className="max-h-64 overflow-y-auto rounded border border-red-200 dark:border-red-800 bg-white dark:bg-[#1a1a1a]">
        <table className="w-full text-xs" data-testid="bulk-upload-errors-table">
          <thead className="bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 w-16">Row</th>
              <th className="text-left px-3 py-2">Email / Name</th>
              <th className="text-left px-3 py-2">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-red-100 dark:divide-red-900/40">
            {errors.map((e, idx) => (
              <tr key={idx} className="hover:bg-red-50 dark:hover:bg-red-900/20">
                <td className="px-3 py-2 font-mono text-red-700 dark:text-red-300">{e.row}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 break-all">{e.email}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 break-words">{e.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdminDashboard = ({ isOpen, onClose }) => {
  const { token, logout } = useAuth();
  const [pendingVendors, setPendingVendors] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [typeAccessRequests, setTypeAccessRequests] = useState([]);
  const [attentionVendors, setAttentionVendors] = useState([]);
  const [manualSuspendEmail, setManualSuspendEmail] = useState('');
  const [manualSuspending, setManualSuspending] = useState(false);
  const [showPayFastSyncModal, setShowPayFastSyncModal] = useState(false);
  const [payFastSyncText, setPayFastSyncText] = useState('');
  const [payFastSyncing, setPayFastSyncing] = useState(false);
  const [payFastLiveSyncing, setPayFastLiveSyncing] = useState(false);
  const [payFastLiveSyncResult, setPayFastLiveSyncResult] = useState(null);
  const [payFastSyncResult, setPayFastSyncResult] = useState(null);
  // Active tab on the Add/Edit Stall Location sections (per-modal)
  const [adminNewStallTab, setAdminNewStallTab] = useState('market');
  const [adminEditStallTab, setAdminEditStallTab] = useState('market');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Auto logout after 10 minutes of inactivity
  const handleAutoLogout = useCallback(() => {
    logout();
    onClose();
  }, [logout, onClose]);
  
  useAutoLogout(10, handleAutoLogout, isOpen);
  
  // User filter for Users tab (All, Vendor, Admin)
  const [userFilter, setUserFilter] = useState('all');
  
  // Stall filter for Stalls tab (All, Assigned, Unassigned)
  const [stallFilter, setStallFilter] = useState('all');
  
  // Category & Tag & Market Management - NO MOCK DATA
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [draggedMarket, setDraggedMarket] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newMarket, setNewMarket] = useState('');
  
  // Expos & Festivals
  const [expos, setExpos] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [newExpo, setNewExpo] = useState('');
  const [newFestival, setNewFestival] = useState('');
  const [draggedExpo, setDraggedExpo] = useState(null);
  const [draggedFestival, setDraggedFestival] = useState(null);

  // Sort-mode for each of the 5 admin-managed lists — persisted server-side
  // via GET/PUT /api/categories/sort-modes and applied to the public GETs.
  const [sortModes, setSortModes] = useState({
    categories: 'az', tags: 'az', markets: 'custom', expos: 'custom', festivals: 'custom',
  });
  
  // Stall Management - NO MOCK DATA
  const [allStalls, setAllStalls] = useState([]);
  const [editingStall, setEditingStall] = useState(null);
  const [locationEdit, setLocationEdit] = useState({ lat: '', lng: '' });
  const [editingStallData, setEditingStallData] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editStallFiles, setEditStallFiles] = useState({ logo: null, banner: null });
  const [editStallSelectedTags, setEditStallSelectedTags] = useState([]);
  const [editStallMarketLocations, setEditStallMarketLocations] = useState({});
  const [editStallVendorId, setEditStallVendorId] = useState(null);
  const [savingEditStall, setSavingEditStall] = useState(false);
  const [categoryUsage, setCategoryUsage] = useState({});
  const [tagUsage, setTagUsage] = useState({});
  const [marketUsage, setMarketUsage] = useState({});
  const [showExportStallsModal, setShowExportStallsModal] = useState(false);
  const [showBulkDeleteStallsModal, setShowBulkDeleteStallsModal] = useState(false);
  const [stallsToDelete, setStallsToDelete] = useState('');
  const [stallExportFilter, setStallExportFilter] = useState('all');
  const [stallExportSearch, setStallExportSearch] = useState('');
  // Add Stall functionality
  const [showAddStallModal, setShowAddStallModal] = useState(false);
  const [newStall, setNewStall] = useState({
    stallName: '',
    stallNumber: '',
    market: '',
    category: 'General',
    slogan: '',
    about: '',
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    socialMedia: { facebook: '', instagram: '', tiktok: '', twitter: '', linkedin: '' },
    tags: [],
    location: { lat: '', lng: '' },
    vendorId: '',
    status: 'draft'
  });

  // Export/Bulk Delete functionality
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [exportFilter, setExportFilter] = useState('all');
  const [exportSearch, setExportSearch] = useState('');

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedStallsForExport, setSelectedStallsForExport] = useState([]);
  const [selectedStallsForDelete, setSelectedStallsForDelete] = useState([]);

  // Bulk Upload functionality
  const [showBulkUploadVendors, setShowBulkUploadVendors] = useState(false);
  const [showBulkUploadStalls, setShowBulkUploadStalls] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [uploadStep, setUploadStep] = useState(1); // 1: upload, 2: mapping, 3: preview
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, status: '' });
  const [uploadErrors, setUploadErrors] = useState([]); // { row, email, reason } list from last bulk upload
  const [newStallFiles, setNewStallFiles] = useState({ logo: null, banner: null });
  
  // New stall multi-market and tag selection
  const [newStallSelectedMarkets, setNewStallSelectedMarkets] = useState([]);
  const [newStallMarketLocations, setNewStallMarketLocations] = useState({});
  const [newStallSelectedTags, setNewStallSelectedTags] = useState([]);
  
  // Manual user creation
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    surname: '',
    companyName: '',
    email: '',
    phone: '',
    password: '',
    role: 'vendor',
    vendorType: 'market',
    stallNumber: '',
    subscription: 'cash'
  });
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);
  
  // Edit User functionality
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    phone: '',
    role: 'vendor',
    vendorType: 'market',
    status: 'approved',
    subscription: 'cash',
    newPassword: ''
  });
  const [savingEditUser, setSavingEditUser] = useState(false);
  const [showEditUserNewPassword, setShowEditUserNewPassword] = useState(false);
  
  // Transactions state for PayFast tab
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // Analytics state
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [analyticsEvents, setAnalyticsEvents] = useState([]);
  const [linkClickAnalytics, setLinkClickAnalytics] = useState(null);
  const [showAllDirections, setShowAllDirections] = useState(false);
  
  // Search and Sort for Users tab
  const [userSearch, setUserSearch] = useState('');
  const [userSortOrder, setUserSortOrder] = useState('asc'); // 'asc' or 'desc'
  
  // Search and Sort for Stalls tab
  const [stallSearch, setStallSearch] = useState('');
  const [stallSortOrder, setStallSortOrder] = useState('asc'); // 'asc' or 'desc'
  
  // Pagination for Users tab
  const [usersPerPage, setUsersPerPage] = useState('20');
  const [usersCurrentPage, setUsersCurrentPage] = useState(1);
  
  // Pagination for Stalls tab
  const [stallsPerPage, setStallsPerPage] = useState('20');
  const [stallsCurrentPage, setStallsCurrentPage] = useState(1);

  // Helper: check if trial is expired (same logic as AdminVendorsTab)
  const isTrialExpired = (user) => {
    if (user.subscription !== 'trial') return false;
    if (!user.trial_start_date) return true;
    const trialStart = new Date(user.trial_start_date);
    const trialEnd = new Date(trialStart.getTime() + 7 * 24 * 60 * 60 * 1000);
    return new Date() > trialEnd;
  };

  // Compute filtered vendors for export/delete (uses export modal's own filter/search)
  const exportFilteredVendors = useMemo(() => {
    const approved = allVendors.filter(v => v.status === 'approved' || v.status === 'active');
    return approved
      .filter(v => {
        if (exportFilter === 'all') return true;
        if (exportFilter === 'vendor') return v.role === 'vendor';
        if (exportFilter === 'admin') return v.role === 'admin';
        if (exportFilter === 'market-vendor') return v.role === 'vendor' && (v.vendor_type === 'market' || !v.vendor_type);
        if (exportFilter === 'expo-vendor') return v.role === 'vendor' && v.vendor_type === 'expo';
        if (exportFilter === 'festival-vendor') return v.role === 'vendor' && v.vendor_type === 'festival';
        if (exportFilter === 'yearly') return v.role === 'vendor' && v.subscription === 'yearly';
        if (exportFilter === 'monthly') return v.role === 'vendor' && v.subscription === 'monthly';
        if (exportFilter === 'cash') return v.role === 'vendor' && (!v.subscription || v.subscription === 'cash');
        if (exportFilter === 'manual') return v.role === 'vendor' && v.subscription === 'manual';
        if (exportFilter === 'active-trial') return v.role === 'vendor' && v.subscription === 'trial' && !isTrialExpired(v);
        if (exportFilter === 'expired-trial') return v.role === 'vendor' && v.subscription === 'trial' && isTrialExpired(v);
        return true;
      })
      .filter(v => {
        if (!exportSearch) return true;
        const search = exportSearch.toLowerCase();
        const stall = v.stall_id ? allStalls.find(s => s._id === v.stall_id) : null;
        return (
          (v.name || '').toLowerCase().includes(search) ||
          (v.email || '').toLowerCase().includes(search) ||
          (stall?.stallName || '').toLowerCase().includes(search) ||
          (v.first_name || '').toLowerCase().includes(search) ||
          (v.last_name || '').toLowerCase().includes(search) ||
          (v.phone || '').toLowerCase().includes(search)
        );
      });
  }, [allVendors, allStalls, exportFilter, exportSearch]);

  // Compute filtered stalls for export/delete (uses stall modal's own filter/search)
  const exportFilteredStalls = useMemo(() => {
    return allStalls
      .filter(stall => {
        if (stallExportFilter === 'all') return true;
        if (stallExportFilter === 'assigned') return stall.vendorId || stall.created_by;
        if (stallExportFilter === 'unassigned') return !stall.vendorId && !stall.created_by;
        if (stallExportFilter === 'published') return stall.status === 'published';
        if (stallExportFilter === 'draft') return !stall.status || stall.status === 'draft';
        return true;
      })
      .filter(stall => {
        if (!stallExportSearch) return true;
        const search = stallExportSearch.toLowerCase();
        return (
          (stall.stallName || '').toLowerCase().includes(search) ||
          (stall.stallNumber || '').toLowerCase().includes(search) ||
          (stall.category || '').toLowerCase().includes(search) ||
          (stall.market || '').toLowerCase().includes(search)
        );
      });
  }, [allStalls, stallExportFilter, stallExportSearch]);

  // Media management state
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [mediaSearch, setMediaSearch] = useState('');
  const [orphanedCount, setOrphanedCount] = useState(0);
  const [mediaFilter, setMediaFilter] = useState('all'); // 'all', 'logo', 'banner', 'orphaned'
  
  // Options modals state
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [showMarketsModal, setShowMarketsModal] = useState(false);
  const [showExposModal, setShowExposModal] = useState(false);
  const [showFestivalsModal, setShowFestivalsModal] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [showEmailDashboard, setShowEmailDashboard] = useState(false);
  const [showProvincesModal, setShowProvincesModal] = useState(false);
  const [tagModalMessage, setTagModalMessage] = useState(null);
  const [categoryModalMessage, setCategoryModalMessage] = useState(null);
  const [marketModalMessage, setMarketModalMessage] = useState(null);
  const [expoModalMessage, setExpoModalMessage] = useState(null);
  const [festivalModalMessage, setFestivalModalMessage] = useState(null);
  
  // Default legal content (same as LegalModal.jsx for consistency)
  const defaultLegalContent = {
    terms: {
      title: 'Terms and Conditions',
      content: `Wayo Platform Use Agreement

Last updated: 05 February 2026

1. Introduction

Welcome to Wayo ("Wayo", "we", "us", "our").

Wayo is an online directory platform that enables market vendors to create paid digital listings for their physical market stalls and enables members of the public to discover those vendors.

By accessing or using the platform, you agree to be bound by these Terms and Conditions. If you do not agree, you may not use the platform.

2. Definitions

Platform: The Wayo website, applications, and related services

Vendor: Any individual or business subscribing to list a market stall

Visitor: Any user browsing the platform

Listing: Vendor-submitted content including descriptions, images, and contact details

Subscription: A paid monthly listing access plan

Market: A physical, in-person trading environment

3. Nature of the Service

Wayo is a paid vendor directory and discovery platform only.

Wayo does not:

Facilitate sales or payments between vendors and customers

Act as an agent, broker, or intermediary

Verify vendors, products, or services

Guarantee availability, pricing, quality, legality, or compliance

All interactions, enquiries, and transactions occur independently and outside the platform.

4. Vendor Accounts and Listings

Vendors are solely responsible for all information submitted.

By creating a listing, vendors confirm that:

All information is accurate, lawful, and not misleading

They are authorised to represent the stall or business

They comply with all applicable laws, regulations, and market rules

Wayo reserves the right to edit, suspend, or remove listings at its sole discretion.

5. Prohibited Use (Robust – Expanded)

You may not, directly or indirectly, use the platform to:

5.1 Misrepresentation and Deception

Provide false, misleading, or incomplete information

Misrepresent products, pricing, availability, or market attendance

Claim unauthorised affiliations or endorsements

5.2 Illegal or Unlawful Activity

Promote or display illegal, restricted, or regulated goods or services

Breach consumer protection, health, safety, or trading laws

Infringe intellectual property rights

5.3 Harmful, Offensive, or Unsafe Content

Upload defamatory, abusive, hateful, discriminatory, or threatening content

Publish sexually explicit, violent, or inappropriate material

Promote exploitation, harassment, or unsafe practices

5.4 Platform Abuse and Technical Misuse

Interfere with platform operation or security

Scrape, copy, or harvest data without permission

Use bots, automation, or unauthorised scripts

5.5 Advertising and Monetisation Abuse

Attempt to manipulate, interfere with, or bypass advertising systems

Click on advertisements displayed on your own listings or encourage others to do so

Use misleading content to artificially increase ad impressions or clicks

Violate Google AdSense or third-party advertising policies

5.6 Impersonation and Fraud

Impersonate individuals, vendors, markets, or organisations

Create duplicate, deceptive, or fraudulent listings

Provide false identities or contact details

5.7 Circumvention of Enforcement

Bypass moderation or enforcement actions

Recreate accounts after suspension or termination

Assist others in violating these Terms

Violations may result in immediate suspension or permanent termination without notice and without refund.

6. Content Ownership and Licence

Vendors retain ownership of submitted content.

By submitting content, vendors grant Wayo a non-exclusive, worldwide, royalty-free licence to host, display, reproduce, and promote such content in connection with the platform and its marketing.

7. Advertising and Third-Party Monetisation (Google AdSense)

7.1 Advertising Display

Wayo displays third-party advertisements, including advertisements served by Google AdSense, across the platform.

Advertisements may appear:

On vendor listings

Within browsing pages

Adjacent to content

7.2 No Endorsement

The display of advertisements does not constitute endorsement by Wayo of any advertised product or service.

7.3 Cookies and Data Usage

Third-party advertisers, including Google, may:

Use cookies or similar technologies

Collect anonymised usage data

Serve ads based on user activity

Users consent to such data processing by continuing to use the platform. More information is provided in the Privacy Policy.

7.4 Advertising Compliance

Users and vendors may not:

Interfere with ad delivery

Attempt to influence ad performance

Block or obscure advertisements in a manner that violates platform rules

8. Subscriptions and Payments

8.1 Paid Listings

Vendor listings are available only via paid subscription at R25.00 per month.

8.2 Payment Processing

Payments are processed via PayFast

Wayo does not store payment information

Vendors agree to PayFast's terms in addition to these Terms

8.3 Billing and Failure

Subscriptions renew monthly in advance

Failed payments may result in listing suspension or removal

8.4 Cancellation

Vendors may cancel at any time

Cancellation takes effect at the end of the current billing cycle

No partial refunds are issued

8.5 Price Changes

Wayo may adjust subscription pricing with reasonable notice. Continued use constitutes acceptance.

9. No Warranty

The platform is provided "as is" and "as available".

Wayo makes no warranties regarding:

Listing visibility or traffic

Vendor performance or reliability

Market participation or availability

10. Limitation of Liability

To the maximum extent permitted by law:

Wayo is not liable for disputes, losses, or damages arising from vendor-customer interactions

Wayo is not responsible for vendor conduct, products, or services

11. Suspension and Termination

Wayo may suspend or terminate access at any time if:

These Terms are breached

Advertising or payment systems are abused

Required by law

12. Governing Law

These Terms are governed by the laws of the Republic of South Africa.`
    },
    privacy: {
      title: 'Privacy Policy',
      content: `Privacy Policy (POPIA & Advertising Aligned)

1. Overview

Wayo processes personal information in accordance with the Protection of Personal Information Act (POPIA).

2. Information Collected

Vendors:

Business and contact details

Listing content and images

Visitors:

Usage and analytics data

Device and browser information

3. Advertising Data

Third-party advertising partners, including Google AdSense, may collect anonymised data via cookies to serve relevant advertisements.

Wayo does not control third-party data collection practices.

4. Purpose of Processing

Information is used to:

Operate the platform

Display listings and advertisements

Improve user experience

Comply with legal obligations

5. Data Sharing

Personal information is not sold or rented.

Limited sharing occurs only with:

Platform service providers

Advertising partners

Legal authorities when required

6. Security

Appropriate safeguards are implemented to protect personal information.

7. User Rights

Users may request access, correction, or deletion of personal data as permitted by law.

8. Cookies

Wayo uses functional, analytical, and advertising cookies. Continued use constitutes consent.`
    },
    other: {
      title: 'Other Legal Information',
      content: `Signup

By creating an account, you agree to Wayo's Terms & Privacy Policy. Wayo is a paid vendor directory, not a sales platform.

Subscription Checkout

You are subscribing to a monthly vendor listing for R25.00. Payments are processed securely via PayFast.

Advertising Notice

This platform displays third-party advertisements, including Google AdSense.

Listing Disclaimer

Vendor information is provided by third parties. Wayo does not verify or endorse vendors or products.`
    }
  };
  
  // Legal content state - initialized with default content
  const [legalContent, setLegalContent] = useState(defaultLegalContent);
  const [selectedLegalType, setSelectedLegalType] = useState('terms');
  const [legalLoading, setLegalLoading] = useState(false);

  const API_URL = window.location.origin;

  // Auto-dismiss messages after 20 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 20000);
      return () => clearTimeout(timer);
    }
  }, [message.text]);

  useEffect(() => {
    if (isOpen) {
      fetchVendors();
      fetchCategoriesAndTags();
      fetchStalls();
      fetchMedia(); // Auto-load media files
      fetchTypeAccessRequests();
      fetchAttentionVendors();
      // Track dashboard opened
      trackDashboardOpened('admin');
      // Load analytics data
      refreshAnalytics();
    }
  }, [isOpen]);

  const fetchTypeAccessRequests = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/type-access-requests?status=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setTypeAccessRequests(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch type-access requests', e);
    }
  };

  // Vendors with failed / expired payments or already suspended — used to
  // populate the "Needs Attention" panel on the Pending tab.
  const fetchAttentionVendors = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/vendors/attention`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAttentionVendors(data.vendors || []);
      }
    } catch (e) {
      console.error('Failed to fetch attention vendors', e);
    }
  };

  const handleSuspendForPayment = async (userId, email) => {
    if (!window.confirm(`Suspend ${email} for a failed payment? This will draft their stall.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/vendors/${userId}/suspend-for-payment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage({ type: 'error', text: data.detail || 'Suspend failed' });
        return;
      }
      setMessage({ type: 'success', text: `${email} suspended${data.stall_drafted ? ' & stall drafted' : ''}.` });
      fetchAttentionVendors();
      fetchVendors(false);
    } catch (e) {
      setMessage({ type: 'error', text: 'Network error' });
    }
  };

  // Manually suspend a vendor by email — for cases where PayFast reported a
  // failure but the ITN webhook never reached the app.
  const handleManualSuspendByEmail = async () => {
    const email = manualSuspendEmail.trim().toLowerCase();
    if (!email) { setMessage({ type: 'error', text: 'Enter a vendor email' }); return; }
    setManualSuspending(true);
    try {
      const vendor = allVendors.find(v => (v.email || '').toLowerCase() === email);
      if (!vendor) {
        setMessage({ type: 'error', text: `No vendor found with email "${email}"` });
        setManualSuspending(false);
        return;
      }
      if (!window.confirm(`Suspend ${vendor.email} for a failed payment? This will draft their stall and lock the account.`)) {
        setManualSuspending(false);
        return;
      }
      const res = await fetch(`${API_URL}/api/admin/vendors/${vendor._id}/suspend-for-payment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage({ type: 'error', text: data.detail || 'Suspend failed' });
      } else {
        setMessage({ type: 'success', text: `${vendor.email} suspended${data.stall_drafted ? ' & stall drafted' : ''}.` });
        setManualSuspendEmail('');
        fetchAttentionVendors();
        fetchVendors(false);
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Network error' });
    } finally {
      setManualSuspending(false);
    }
  };

  // ── PRIMARY: one-click live sync via PayFast Subscriptions API ─────
  // Zero manual work — hits the backend which loops through every vendor
  // with a stored subscription_token and asks PayFast for their current
  // status directly. Same apply-rules as the CSV sync.
  const handlePayFastLiveSync = async () => {
    setPayFastLiveSyncing(true);
    setPayFastLiveSyncResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/sync-live-from-payfast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage({ type: 'error', text: data.detail || 'Live sync failed' });
      } else {
        setPayFastLiveSyncResult(data);
        setMessage({
          type: 'success',
          text: `Live sync checked ${data.checked} vendors · ${data.suspended} suspended · ${data.published} published`,
        });
        fetchAttentionVendors();
        fetchVendors(false);
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Network error during live sync' });
    } finally {
      setPayFastLiveSyncing(false);
    }
  };

  const handlePayFastSync = async () => {
    const raw = payFastSyncText.trim();
    if (!raw) { setMessage({ type: 'error', text: 'Paste subscription data first' }); return; }
    setPayFastSyncing(true);
    setPayFastSyncResult(null);

    let subscriptions = [];
    try {
      // Try JSON first
      if (raw.startsWith('[') || raw.startsWith('{')) {
        const parsed = JSON.parse(raw);
        subscriptions = Array.isArray(parsed) ? parsed : (parsed.subscriptions || []);
      } else {
        // CSV/TSV: assume header row includes email + status columns
        const lines = raw.split(/\r?\n/).filter(l => l.trim());
        if (lines.length < 2) throw new Error('CSV needs at least a header + one row');
        const delim = lines[0].includes('\t') ? '\t' : ',';
        const headers = lines[0].split(delim).map(h => h.trim().toLowerCase().replace(/\s+/g, ''));
        const findIdx = (opts) => headers.findIndex(h => opts.includes(h));
        const emailIdx = findIdx(['email', 'customeremail', 'payeremail']);
        const statusIdx = findIdx(['status', 'subscriptionstatus']);
        const planIdx = findIdx(['plan', 'frequency', 'itemname']);
        if (emailIdx === -1 || statusIdx === -1) {
          throw new Error('CSV must include Email and Status columns');
        }
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(delim).map(c => c.trim());
          const email = (cols[emailIdx] || '').replace(/\s+/g, '').toLowerCase();
          if (!email) continue;
          const status = cols[statusIdx] || '';
          let plan = planIdx > -1 ? (cols[planIdx] || '').toLowerCase() : '';
          if (plan.includes('year') || plan.includes('annual')) plan = 'yearly';
          else if (plan.includes('month')) plan = 'monthly';
          else plan = '';
          subscriptions.push({ email, status, plan });
        }
      }
    } catch (e) {
      setMessage({ type: 'error', text: `Parse error: ${e.message}` });
      setPayFastSyncing(false);
      return;
    }

    if (subscriptions.length === 0) {
      setMessage({ type: 'error', text: 'No subscriptions parsed — check the format' });
      setPayFastSyncing(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/sync-from-payfast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscriptions })
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage({ type: 'error', text: data.detail || 'Sync failed' });
      } else {
        setPayFastSyncResult(data);
        setMessage({ type: 'success', text: `Synced ${subscriptions.length} rows · ${data.suspended} suspended · ${data.published} published` });
        fetchAttentionVendors();
        fetchVendors(false);
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Network error during sync' });
    } finally {
      setPayFastSyncing(false);
    }
  };

  const handleTypeAccessDecision = async (requestId, decision) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/type-access-requests/${requestId}/${decision}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage({ type: 'error', text: data.detail || 'Failed' });
        return;
      }
      setMessage({ type: 'success', text: data.message });
      fetchTypeAccessRequests();
      fetchVendors(false);
    } catch (e) {
      setMessage({ type: 'error', text: 'Network error' });
    }
  };

  const handleAdminGrantType = async (vendorId, typeToGrant) => {
    // Optimistic update so the tab flips to picker immediately
    setAllVendors(prev => prev.map(v => {
      if (v._id !== vendorId) return v;
      const currentTypes = v.vendor_types && v.vendor_types.length > 0
        ? v.vendor_types
        : [v.vendor_type || 'market'];
      if (currentTypes.includes(typeToGrant)) return v;
      return { ...v, vendor_types: [...currentTypes, typeToGrant] };
    }));
    try {
      const res = await fetch(`${API_URL}/api/admin/vendors/${vendorId}/grant-type`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: typeToGrant }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to grant access');
        fetchVendors(false); // revert to server state
        return;
      }
      toast.success(data.message || `Access to ${typeToGrant} granted`);
      fetchVendors(false); // resync
    } catch (e) {
      toast.error('Network error');
      fetchVendors(false);
    }
  };

  const handleAdminRevokeType = async (vendorId, typeToRevoke) => {
    // Optimistic update
    setAllVendors(prev => prev.map(v => {
      if (v._id !== vendorId) return v;
      const currentTypes = v.vendor_types && v.vendor_types.length > 0
        ? v.vendor_types
        : [v.vendor_type || 'market'];
      return { ...v, vendor_types: currentTypes.filter(x => x !== typeToRevoke) };
    }));
    try {
      const res = await fetch(`${API_URL}/api/admin/vendors/${vendorId}/revoke-type`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: typeToRevoke }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to revoke access');
        fetchVendors(false);
        return;
      }
      toast.success(data.message || `Access to ${typeToRevoke} revoked`);
      fetchVendors(false);
    } catch (e) {
      toast.error('Network error');
      fetchVendors(false);
    }
  };

  // When a stall is selected for editing, default the Location tab to the vendor's primary type
  // (only when the vendor changes — NOT on every allVendors refresh, which would clobber the
  // admin's active tab after a grant/revoke).
  useEffect(() => {
    if (editStallVendorId) {
      const vendor = allVendors.find(v => v._id === editStallVendorId);
      setAdminEditStallTab(vendor?.vendor_type || 'market');
    }
  }, [editStallVendorId]); // intentionally omit allVendors

  // Same for the Add Stall modal when a vendor is picked
  useEffect(() => {
    if (newStall?.vendorId) {
      const vendor = allVendors.find(v => v._id === newStall.vendorId);
      setAdminNewStallTab(vendor?.vendor_type || 'market');
    }
  }, [newStall?.vendorId]); // intentionally omit allVendors

  const refreshAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/summary`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalyticsSummary({
          today: {
            total: data.today?.total || 0,
            appOpened: data.today?.appOpened || 0,
            screenViews: data.today?.stallViewed || 0,
            loginSuccess: data.today?.loginSuccess || 0,
            loginFailure: data.today?.loginFailure || 0,
            stallViewed: data.today?.stallViewed || 0,
            stallCreated: data.today?.stallCreated || 0,
            errors: data.today?.errors || 0,
            backendActivity: data.today?.linkClicks || 0
          },
          last7Days: {
            total: data.last7Days?.total || 0,
            appOpened: data.last7Days?.appOpened || 0,
            stallViewed: data.last7Days?.stallViewed || 0,
            loginSuccess: data.last7Days?.loginSuccess || 0
          },
          last30Days: data.last30Days || {},
          topStalls: data.topStalls || [],
          topCategories: data.topCategories || [],
          topTags: data.topTags || [],
          topMarkets: data.topMarkets || [],
          recentEvents: data.recentEvents || []
        });
        // Use server-side link click analytics
        if (data.linkClickAnalytics) {
          setLinkClickAnalytics(data.linkClickAnalytics);
        }
      } else {
        // Fallback to localStorage if server fails
        setAnalyticsSummary(getAnalyticsSummary());
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      // Fallback to localStorage only when server fails
      setAnalyticsSummary(getAnalyticsSummary());
      setLinkClickAnalytics(getAllLinkClickAnalytics());
    }
    setAnalyticsEvents(getStoredEvents());
  };

  const handleClearAnalytics = () => {
    if (window.confirm('Are you sure you want to clear all local analytics data? Server data will remain.')) {
      clearStoredEvents();
      refreshAnalytics();
      setMessage({ type: 'success', text: 'Local analytics data cleared' });
    }
  };

  const fetchVendors = async (showError = true) => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        fetchWithRetry(`${API_URL}/api/admin/pending-vendors`, {
          headers: { Authorization: `Bearer ${token}` },
        }, { retries: 4, backoffMs: 500 }),
        fetchWithRetry(`${API_URL}/api/admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
        }, { retries: 4, backoffMs: 500 }),
      ]);

      if (pendingRes.ok) {
        const pending = await pendingRes.json();
        setPendingVendors(pending);
      }
      if (allRes.ok) {
        const all = await allRes.json();
        setAllVendors(all);
      }
      if (!pendingRes.ok && !allRes.ok && showError) {
        setMessage({ type: 'error', text: 'Failed to load users' });
      }
    } catch (error) {
      if (showError) {
        setMessage({ type: 'error', text: 'Failed to load users' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch recent transactions for PayFast tab
  const fetchRecentTransactions = async () => {
    
    setLoadingTransactions(true);
    try {
      const response = await fetch(`${API_URL}/api/payfast/transactions?limit=20`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        setRecentTransactions(data.transactions || []);
      } else {
        console.error('Failed to fetch transactions:', response.statusText);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchCategoriesAndTags = async () => {
    // Also refresh the admin's sort-mode choices for each list
    loadSortModes();
    try {
      const [catRes, tagRes, marketRes, expoRes, festivalRes, stallsRes] = await Promise.all([
        fetch(`${API_URL}/api/categories/`),
        fetch(`${API_URL}/api/categories/tags`),
        fetch(`${API_URL}/api/categories/markets`),
        fetch(`${API_URL}/api/categories/expos`),
        fetch(`${API_URL}/api/categories/festivals`),
        fetch(`${API_URL}/api/stalls/`),
      ]);

      if (catRes.ok) {
        const cats = await catRes.json();
        
        setCategories(cats);
        localStorage.setItem('wayo_categories', JSON.stringify(cats));
      } else {
        console.error('Failed to fetch categories, status:', catRes.status);
        const cached = localStorage.getItem('wayo_categories');
        if (cached) setCategories(JSON.parse(cached));
      }
      
      if (tagRes.ok) {
        const tgs = await tagRes.json();
        
        setTags(tgs);
        localStorage.setItem('wayo_tags', JSON.stringify(tgs));
      } else {
        console.error('Failed to fetch tags, status:', tagRes.status);
        const cached = localStorage.getItem('wayo_tags');
        if (cached) setTags(JSON.parse(cached));
      }

      if (marketRes.ok) {
        const mkts = await marketRes.json();
        
        setMarkets(mkts);
        localStorage.setItem('wayo_markets', JSON.stringify(mkts));
      } else {
        console.error('Failed to fetch markets, status:', marketRes.status);
        const cached = localStorage.getItem('wayo_markets');
        if (cached) setMarkets(JSON.parse(cached));
      }

      if (expoRes.ok) {
        setExpos(await expoRes.json());
      }
      if (festivalRes.ok) {
        setFestivals(await festivalRes.json());
      }
      
      // Calculate usage counts
      if (stallsRes.ok) {
        const stallsData = await stallsRes.json();
        
        // Count category usage
        const catCount = {};
        stallsData.forEach(stall => {
          if (stall.category) {
            catCount[stall.category] = (catCount[stall.category] || 0) + 1;
          }
        });
        setCategoryUsage(catCount);
        
        // Count tag usage
        const tagCount = {};
        stallsData.forEach(stall => {
          if (stall.tags) {
            stall.tags.forEach(tag => {
              tagCount[tag] = (tagCount[tag] || 0) + 1;
            });
          }
        });
        setTagUsage(tagCount);
        
        // Count market usage
        const mktCount = {};
        stallsData.forEach(stall => {
          // Check primary market
          if (stall.market) {
            mktCount[stall.market] = (mktCount[stall.market] || 0) + 1;
          }
          // Check locations array for multi-market stalls
          if (stall.locations && Array.isArray(stall.locations)) {
            stall.locations.forEach(loc => {
              if (loc.market && loc.market !== stall.market) {
                mktCount[loc.market] = (mktCount[loc.market] || 0) + 1;
              }
            });
          }
        });
        setMarketUsage(mktCount);
      }
    } catch (error) {
      console.error('Failed to load categories/tags', error);
    }
  };

  const fetchStalls = async () => {
    try {
      // Admin fetches ALL stalls (including drafts) via admin endpoint
      const res = await fetch(`${API_URL}/api/admin/stalls`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        
        setAllStalls(data);
      } else {
        console.error('Failed to fetch stalls, status:', res.status);
      }
    } catch (error) {
      console.error('Failed to load stalls', error);
    }
  };

  const handleApproval = async (vendorId, status) => {
    setProcessing(vendorId);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_URL}/api/admin/vendors/${vendorId}/approve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });

      if (response.ok) {
        setMessage({
          type: 'success',
          text: `Vendor ${status === 'approved' ? 'approved' : 'declined'} successfully!`,
        });
        fetchVendors(false);
        fetchCategoriesAndTags();
        fetchStalls();
      } else {
        throw new Error('Failed to process vendor');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    setProcessing(vendorId);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_URL}/api/admin/vendors/${vendorId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok || response.status === 204) {
        setMessage({ type: 'success', text: 'User deleted successfully!' });
        setAllVendors(allVendors.filter(v => v._id !== vendorId));
        setPendingVendors(pendingVendors.filter(v => v._id !== vendorId));
        fetchVendors(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete user');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to delete user' });
    } finally {
      setProcessing(null);
    }
  };

  const handleResetPassword = async (vendorEmail) => {
    setProcessing(vendorEmail);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: vendorEmail }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `Password reset link sent to ${vendorEmail}` });
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to send reset password link');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to send reset password link' });
    } finally {
      setProcessing(null);
    }
  };

  // Open Edit User Modal
  const handleOpenEditUser = (vendor) => {
    setEditingUser(vendor);
    setEditUserData({
      firstName: vendor.first_name || vendor.name?.split(' ')[0] || '',
      lastName: vendor.last_name || vendor.name?.split(' ').slice(1).join(' ') || '',
      companyName: vendor.company_name || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      role: vendor.role || 'vendor',
      vendorType: vendor.vendor_type || 'market',
      status: vendor.status || 'approved',
      subscription: vendor.subscription || 'cash',
      newPassword: ''
    });
    setShowEditUserNewPassword(false);
    setShowEditUserModal(true);
  };

  // Save Edit User
  const handleSaveEditUser = async () => {
    setSavingEditUser(true);
    setMessage({ type: '', text: '' });

    try {
      const payload = {
        email: editUserData.email,
        first_name: editUserData.firstName,
        last_name: editUserData.lastName,
        company_name: editUserData.companyName,
        phone: editUserData.phone,
        role: editUserData.role,
        vendor_type: editUserData.vendorType,
        status: editUserData.status,
        subscription: editUserData.subscription,
      };
      // Only send new_password when the admin actually typed a new one.
      // The backend hashes it and updates hashed_password when present.
      if (editUserData.newPassword && editUserData.newPassword.trim().length > 0) {
        if (editUserData.newPassword.trim().length < 6) {
          setMessage({ type: 'error', text: 'New password must be at least 6 characters' });
          setSavingEditUser(false);
          return;
        }
        payload.new_password = editUserData.newPassword.trim();
      }

      const response = await fetch(`${API_URL}/api/admin/users/${editingUser._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const passwordChanged = !!payload.new_password;
        setMessage({
          type: 'success',
          text: passwordChanged
            ? 'User updated and password reset successfully.'
            : 'User updated successfully!'
        });
        setShowEditUserModal(false);
        setEditingUser(null);
        // Refresh users list (suppress error to not overwrite success message)
        fetchVendors(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update user');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to update user' });
    } finally {
      setSavingEditUser(false);
    }
  };

  const fetchMedia = async () => {
    setLoadingMedia(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMediaFiles(data.media || []);
        setOrphanedCount(data.orphaned_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch media:', error);
    } finally {
      setLoadingMedia(false);
    }
  };

  const fetchLegalContent = async () => {
    setLegalLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/legal`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Merge API data with defaults - use API content if it has content, otherwise use default
        setLegalContent({
          terms: (data.terms && data.terms.content) ? data.terms : defaultLegalContent.terms,
          privacy: (data.privacy && data.privacy.content) ? data.privacy : defaultLegalContent.privacy,
          other: (data.other && data.other.content) ? data.other : defaultLegalContent.other
        });
      } else {
        // If API fails, use defaults
        setLegalContent(defaultLegalContent);
      }
    } catch (error) {
      console.error('Failed to fetch legal content:', error);
      // On error, use defaults
      setLegalContent(defaultLegalContent);
    } finally {
      setLegalLoading(false);
    }
  };

  const handleSaveLegalContent = async () => {
    setLegalLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/legal/${selectedLegalType}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(legalContent[selectedLegalType])
      });
      if (response.ok) {
        setMessage({ type: 'success', text: `${legalContent[selectedLegalType].title} saved successfully!` });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save legal content' });
    } finally {
      setLegalLoading(false);
    }
  };

  const handleCleanupOrphaned = async () => {
    if (!window.confirm('This will remove all broken image references from stalls. The stalls will need new images uploaded. Continue?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/media/cleanup-orphaned`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message });
        fetchMedia();
        fetchStalls();
      } else {
        throw new Error('Failed to cleanup');
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to cleanup orphaned media' });
    }
  };

  const handleScanBroken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/media/scan-broken`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.broken_count > 0) {
          setOrphanedCount(data.broken_count);
          setMessage({ 
            type: 'warning', 
            text: `Found ${data.broken_count} broken image references. Click "Clean Up Broken" to remove them.` 
          });
        } else {
          setOrphanedCount(0);
          setMessage({ type: 'success', text: 'No broken image references found!' });
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to scan for broken files' });
    }
  };

  const handleDeleteMedia = async (filename) => {
    if (!window.confirm(`Delete ${filename}? This will also remove it from any stalls using it.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/media/${filename}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setMessage({ type: 'success', text: 'Media file deleted' });
        fetchMedia();
        fetchStalls(); // Refresh stalls to update image references
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete media file' });
    }
  };

  const handleAddUser = async () => {
    // Validate required fields based on role
    if (newUser.role === 'vendor') {
      if (!newUser.name || !newUser.surname || !newUser.companyName || !newUser.email || !newUser.password || !newUser.phone || !newUser.stallNumber) {
        setMessage({ type: 'error', text: 'Please fill all required fields' });
        return;
      }
    } else {
      // Admin
      if (!newUser.name || !newUser.surname || !newUser.email || !newUser.password) {
        setMessage({ type: 'error', text: 'Please fill all required fields' });
        return;
      }
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_URL}/api/admin/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newUser.role === 'vendor' ? newUser.companyName : newUser.name,
          first_name: newUser.name,
          last_name: newUser.surname,
          email: newUser.email,
          phone: newUser.phone,
          password: newUser.password,
          role: newUser.role,
          vendor_type: newUser.vendorType,
          stall_number: newUser.stallNumber,
          subscription: newUser.role === 'vendor' ? newUser.subscription : undefined
        }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `${newUser.role === 'admin' ? 'Admin' : 'Vendor'} created successfully!` });
        setShowAddUserModal(false);
        setNewUser({ name: '', surname: '', companyName: '', email: '', phone: '', password: '', role: 'vendor', vendorType: 'market', stallNumber: '', subscription: 'cash' });
        fetchVendors(false);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create user');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  // New Stall - Market toggle handler
  const handleNewStallMarketToggle = (market) => {
    if (newStallSelectedMarkets.includes(market)) {
      setNewStallSelectedMarkets(newStallSelectedMarkets.filter(m => m !== market));
      const newLocations = { ...newStallMarketLocations };
      delete newLocations[market];
      setNewStallMarketLocations(newLocations);
    } else {
      setNewStallSelectedMarkets([...newStallSelectedMarkets, market]);
      setNewStallMarketLocations({
        ...newStallMarketLocations,
        [market]: { stallNumber: '', lat: '', lng: '' }
      });
    }
  };

  // New Stall - Update market location
  const updateNewStallMarketLocation = (market, field, value) => {
    setNewStallMarketLocations({
      ...newStallMarketLocations,
      [market]: {
        ...newStallMarketLocations[market],
        [field]: value
      }
    });
  };

  // New Stall - Tag toggle handler
  const handleNewStallTagToggle = (tag) => {
    if (newStallSelectedTags.includes(tag)) {
      setNewStallSelectedTags(newStallSelectedTags.filter(t => t !== tag));
    } else if (newStallSelectedTags.length < 10) {
      setNewStallSelectedTags([...newStallSelectedTags, tag]);
    }
  };

  const handleAddStall = async () => {
    // Validate required fields
    if (!newStall.stallName) {
      setMessage({ type: 'error', text: 'Please enter a company name' });
      return;
    }
    if (!newStall.category) {
      setMessage({ type: 'error', text: 'Please select a category' });
      return;
    }
    if (!newStall.slogan) {
      setMessage({ type: 'error', text: 'Please enter a slogan' });
      return;
    }
    if (!newStall.about) {
      setMessage({ type: 'error', text: 'Please enter an about description' });
      return;
    }
    if (!newStall.phone) {
      setMessage({ type: 'error', text: 'Please enter a phone number' });
      return;
    }
    if (!newStall.whatsapp) {
      setMessage({ type: 'error', text: 'Please enter a WhatsApp number' });
      return;
    }
    if (!newStall.email) {
      setMessage({ type: 'error', text: 'Please enter an email address' });
      return;
    }
    if (newStallSelectedMarkets.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one market' });
      return;
    }
    
    // Validate market locations
    for (const market of newStallSelectedMarkets) {
      const loc = newStallMarketLocations[market];
      if (!loc?.stallNumber) {
        setMessage({ type: 'error', text: `Please enter a stall number for ${market}` });
        return;
      }
      if (!loc?.lat || !loc?.lng) {
        setMessage({ type: 'error', text: `Please enter latitude and longitude for ${market}` });
        return;
      }
    }

    // Validate required images
    if (!newStallFiles.logo) {
      setMessage({ type: 'error', text: 'Please upload a logo image' });
      return;
    }
    if (!newStallFiles.banner) {
      setMessage({ type: 'error', text: 'Please upload a banner image' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Use first market's stall number as the main stall number
      const firstMarket = newStallSelectedMarkets[0];
      const stallNumber = newStallMarketLocations[firstMarket]?.stallNumber || '';
      
      // Create stall data
      const stallData = {
        stallName: newStall.stallName,
        stallNumber: stallNumber,
        market: firstMarket,
        category: newStall.category,
        slogan: newStall.slogan,
        about: newStall.about,
        phone: newStall.phone,
        whatsapp: newStall.whatsapp,
        email: newStall.email,
        website: newStall.website,
        socialMedia: newStall.socialMedia,
        tags: newStallSelectedTags,
        location: {
          lat: parseFloat(newStallMarketLocations[firstMarket]?.lat) || null,
          lng: parseFloat(newStallMarketLocations[firstMarket]?.lng) || null
        },
        vendorId: newStall.vendorId || null,
        status: newStall.status || 'draft'
      };

      const response = await fetch(`${API_URL}/api/admin/create-stall`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(stallData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create stall');
      }

      const result = await response.json();
      const stallId = result.stall_id;

      // Upload logo if provided
      if (newStallFiles.logo) {
        const logoFormData = new FormData();
        logoFormData.append('file', newStallFiles.logo);
        await fetch(`${API_URL}/api/admin/stalls/${stallId}/upload-logo`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: logoFormData,
        });
      }

      // Upload banner if provided
      if (newStallFiles.banner) {
        const bannerFormData = new FormData();
        bannerFormData.append('file', newStallFiles.banner);
        await fetch(`${API_URL}/api/admin/stalls/${stallId}/upload-banner`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: bannerFormData,
        });
      }

      // Track stall creation
      trackStallCreated(stallId, newStall.stallName, 'admin');
      trackBackendActivity('stall_created', '/api/admin/create-stall', true, { stall_name: newStall.stallName });
      
      setMessage({ type: 'success', text: 'Stall created successfully!' });
      setShowAddStallModal(false);
      // Reset all form state
      setNewStall({
        stallName: '', stallNumber: '', market: '', category: '', slogan: '', about: '',
        phone: '', whatsapp: '', email: '', website: '',
        socialMedia: { facebook: '', instagram: '', tiktok: '', twitter: '', linkedin: '' },
        tags: [], location: { lat: '', lng: '' }, vendorId: ''
      });
      setNewStallFiles({ logo: null, banner: null });
      setNewStallSelectedMarkets([]);
      setNewStallMarketLocations({});
      setNewStallSelectedTags([]);
      fetchStalls();
      fetchVendors(false);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Initialize edit modal with stall data
  const openEditStallModal = (stall) => {
    setEditingStallData({
      ...stall,
      socialMedia: stall.socialMedia || { facebook: '', instagram: '', tiktok: '', twitter: '', linkedin: '' }
    });
    setEditStallSelectedTags(stall.tags || []);
    setEditStallFiles({ logo: null, banner: null });
    // Set the current vendor ID (could be vendorId or created_by)
    setEditStallVendorId(stall.vendorId || stall.created_by || null);
    
    // Initialize market locations from the stall's locations array
    const marketLocs = {};
    if (stall.locations && stall.locations.length > 0) {
      stall.locations.forEach(loc => {
        if (loc.market) {
          marketLocs[loc.market] = {
            stallNumber: loc.stallNumber || '',
            lat: loc.lat || '',
            lng: loc.lng || ''
          };
        }
      });
    } else if (stall.market) {
      marketLocs[stall.market] = {
        stallNumber: stall.stallNumber || '',
        lat: stall.location?.lat || '',
        lng: stall.location?.lng || ''
      };
    }
    setEditStallMarketLocations(marketLocs);
    setShowEditModal(true);
  };

  // Handle edit stall tag toggle
  const handleEditStallTagToggle = (tag) => {
    if (editStallSelectedTags.includes(tag)) {
      setEditStallSelectedTags(editStallSelectedTags.filter(t => t !== tag));
    } else if (editStallSelectedTags.length < 10) {
      setEditStallSelectedTags([...editStallSelectedTags, tag]);
    }
  };

  // Save edited stall
  const handleSaveEditStall = async () => {
    if (!editingStallData.stallName || !editingStallData.category) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    setSavingEditStall(true);
    setMessage({ type: '', text: '' });

    try {
      // Build locations array from editStallMarketLocations
      const locations = Object.entries(editStallMarketLocations).map(([market, loc]) => ({
        market,
        stallNumber: loc.stallNumber || '',
        lat: loc.lat ? parseFloat(loc.lat) : null,
        lng: loc.lng ? parseFloat(loc.lng) : null
      }));

      // Get primary market and stall number (first selected market)
      const primaryMarket = Object.keys(editStallMarketLocations)[0] || editingStallData.market || '';
      const primaryStallNumber = editStallMarketLocations[primaryMarket]?.stallNumber || editingStallData.stallNumber || '';
      const primaryLocation = {
        lat: editStallMarketLocations[primaryMarket]?.lat ? parseFloat(editStallMarketLocations[primaryMarket].lat) : null,
        lng: editStallMarketLocations[primaryMarket]?.lng ? parseFloat(editStallMarketLocations[primaryMarket].lng) : null
      };

      // Build stall data
      const stallData = {
        stallName: editingStallData.stallName,
        stallNumber: primaryStallNumber,
        market: primaryMarket,
        category: editingStallData.category,
        slogan: editingStallData.slogan,
        about: editingStallData.about,
        phone: editingStallData.phone,
        whatsapp: editingStallData.whatsapp,
        email: editingStallData.email,
        website: editingStallData.website,
        socialMedia: editingStallData.socialMedia,
        tags: editStallSelectedTags,
        location: primaryLocation,
        locations: locations,
        status: editingStallData.status
      };

      const res = await fetch(`${API_URL}/api/admin/stalls/${editingStallData._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(stallData),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to update stall');
      }

      // Handle vendor assignment/unassignment
      // Normalize vendor IDs - treat empty string, undefined, and null all as "no vendor"
      const originalVendorId = editingStallData.vendorId || editingStallData.created_by || null;
      const normalizedOriginal = originalVendorId || null;
      const normalizedNew = editStallVendorId || null;
      
      // Always call assign-vendor endpoint if there's any change (or if we're explicitly unassigning)
      if (normalizedNew !== normalizedOriginal) {
        
        const assignRes = await fetch(`${API_URL}/api/admin/stalls/${editingStallData._id}/assign-vendor`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ vendor_id: normalizedNew }),
        });
        
        if (!assignRes.ok) {
          const error = await assignRes.json();
          console.error('Failed to update vendor assignment:', error);
          throw new Error('Failed to update vendor assignment');
        } else {
          
        }
      }

      // Upload new logo if provided
      if (editStallFiles.logo) {
        const logoFormData = new FormData();
        logoFormData.append('file', editStallFiles.logo);
        const logoRes = await fetch(`${API_URL}/api/admin/stalls/${editingStallData._id}/upload-logo`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: logoFormData,
        });
        if (!logoRes.ok) {
          console.error('Logo upload failed:', await logoRes.text());
        } else {
          const logoData = await logoRes.json();
          
        }
      }

      // Upload new banner if provided
      if (editStallFiles.banner) {
        const bannerFormData = new FormData();
        bannerFormData.append('file', editStallFiles.banner);
        const bannerRes = await fetch(`${API_URL}/api/admin/stalls/${editingStallData._id}/upload-banner`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: bannerFormData,
        });
        if (!bannerRes.ok) {
          console.error('Banner upload failed:', await bannerRes.text());
        } else {
          const bannerData = await bannerRes.json();
          
        }
      }

      setMessage({ type: 'success', text: 'Stall updated successfully!' });
      setShowEditModal(false);
      setEditingStallData(null);
      setEditStallFiles({ logo: null, banner: null });
      setEditStallSelectedTags([]);
      setEditStallMarketLocations({});
      setEditStallVendorId(null);
      fetchStalls();
      fetchVendors(false); // Refresh vendors to update their stall_id assignments
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSavingEditStall(false);
    }
  };

  // ─────────── Shared list-manager handlers (sort mode + rename) ───────────
  const refreshList = async (kind) => {
    const url = kind === 'categories'
      ? `${API_URL}/api/categories/`
      : `${API_URL}/api/categories/${kind}`;
    try {
      const res = await fetchWithRetry(url);
      if (!res.ok) return;
      const data = await res.json();
      if (kind === 'categories') setCategories(data);
      else if (kind === 'tags') setTags(data);
      else if (kind === 'markets') setMarkets(data);
      else if (kind === 'expos') setExpos(data);
      else if (kind === 'festivals') setFestivals(data);
    } catch (_) { /* ignore transient errors */ }
  };

  const loadSortModes = async () => {
    try {
      const res = await fetchWithRetry(`${API_URL}/api/categories/sort-modes`);
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.modes) setSortModes({ ...sortModes, ...data.modes });
    } catch (_) { /* ignore */ }
  };

  const handleSetSortMode = async (kind, mode) => {
    setSortModes((prev) => ({ ...prev, [kind]: mode }));
    try {
      const res = await fetchWithRetry(`${API_URL}/api/categories/sort-modes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        body: JSON.stringify({ kind, mode }),
      });
      if (!res.ok) throw new Error('Failed to update sort mode');
      await refreshList(kind);
    } catch (e) {
      setMessage({ type: 'error', text: `Sort mode update failed: ${e.message}` });
    }
  };

  const handleShuffleKind = async (kind) => {
    try {
      const res = await fetchWithRetry(`${API_URL}/api/categories/shuffle/${kind}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      if (!res.ok) throw new Error('Failed to shuffle');
      await refreshList(kind);
      setMessage({ type: 'success', text: `Shuffled ${kind}` });
    } catch (e) {
      setMessage({ type: 'error', text: `Shuffle failed: ${e.message}` });
    }
  };

  const handleRenameItem = async (kind, oldName, newName) => {
    const trimmed = (newName || '').trim();
    if (!trimmed || trimmed === oldName) return;
    const url = kind === 'categories'
      ? `${API_URL}/api/categories/rename`
      : `${API_URL}/api/categories/${kind}/rename`;
    const res = await fetchWithRetry(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      body: JSON.stringify({ old_name: oldName, new_name: trimmed }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = err.detail || `Rename failed (${res.status})`;
      setMessage({ type: 'error', text: msg });
      throw new Error(msg);
    }
    await refreshList(kind);
    setMessage({ type: 'success', text: `Renamed to "${trimmed}"` });
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    
    // Clear previous message
    setCategoryModalMessage(null);
    
    // Split by comma and clean up each category
    const newCategories = newCategory.split(',')
      .map(c => c.trim())
      .filter(c => c.length > 0);
    
    if (newCategories.length === 0) return;
    
    // Check for duplicates (case-insensitive)
    const existingCategories = categories.map(c => c.toLowerCase());
    const duplicates = newCategories.filter(c => existingCategories.includes(c.toLowerCase()));
    const uniqueNewCategories = newCategories.filter(c => !existingCategories.includes(c.toLowerCase()));
    
    if (duplicates.length > 0 && uniqueNewCategories.length === 0) {
      setCategoryModalMessage({ type: 'error', text: `Category(s) already exist: ${duplicates.join(', ')}` });
      return;
    }
    
    if (uniqueNewCategories.length === 0) {
      setCategoryModalMessage({ type: 'error', text: 'No new categories to add' });
      return;
    }
    
    let addedCount = 0;
    let failedCategories = [];
    
    // Add each unique category
    for (const categoryName of uniqueNewCategories) {
      try {
        const res = await fetch(`${API_URL}/api/categories/`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ name: categoryName }),
        });

        if (res.ok) {
          addedCount++;
        } else {
          const error = await res.json();
          if (error.detail?.includes('exists')) {
            failedCategories.push(`${categoryName} (exists)`);
          } else {
            failedCategories.push(categoryName);
          }
        }
      } catch (error) {
        // On CORS/network error, count as added locally
        addedCount++;
      }
    }
    
    // Update local state
    setNewCategory('');
    fetchCategoriesAndTags();
    window.dispatchEvent(new CustomEvent('wayo-categories-updated'));
    
    // Show result message in modal
    let messageText = '';
    if (addedCount > 0) {
      messageText = `Added ${addedCount} category(s) successfully!`;
    }
    if (duplicates.length > 0) {
      messageText += ` Skipped duplicates: ${duplicates.join(', ')}`;
    }
    if (failedCategories.length > 0) {
      messageText += ` Failed: ${failedCategories.join(', ')}`;
    }
    
    setCategoryModalMessage({ 
      type: addedCount > 0 ? 'success' : 'error', 
      text: messageText.trim() 
    });
    
    // Auto-clear success message after 3 seconds
    if (addedCount > 0) {
      setTimeout(() => setCategoryModalMessage(null), 3000);
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    
    if (!window.confirm(`Delete category "${categoryName}"? This will fail if any stalls use it.`)) return;

    try {
      const res = await fetch(`${API_URL}/api/categories/${encodeURIComponent(categoryName)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok || res.status === 204) {
        const updated = categories.filter(c => c !== categoryName);
        setMessage({ type: 'success', text: 'Category deleted!' });
        setCategories(updated);
        const newUsage = { ...categoryUsage };
        delete newUsage[categoryName];
        setCategoryUsage(newUsage);
        localStorage.setItem('wayo_categories', JSON.stringify(updated));
        fetchCategoriesAndTags();
        // Trigger global refresh
        window.dispatchEvent(new CustomEvent('wayo-categories-updated'));
      } else {
        const error = await res.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to delete category' });
      }
    } catch (error) {
      // On CORS/network error in dev, still update local state
      const updated = categories.filter(c => c !== categoryName);
      setMessage({ type: 'success', text: 'Category deleted (local)!' });
      setCategories(updated);
      const newUsage = { ...categoryUsage };
      delete newUsage[categoryName];
      setCategoryUsage(newUsage);
      localStorage.setItem('wayo_categories', JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('categoriesUpdated'));
    }
  };

  const handleAddTag = async () => {
    if (!newTag.trim()) return;
    
    // Clear previous message
    setTagModalMessage(null);
    
    // Split by comma and clean up each tag
    const newTags = newTag.split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);
    
    if (newTags.length === 0) return;
    
    // Check for duplicates
    const existingTags = tags.map(t => t.toLowerCase());
    const duplicates = newTags.filter(t => existingTags.includes(t.toLowerCase()));
    const uniqueNewTags = newTags.filter(t => !existingTags.includes(t.toLowerCase()));
    
    if (duplicates.length > 0 && uniqueNewTags.length === 0) {
      setTagModalMessage({ type: 'error', text: `Tag(s) already exist: ${duplicates.join(', ')}` });
      return;
    }
    
    if (uniqueNewTags.length === 0) {
      setTagModalMessage({ type: 'error', text: 'No new tags to add' });
      return;
    }
    
    let addedCount = 0;
    let failedTags = [];
    
    // Add each unique tag
    for (const tagName of uniqueNewTags) {
      try {
        const res = await fetch(`${API_URL}/api/categories/tags`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ name: tagName }),
        });

        if (res.ok) {
          addedCount++;
        } else {
          const error = await res.json();
          if (error.detail?.includes('exists')) {
            // Tag already exists on server
            failedTags.push(`${tagName} (exists)`);
          } else {
            failedTags.push(tagName);
          }
        }
      } catch (error) {
        // On CORS/network error, count as added locally
        addedCount++;
      }
    }
    
    // Update local state
    setNewTag('');
    fetchCategoriesAndTags();
    window.dispatchEvent(new CustomEvent('tagsUpdated'));
    
    // Show result message in modal
    let messageText = '';
    if (addedCount > 0) {
      messageText = `Added ${addedCount} tag(s) successfully!`;
    }
    if (duplicates.length > 0) {
      messageText += ` Skipped duplicates: ${duplicates.join(', ')}`;
    }
    if (failedTags.length > 0) {
      messageText += ` Failed: ${failedTags.join(', ')}`;
    }
    
    setTagModalMessage({ 
      type: addedCount > 0 ? 'success' : 'error', 
      text: messageText.trim() 
    });
    
    // Auto-clear success message after 3 seconds
    if (addedCount > 0) {
      setTimeout(() => setTagModalMessage(null), 3000);
    }
  };

  const handleDeleteTag = async (tagName) => {
    
    if (!window.confirm(`Delete tag "${tagName}"? It will be removed from all stalls.`)) return;

    try {
      const res = await fetch(`${API_URL}/api/categories/tags/${encodeURIComponent(tagName)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok || res.status === 204) {
        setMessage({ type: 'success', text: 'Tag deleted!' });
        // Update local state
        setTags(tags.filter(t => t !== tagName));
        const newUsage = { ...tagUsage };
        delete newUsage[tagName];
        setTagUsage(newUsage);
        fetchCategoriesAndTags();
        // Trigger global refresh
        window.dispatchEvent(new CustomEvent('tagsUpdated'));
      } else {
        const error = await res.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to delete tag' });
      }
    } catch (error) {
      // On CORS/network error in dev, still update local state
      setMessage({ type: 'success', text: 'Tag deleted (local)!' });
      setTags(tags.filter(t => t !== tagName));
      const newUsage = { ...tagUsage };
      delete newUsage[tagName];
      setTagUsage(newUsage);
      window.dispatchEvent(new CustomEvent('tagsUpdated'));
    }
  };


  // Market Management
  const handleAddMarket = async () => {
    if (!newMarket.trim()) return;
    
    // Clear previous message
    setMarketModalMessage(null);
    
    // Split by comma and clean up each market
    const newMarkets = newMarket.split(',')
      .map(m => m.trim())
      .filter(m => m.length > 0);
    
    if (newMarkets.length === 0) return;
    
    // Check for duplicates (case-insensitive)
    const existingMarkets = markets.map(m => m.toLowerCase());
    const duplicates = newMarkets.filter(m => existingMarkets.includes(m.toLowerCase()));
    const uniqueNewMarkets = newMarkets.filter(m => !existingMarkets.includes(m.toLowerCase()));
    
    if (duplicates.length > 0 && uniqueNewMarkets.length === 0) {
      setMarketModalMessage({ type: 'error', text: `Market(s) already exist: ${duplicates.join(', ')}` });
      return;
    }
    
    if (uniqueNewMarkets.length === 0) {
      setMarketModalMessage({ type: 'error', text: 'No new markets to add' });
      return;
    }
    
    let addedCount = 0;
    let failedMarkets = [];
    
    // Add each unique market
    for (const marketName of uniqueNewMarkets) {
      try {
        const res = await fetch(`${API_URL}/api/categories/markets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ name: marketName }),
        });

        if (res.ok) {
          addedCount++;
        } else {
          const error = await res.json();
          if (error.detail?.includes('exists')) {
            failedMarkets.push(`${marketName} (exists)`);
          } else {
            failedMarkets.push(marketName);
          }
        }
      } catch (error) {
        // On CORS/network error, count as added locally
        addedCount++;
      }
    }
    
    // Update local state
    setNewMarket('');
    fetchCategoriesAndTags();
    window.dispatchEvent(new CustomEvent('wayo-categories-updated'));
    
    // Show result message in modal
    let messageText = '';
    if (addedCount > 0) {
      messageText = `Added ${addedCount} market(s) successfully!`;
    }
    if (duplicates.length > 0) {
      messageText += ` Skipped duplicates: ${duplicates.join(', ')}`;
    }
    if (failedMarkets.length > 0) {
      messageText += ` Failed: ${failedMarkets.join(', ')}`;
    }
    
    setMarketModalMessage({ 
      type: addedCount > 0 ? 'success' : 'error', 
      text: messageText.trim() 
    });
    
    // Auto-clear success message after 3 seconds
    if (addedCount > 0) {
      setTimeout(() => setMarketModalMessage(null), 3000);
    }
  };

  const handleDeleteMarket = async (marketName) => {
    if (!window.confirm(`Delete market "${marketName}"?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/categories/markets/${encodeURIComponent(marketName)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok || res.status === 204) {
        setMessage({ type: 'success', text: 'Market deleted!' });
        setMarkets(markets.filter(m => m !== marketName));
        fetchCategoriesAndTags();
        window.dispatchEvent(new CustomEvent('wayo-categories-updated'));
      } else {
        const error = await res.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to delete market' });
      }
    } catch (error) {
      setMessage({ type: 'success', text: 'Market deleted (local)!' });
      setMarkets(markets.filter(m => m !== marketName));
      window.dispatchEvent(new CustomEvent('wayo-categories-updated'));
    }
  };

  // Handle market reordering via drag and drop
  const handleMarketDragStart = (e, market) => {
    setDraggedMarket(market);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleMarketDragOver = (e, market) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleMarketDrop = async (e, targetMarket) => {
    e.preventDefault();
    if (!draggedMarket || draggedMarket === targetMarket) {
      setDraggedMarket(null);
      return;
    }

    const newMarkets = [...markets];
    const draggedIndex = newMarkets.indexOf(draggedMarket);
    const targetIndex = newMarkets.indexOf(targetMarket);

    // Remove from old position and insert at new position
    newMarkets.splice(draggedIndex, 1);
    newMarkets.splice(targetIndex, 0, draggedMarket);

    setMarkets(newMarkets);
    setDraggedMarket(null);

    // Save the new order to the backend
    try {
      const res = await fetch(`${API_URL}/api/categories/markets/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ markets: newMarkets }),
      });

      if (res.ok) {
        setMarketModalMessage({ type: 'success', text: 'Market order saved!' });
        window.dispatchEvent(new CustomEvent('wayo-categories-updated'));
      } else {
        // Revert on error
        fetchCategoriesAndTags();
        setMarketModalMessage({ type: 'error', text: 'Failed to save order' });
      }
    } catch (error) {
      console.error('Failed to save market order:', error);
      setMarketModalMessage({ type: 'success', text: 'Order updated locally' });
      window.dispatchEvent(new CustomEvent('wayo-categories-updated'));
    }

    // Clear message after 2 seconds
    setTimeout(() => setMarketModalMessage(null), 2000);
  };

  const handleMarketDragEnd = () => {
    setDraggedMarket(null);
  };


  // ============ EXPOS HANDLERS ============
  const handleAddExpo = async () => {
    const newExpos = newExpo.split(',').map(e => e.trim()).filter(e => e.length > 0);
    if (newExpos.length === 0) return;
    
    const existing = expos.map(e => e.toLowerCase());
    const unique = newExpos.filter(e => !existing.includes(e.toLowerCase()));
    
    if (unique.length === 0) {
      setExpoModalMessage({ type: 'error', text: `Expo(s) already exist: ${newExpos.join(', ')}` });
      setTimeout(() => setExpoModalMessage(null), 2000);
      return;
    }
    
    let added = 0;
    for (const name of unique) {
      try {
        const res = await fetch(`${API_URL}/api/categories/expos`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name }),
        });
        if (res.ok) added++;
      } catch { added++; }
    }
    
    setNewExpo('');
    setExpos([...expos, ...unique]);
    setExpoModalMessage({ type: 'success', text: `${added} expo(s) added!` });
    setTimeout(() => setExpoModalMessage(null), 2000);
  };

  const handleDeleteExpo = async (name) => {
    if (!window.confirm(`Delete expo "${name}"?`)) return;
    try {
      await fetch(`${API_URL}/api/categories/expos/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setExpos(expos.filter(e => e !== name));
      setExpoModalMessage({ type: 'success', text: 'Expo deleted!' });
    } catch { setExpoModalMessage({ type: 'error', text: 'Failed to delete expo' }); }
    setTimeout(() => setExpoModalMessage(null), 2000);
  };

  const handleExpoDragStart = (e, expo) => { setDraggedExpo(expo); e.dataTransfer.effectAllowed = 'move'; };
  const handleExpoDragOver = (e, expo) => {
    e.preventDefault();
    if (!draggedExpo || draggedExpo === expo) return;
    const newExpos = [...expos];
    const fromIdx = newExpos.indexOf(draggedExpo);
    const toIdx = newExpos.indexOf(expo);
    newExpos.splice(fromIdx, 1);
    newExpos.splice(toIdx, 0, draggedExpo);
    setExpos(newExpos);
  };
  const handleExpoDragEnd = async () => {
    setDraggedExpo(null);
    try {
      const res = await fetch(`${API_URL}/api/categories/expos/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ expos }),
      });
      if (res.ok) setExpoModalMessage({ type: 'success', text: 'Order saved!' });
    } catch { /* silent */ }
    setTimeout(() => setExpoModalMessage(null), 2000);
  };

  // ============ FESTIVALS HANDLERS ============
  const handleAddFestival = async () => {
    const newFestivals = newFestival.split(',').map(f => f.trim()).filter(f => f.length > 0);
    if (newFestivals.length === 0) return;
    
    const existing = festivals.map(f => f.toLowerCase());
    const unique = newFestivals.filter(f => !existing.includes(f.toLowerCase()));
    
    if (unique.length === 0) {
      setFestivalModalMessage({ type: 'error', text: `Festival(s) already exist: ${newFestivals.join(', ')}` });
      setTimeout(() => setFestivalModalMessage(null), 2000);
      return;
    }
    
    let added = 0;
    for (const name of unique) {
      try {
        const res = await fetch(`${API_URL}/api/categories/festivals`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name }),
        });
        if (res.ok) added++;
      } catch { added++; }
    }
    
    setNewFestival('');
    setFestivals([...festivals, ...unique]);
    setFestivalModalMessage({ type: 'success', text: `${added} festival(s) added!` });
    setTimeout(() => setFestivalModalMessage(null), 2000);
  };

  const handleDeleteFestival = async (name) => {
    if (!window.confirm(`Delete festival "${name}"?`)) return;
    try {
      await fetch(`${API_URL}/api/categories/festivals/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setFestivals(festivals.filter(f => f !== name));
      setFestivalModalMessage({ type: 'success', text: 'Festival deleted!' });
    } catch { setFestivalModalMessage({ type: 'error', text: 'Failed to delete festival' }); }
    setTimeout(() => setFestivalModalMessage(null), 2000);
  };

  const handleFestivalDragStart = (e, festival) => { setDraggedFestival(festival); e.dataTransfer.effectAllowed = 'move'; };
  const handleFestivalDragOver = (e, festival) => {
    e.preventDefault();
    if (!draggedFestival || draggedFestival === festival) return;
    const newFestivals = [...festivals];
    const fromIdx = newFestivals.indexOf(draggedFestival);
    const toIdx = newFestivals.indexOf(festival);
    newFestivals.splice(fromIdx, 1);
    newFestivals.splice(toIdx, 0, draggedFestival);
    setFestivals(newFestivals);
  };
  const handleFestivalDragEnd = async () => {
    setDraggedFestival(null);
    try {
      const res = await fetch(`${API_URL}/api/categories/festivals/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ festivals }),
      });
      if (res.ok) setFestivalModalMessage({ type: 'success', text: 'Order saved!' });
    } catch { /* silent */ }
    setTimeout(() => setFestivalModalMessage(null), 2000);
  };


  const handleUpdateLocation = async (stallId) => {
    const { lat, lng } = locationEdit;
    
    if (!lat || !lng) {
      setMessage({ type: 'error', text: 'Please enter both latitude and longitude' });
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/admin/stalls/${stallId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          location: {
            lat: parseFloat(lat),
            lng: parseFloat(lng),
          },
        }),
      });

      if (res.ok) {
        setMessage({ type: 'success', text: 'Location updated successfully!' });
        setEditingStall(null);
        setLocationEdit({ lat: '', lng: '' });
        fetchStalls();
      } else {
        const error = await res.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to update location' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update location' });
    }
  };

  // Export vendors functionality
  const handleExport = () => {
    const vendorsToExport = selectedVendors.length > 0 
      ? exportFilteredVendors.filter(v => selectedVendors.includes(v._id))
      : exportFilteredVendors;

    if (vendorsToExport.length === 0) {
      setMessage({ type: 'error', text: 'No users to export with current filters' });
      return;
    }

    // Convert to CSV with company name and vendor type
    const headers = ['Company Name', 'First Name', 'Surname', 'Email', 'Phone', 'Stall Number', 'Vendor Type', 'Role', 'Subscription', 'Status'];
    const csvContent = [
      headers.join(','),
      ...vendorsToExport.map(v => {
        const stall = v.stall_id ? allStalls.find(s => s._id === v.stall_id) : null;
        const companyName = stall?.stallName || v.company_name || '';
        return [
          `"${companyName}"`,
          `"${v.first_name || v.name?.split(' ')[0] || ''}"`,
          `"${v.last_name || v.name?.split(' ').slice(1).join(' ') || ''}"`,
          v.email || '',
          v.phone || '',
          v.stall_number || '',
          v.vendor_type || 'market',
          v.role || '',
          v.subscription || '',
          v.status || ''
        ].join(',');
      })
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    setShowExportModal(false);
    setSelectedVendors([]);
    setExportFilter('all');
    setExportSearch('');
    setMessage({ type: 'success', text: `Exported ${vendorsToExport.length} users successfully` });
  };

  // Bulk delete functionality
  const handleBulkDelete = async () => {
    if (selectedVendors.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one vendor to delete' });
      return;
    }

    setProcessing('bulk-delete');
    try {
      // Delete each selected vendor
      const deletePromises = selectedVendors.map(vendorId =>
        fetch(`${API_URL}/api/admin/vendors/${vendorId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })
      );

      await Promise.all(deletePromises);

      setMessage({ type: 'success', text: `Deleted ${selectedVendors.length} vendors successfully` });
      setShowBulkDeleteModal(false);
      setShowDeleteConfirm(false);
      setSelectedVendors([]);
      setExportFilter('all');
      setExportSearch('');
      fetchVendors(false);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete vendors' });
    } finally {
      setProcessing(null);
    }
  };

  const toggleVendorSelection = (vendorId) => {
    setSelectedVendors(prev =>
      prev.includes(vendorId)
        ? prev.filter(id => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  // Export stalls functionality
  const handleExportStalls = () => {
    const stallsToExport = selectedStallsForExport.length > 0 
      ? exportFilteredStalls.filter(s => selectedStallsForExport.includes(s._id))
      : exportFilteredStalls;

    if (stallsToExport.length === 0) {
      setMessage({ type: 'error', text: 'No stalls to export with current filters' });
      return;
    }

    // Convert to CSV with all fields - ORDER MATCHES "Add New Stall" modal inputs
    const headers = [
      'Company Name', 'Category', 'Tags', 'Slogan', 'About',
      'Phone', 'WhatsApp', 'Email', 'Website',
      'Facebook', 'Instagram', 'TikTok', 'Twitter', 'LinkedIn',
      'Market', 'Stall Number', 'Latitude', 'Longitude',
      'Vendor ID', 'Status'
    ];
    const csvContent = [
      headers.join(','),
      ...stallsToExport.map(s => [
        `"${(s.stallName || '').replace(/"/g, '""')}"`,
        s.category || '',
        `"${(s.tags || []).join('; ')}"`,
        `"${(s.slogan || '').replace(/"/g, '""')}"`,
        `"${(s.about || '').replace(/"/g, '""')}"`,
        s.phone || '',
        s.whatsapp || '',
        s.email || '',
        s.website || '',
        s.socialMedia?.facebook || '',
        s.socialMedia?.instagram || '',
        s.socialMedia?.tiktok || '',
        s.socialMedia?.twitter || '',
        s.socialMedia?.linkedin || '',
        s.market || '',
        s.stallNumber || '',
        s.location?.lat || '',
        s.location?.lng || '',
        s.vendorId || s.created_by || '',
        s.status || 'draft'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stalls_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    setShowExportStallsModal(false);
    setSelectedStallsForExport([]);
    setStallExportFilter('all');
    setStallExportSearch('');
    setMessage({ type: 'success', text: `Exported ${stallsToExport.length} stalls successfully` });
  };

  // Bulk delete stalls functionality
  const handleBulkDeleteStalls = async () => {
    if (selectedStallsForDelete.length === 0) {
      setMessage({ type: 'error', text: 'Please select stalls to delete' });
      return;
    }

    setProcessing('bulk-delete-stalls');
    try {
      let deletedCount = 0;
      for (const stallId of selectedStallsForDelete) {
        const response = await fetch(`${API_URL}/api/admin/stalls/${stallId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (response.ok) deletedCount++;
      }

      setMessage({ type: 'success', text: `Deleted ${deletedCount} stalls successfully` });
      setShowBulkDeleteStallsModal(false);
      setSelectedStallsForDelete([]);
      setStallExportFilter('all');
      setStallExportSearch('');
      fetchStalls();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete stalls' });
    } finally {
      setProcessing(null);
    }
  };

  // Single stall delete functionality
  const handleDeleteSingleStall = async (stallId, stallName) => {
    if (!window.confirm(`Are you sure you want to delete "${stallName}"? This action cannot be undone.`)) {
      return;
    }

    setProcessing(`delete-stall-${stallId}`);
    try {
      const response = await fetch(`${API_URL}/api/admin/stalls/${stallId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `Stall "${stallName}" deleted successfully` });
        fetchStalls();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to delete stall' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete stall' });
    } finally {
      setProcessing(null);
    }
  };

  // CSV parsing helper to handle quoted fields with commas
  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim()); // Don't forget the last value
    return values;
  };

  // CSV Upload and Parsing
  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFile(file);
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        setMessage({ type: 'error', text: 'CSV file must have at least a header row and one data row' });
        return;
      }

      const headers = parseCSVLine(lines[0]);
      const data = lines.slice(1).map(line => {
        const values = parseCSVLine(line);
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      });

      setCsvHeaders(headers);
      setCsvData(data);
      setUploadStep(2);
    };

    reader.readAsText(file);
  };

  const resetBulkUpload = () => {
    setCsvFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setFieldMapping({});
    setUploadStep(1);
    setUploadProgress({ current: 0, total: 0, status: '' });
    setUploadErrors([]);
  };

  // Bulk Upload Vendors
  const handleBulkUploadVendors = async () => {
    setProcessing('bulk-upload');
    setUploadErrors([]); // reset from any previous run
    let successCount = 0;
    let unlockedCount = 0;   // existing users who had a new type unlocked
    let skippedCount = 0;    // existing users who already had that type
    let errorCount = 0;
    const errors = []; // { row, email, reason }
    const total = csvData.length;

    // Helper — record a failed row
    const recordError = (rowIdx, email, reason) => {
      errorCount++;
      errors.push({ row: rowIdx + 1, email: email || '(no email)', reason });
    };

    // Initialize progress
    setUploadProgress({ current: 0, total, status: 'Starting upload...' });

    try {
      for (let i = 0; i < csvData.length; i++) {
        const row = csvData[i];
        
        // Update progress
        setUploadProgress({ 
          current: i + 1, 
          total, 
          status: `Processing user ${i + 1} of ${total}...` 
        });
        
        // Combine firstName and lastName into personal name
        const firstName = row[fieldMapping.firstName]?.trim() || '';
        const lastName = row[fieldMapping.lastName]?.trim() || '';
        const personalName = [firstName, lastName].filter(Boolean).join(' ').trim();
        
        // Company name is separate - use it as the main 'name' field for the stall
        const companyName = row[fieldMapping.companyName]?.trim() || '';
        
        // Use company name if provided, otherwise fall back to personal name
        const displayName = companyName || personalName;
        
        // Parse status from CSV or default to approved
        const csvStatus = row[fieldMapping.status]?.toLowerCase() || '';
        const status = csvStatus === 'pending' ? 'pending' : 
                       csvStatus === 'declined' ? 'declined' : 'approved';
        
        // Parse vendor_type from CSV or mapping
        let vendorType = 'market';
        if (fieldMapping.vendorType?.startsWith('__')) {
          vendorType = fieldMapping.vendorType.replace('__', '');
        } else if (fieldMapping.vendorType && row[fieldMapping.vendorType]) {
          const csvType = row[fieldMapping.vendorType].toLowerCase().trim();
          if (['market', 'expo', 'festival'].includes(csvType)) vendorType = csvType;
        }
        
        // Parse subscription from mapping (set-all __xxx or CSV column) — default to 'cash'
        const VALID_SUBS = ['yearly', 'monthly', 'cash', 'manual', 'trial'];
        let subscription = 'cash';
        if (fieldMapping.subscription?.startsWith('__')) {
          subscription = fieldMapping.subscription.replace('__', '');
        } else if (fieldMapping.subscription && row[fieldMapping.subscription]) {
          const csvSub = row[fieldMapping.subscription].toLowerCase().trim();
          if (VALID_SUBS.includes(csvSub)) subscription = csvSub;
        }

        const vendorData = {
          name: displayName,
          first_name: firstName,
          last_name: lastName,
          company_name: companyName,
          email: row[fieldMapping.email]?.trim() || '',
          phone: row[fieldMapping.phone]?.trim() || '',
          password: row[fieldMapping.password] || 'DefaultPassword123',
          role: row[fieldMapping.role]?.toLowerCase() || 'vendor',
          vendor_type: vendorType,
          subscription: subscription,
          stall_number: row[fieldMapping.stall_number] || null,
          status: status
        };

        if (!vendorData.email) {
          recordError(i, '', 'Missing email');
          continue;
        }

        if (!displayName) {
          recordError(i, vendorData.email, 'Missing name or company name');
          continue;
        }

        try {
          // If a user with this email already exists, DON'T create a duplicate.
          // Instead, if the row's vendor_type is not yet in their vendor_types,
          // grant it to unlock that type for them.
          const existing = allVendors.find(
            v => v.email && vendorData.email && v.email.toLowerCase() === vendorData.email.toLowerCase()
          );

          if (existing) {
            const existingTypes = (existing.vendor_types && existing.vendor_types.length > 0)
              ? existing.vendor_types
              : [existing.vendor_type || 'market'];

            if (existingTypes.includes(vendorType)) {
              // Already has this type — nothing to do
              skippedCount++;
              continue;
            }

            const grantResp = await fetch(`${API_URL}/api/admin/vendors/${existing._id}/grant-type`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ type: vendorType }),
            });
            if (grantResp.ok) {
              unlockedCount++;
            } else {
              const err = await grantResp.json().catch(() => ({}));
              recordError(i, vendorData.email, `Failed to unlock ${vendorType}: ${err.detail || 'unknown error'}`);
            }
            continue;
          }

          const response = await fetch(`${API_URL}/api/admin/create-user`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(vendorData),
          });

          if (response.ok) {
            successCount++;
          } else {
            const errorData = await response.json().catch(() => ({}));
            const reason = typeof errorData.detail === 'string'
              ? errorData.detail
              : (Array.isArray(errorData.detail) ? errorData.detail.map(d => d.msg || JSON.stringify(d)).join('; ') : `HTTP ${response.status}`);
            recordError(i, vendorData.email, reason);
          }
        } catch (error) {
          recordError(i, vendorData.email, `Network error: ${error.message || 'unknown'}`);
        }
      }

      setUploadProgress({ current: total, total, status: 'Upload complete!' });

      // Store errors so the inline panel can render them
      setUploadErrors(errors);

      const summaryParts = [`${successCount} added`];
      if (unlockedCount > 0) summaryParts.push(`${unlockedCount} unlocked (existing users)`);
      if (skippedCount > 0) summaryParts.push(`${skippedCount} skipped (already had type)`);
      if (errorCount > 0) summaryParts.push(`${errorCount} failed`);

      setMessage({
        type: (successCount + unlockedCount) > 0 ? 'success' : 'error',
        text: `Bulk upload complete! ${summaryParts.join(', ')}`
      });
      
      // Small delay to show completion
      await new Promise(resolve => setTimeout(resolve, 500));

      // If there are errors, keep the modal open so the user can review them.
      // Otherwise, auto-close and refresh.
      if (errorCount === 0) {
        setShowBulkUploadVendors(false);
        resetBulkUpload();
      }
      fetchVendors(false);
    } catch (error) {
      setMessage({ type: 'error', text: 'Bulk upload failed: ' + error.message });
    } finally {
      setProcessing(null);
      setUploadProgress({ current: 0, total: 0, status: '' });
    }
  };

  // Bulk Upload Stalls
  const handleBulkUploadStalls = async () => {
    setProcessing('bulk-upload');
    setUploadErrors([]);
    let successCount = 0;
    let errorCount = 0;
    let existingLinked = 0;  // existing stall found → linked to a user
    let skippedCount = 0;    // already linked / already exists, no change needed
    let newlyCreated = 0;    // brand new stall created (with or without vendor link)
    const errors = []; // { row, email, reason }
    const total = csvData.length;

    const recordError = (rowIdx, label, reason) => {
      errorCount++;
      errors.push({ row: rowIdx + 1, email: label || '(no name)', reason });
    };

    // Initialize progress
    setUploadProgress({ current: 0, total, status: 'Starting upload...' });

    try {
      for (let i = 0; i < csvData.length; i++) {
        const row = csvData[i];

        // Update progress
        setUploadProgress({
          current: i + 1,
          total,
          status: `Processing stall ${i + 1} of ${total}...`
        });

        // Look up vendor by email from allVendors.
        // Only link the newly-created stall to the user if the user has NO existing stall.
        // We must never unassign a user from their previous stall during bulk import.
        let resolvedVendorId = null;
        let matchedVendor = null;
        let matchedVendorHasStall = false;
        const vendorEmailRaw = row[fieldMapping.vendorId]?.trim();
        if (vendorEmailRaw) {
          matchedVendor = allVendors.find(
            v => v.email?.toLowerCase() === vendorEmailRaw.toLowerCase()
          );
          if (matchedVendor) {
            matchedVendorHasStall = !!matchedVendor.stall_id;
            // Only link if the vendor doesn't already have a stall assigned
            if (!matchedVendorHasStall) {
              resolvedVendorId = matchedVendor._id;
            }
          }
        }

        // Trim strings and coerce empty → undefined so Pydantic Optional validators pass
        const clean = (v) => {
          const s = (v || '').toString().trim();
          return s === '' ? undefined : s;
        };
        const emailVal = clean(row[fieldMapping.email]);

        const stallData = {
          stallName: clean(row[fieldMapping.stallName]),
          stallNumber: clean(row[fieldMapping.stallNumber]),
          market: clean(row[fieldMapping.market]),
          category: clean(row[fieldMapping.category]) || 'General',
          slogan: clean(row[fieldMapping.slogan]),
          about: clean(row[fieldMapping.about]),
          phone: clean(row[fieldMapping.phone]),
          whatsapp: clean(row[fieldMapping.whatsapp]),
          // omit email if empty — Pydantic EmailStr rejects '' silently
          ...(emailVal ? { email: emailVal } : {}),
          website: clean(row[fieldMapping.website]),
          socialMedia: {
            facebook: clean(row[fieldMapping.facebook]),
            instagram: clean(row[fieldMapping.instagram]),
            tiktok: clean(row[fieldMapping.tiktok]),
            twitter: clean(row[fieldMapping.twitter]),
            linkedin: clean(row[fieldMapping.linkedin])
          },
          tags: row[fieldMapping.tags] ? row[fieldMapping.tags].split(';').map(t => t.trim()).filter(Boolean) : [],
          location: {
            lat: parseFloat(row[fieldMapping.latitude]) || null,
            lng: parseFloat(row[fieldMapping.longitude]) || null,
            address: clean(row[fieldMapping.address])
          },
          vendorId: resolvedVendorId,
          status: row[fieldMapping.status]?.toLowerCase() || 'draft'
        };

        if (!stallData.stallName || !stallData.stallNumber) {
          recordError(i, stallData.stallName || '', 'Missing stall name or stall number');
          continue;
        }

        // SMART UPLOAD: only create a new stall if this email isn't already tied to one.
        // Look up an existing stall via the vendor email (either the stall's own email field
        // or the stall assigned to the matched user).
        const emailLower = (emailVal || vendorEmailRaw || '').toLowerCase();
        let existingStall = null;
        if (emailLower) {
          existingStall = allStalls.find(s => {
            const stallEmail = (s.email || '').toLowerCase();
            const stallVendor = s.vendorId || s.vendor_id || s.created_by;
            return (stallEmail && stallEmail === emailLower)
                || (matchedVendor && stallVendor === matchedVendor._id);
          });
        }

        if (existingStall) {
          const currentVendor = existingStall.vendorId || existingStall.vendor_id || existingStall.created_by;
          // Already linked to the matched user → nothing to do
          if (matchedVendor && currentVendor === matchedVendor._id) {
            skippedCount++;
            successCount++;
            continue;
          }
          // Stall exists but is unassigned AND we have a user to link → assign the user
          // Only if the user has no other stall (never unassign an existing link)
          if (matchedVendor && !currentVendor && !matchedVendorHasStall) {
            try {
              const linkRes = await fetch(
                `${API_URL}/api/admin/stalls/${existingStall._id}/link-to-user/${matchedVendor._id}`,
                { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
              );
              if (linkRes.ok) {
                existingLinked++;
                successCount++;
              } else {
                const errorData = await linkRes.json().catch(() => ({}));
                recordError(i, stallData.stallName, `Link failed: ${errorData.detail || 'HTTP ' + linkRes.status}`);
              }
            } catch (error) {
              recordError(i, stallData.stallName, `Link error: ${error.message || 'unknown'}`);
            }
            continue;
          }
          // User already owns a different stall — leave both stalls alone
          if (matchedVendor && !currentVendor && matchedVendorHasStall) {
            skippedCount++;
            successCount++;
            continue;
          }
          // Stall exists, no matched user → skip creation (email already tied to a stall)
          if (!matchedVendor) {
            skippedCount++;
            successCount++;
            continue;
          }
          // Stall exists AND assigned to a DIFFERENT vendor → do NOT create duplicate
          recordError(i, stallData.stallName, `Stall for "${emailVal || vendorEmailRaw}" already exists and is assigned to another vendor. Skipped.`);
          continue;
        }

        // No existing stall for this email → create a fresh stall.
        // If a matching user exists (matchedVendor), create-stall also links user.stall_id ↔ stall.vendorId server-side.
        try {
          const response = await fetch(`${API_URL}/api/admin/create-stall`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(stallData),
          });

          if (response.ok) {
            successCount++;
            newlyCreated++;
          } else {
            const errorData = await response.json().catch(() => ({}));
            const reason = typeof errorData.detail === 'string'
              ? errorData.detail
              : (Array.isArray(errorData.detail) ? errorData.detail.map(d => d.msg || JSON.stringify(d)).join('; ') : `HTTP ${response.status}`);
            recordError(i, stallData.stallName, reason);
          }
        } catch (error) {
          recordError(i, stallData.stallName, `Network error: ${error.message || 'unknown'}`);
        }
      }

      setUploadProgress({ current: total, total, status: 'Upload complete!' });

      // Store errors so the inline panel can render them
      setUploadErrors(errors);

      const partsList = [];
      if (newlyCreated > 0) partsList.push(`${newlyCreated} created`);
      if (existingLinked > 0) partsList.push(`${existingLinked} existing stall${existingLinked === 1 ? '' : 's'} linked to vendor`);
      if (skippedCount > 0) partsList.push(`${skippedCount} skipped (already linked)`);
      if (errorCount > 0) partsList.push(`${errorCount} failed`);
      setMessage({
        type: successCount > 0 ? 'success' : 'error',
        text: `Bulk upload complete! ${partsList.join(', ') || 'No changes'}`
      });

      await new Promise(resolve => setTimeout(resolve, 500));

      // Keep modal open if any errors so admin can review them
      if (errorCount === 0) {
        setShowBulkUploadStalls(false);
        resetBulkUpload();
      }
      fetchStalls();
    } catch (error) {
      setMessage({ type: 'error', text: 'Bulk upload failed' });
    } finally {
      setProcessing(null);
    }
  };

  const VendorCard = ({ vendor, showActions = true, showDelete = false }) => {
    const linkedStall = vendor.stall_id ? allStalls.find(s => s._id === vendor.stall_id) : null;
    const companyName = linkedStall?.stallName || vendor.company_name;
    return (
    <div className={`border rounded-lg p-4 bg-white dark:bg-[#252525] hover:border-[#FFD12D] transition-all ${
      vendor.status === 'declined' 
        ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10' 
        : 'border-gray-200 dark:border-gray-700'
    }`}>
      <div className="flex flex-col gap-3 min-w-0">
        {/* Vendor Info */}
        <div className="flex-1 min-w-0">
          {companyName && (
            <p className="font-bold text-lg text-[#252525] dark:text-white">
              {companyName}
            </p>
          )}
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            <span className={companyName 
              ? "text-sm font-semibold text-gray-700 dark:text-gray-300" 
              : "font-bold text-lg text-[#252525] dark:text-white"}>
              {vendor.name}
            </span>
            {/* Role Badge */}
            <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${
              vendor.role === 'admin' 
                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-700' 
                : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700'
            }`}>
              {vendor.role === 'admin' ? '👑 Admin' : '🏪 Vendor'}
            </span>
            {/* Suspended (payment-failed) Badge */}
            {vendor.status === 'suspended' && (
              <span
                className="px-2 py-0.5 text-xs rounded-full font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-400 dark:border-red-700"
                data-testid="suspended-badge"
                title="Payment failed — stall is drafted"
              >
                ⚠ Suspended
              </span>
            )}
            {/* Vendor Type Tag */}
            {vendor.role === 'vendor' && (
              <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${
                vendor.vendor_type === 'expo'
                  ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-300 dark:border-orange-700'
                  : vendor.vendor_type === 'festival'
                  ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 border border-pink-300 dark:border-pink-700'
                  : 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border border-teal-300 dark:border-teal-700'
              }`}>
                {vendor.vendor_type === 'expo' ? 'Expo' : vendor.vendor_type === 'festival' ? 'Festival' : 'Market'}
              </span>
            )}
            {/* Subscription Tag */}
            {vendor.role === 'vendor' && (
              <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${
                vendor.subscription === 'manual'
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700'
                  : vendor.subscription === 'cash' || !vendor.subscription
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600' 
                  : vendor.subscription === 'trial'
                  ? (() => {
                      const trialStart = vendor.trial_start_date ? new Date(vendor.trial_start_date) : null;
                      const trialEnd = trialStart ? new Date(trialStart.getTime() + 7 * 24 * 60 * 60 * 1000) : null;
                      const expired = trialEnd ? new Date() > trialEnd : false;
                      return expired
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700';
                    })()
                  : vendor.subscription === 'trial_expired'
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700'
                  : vendor.subscription === 'monthly'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'
                  : vendor.subscription === 'yearly'
                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
              }`}>
                {vendor.subscription === 'manual' && '🔒 Manual'}
                {vendor.subscription === 'cash' && '💵 Cash'}
                {vendor.subscription === 'trial' && (() => {
                  const trialStart = vendor.trial_start_date ? new Date(vendor.trial_start_date) : null;
                  const trialEnd = trialStart ? new Date(trialStart.getTime() + 7 * 24 * 60 * 60 * 1000) : null;
                  if (!trialEnd) return '⏱️ Trial';
                  const now = new Date();
                  const diffMs = trialEnd.getTime() - now.getTime();
                  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
                  if (diffDays > 0) return `⏱️ Trial (${diffDays}d left)`;
                  return `⛔ Expired (${Math.abs(diffDays)}d ago)`;
                })()}
                {vendor.subscription === 'trial_expired' && '⛔ Expired'}
                {vendor.subscription === 'monthly' && '💳 Monthly'}
                {vendor.subscription === 'yearly' && '📅 Yearly'}
                {!vendor.subscription && '💵 Cash'}
              </span>
            )}
            {vendor.status === 'declined' && (
              <span className="px-2 py-1 bg-red-600 text-white text-xs rounded-full font-semibold">
                Declined
              </span>
            )}
          </div>
          {vendor.phone && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 font-semibold">
              📞 {vendor.phone}
            </p>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 break-all">{vendor.email}</p>
          {/* Show extra signup details for pending vendors */}
          {(vendor.status === 'pending' || vendor.status === 'declined') && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
              {vendor.first_name && (
                <span>👤 {vendor.first_name}{vendor.last_name ? ` ${vendor.last_name}` : ''}</span>
              )}
              {vendor.main_location && (
                <span>📍 {vendor.main_location}</span>
              )}
              {vendor.stall_number && (
                <span>🏪 Stall: {vendor.stall_number}</span>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons - Now below stall number */}
        {showActions && (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => handleApproval(vendor._id, 'approved')}
              disabled={processing === vendor._id}
              className="bg-green-600 hover:bg-green-700 text-white flex-1 min-w-[120px] text-sm py-2"
            >
              {processing === vendor._id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </>
              )}
            </Button>
            {vendor.status !== 'declined' && (
              <Button
                onClick={() => handleApproval(vendor._id, 'declined')}
                disabled={processing === vendor._id}
                variant="destructive"
                className="flex-1 min-w-[120px] text-sm py-2"
              >
                {processing === vendor._id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    Decline
                  </>
                )}
              </Button>
            )}
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDeleteVendor(vendor._id);
              }}
              disabled={processing === vendor._id}
              variant="outline"
              className="px-4 border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm py-2"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
        {showDelete && (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOpenEditUser(vendor);
              }}
              variant="outline"
              className="flex-1 sm:flex-none border-blue-400 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-sm py-2"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleResetPassword(vendor.email);
              }}
              disabled={processing === vendor.email}
              variant="outline"
              className="flex-1 sm:flex-none border-[#FFD12D] text-[#252525] hover:bg-[#FFD12D] dark:text-white dark:hover:text-[#252525] text-sm py-2"
            >
              {processing === vendor.email ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <KeyRound className="w-4 h-4 mr-2" />
                  Reset Password
                </>
              )}
            </Button>
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDeleteVendor(vendor._id);
              }}
              disabled={processing === vendor._id}
              variant="outline"
              className="flex-1 sm:flex-none border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm py-2"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        )}
      </div>
    </div>
  );
  }

  if (!isOpen) return null;

  // Derive per-vendor multi-type context for the Add/Edit Stall modals
  const TYPE_META = {
    market: { plural: 'Markets', singular: 'Market', items: markets },
    expo: { plural: 'Expos', singular: 'Expo', items: expos },
    festival: { plural: 'Festivals', singular: 'Festival', items: festivals },
  };
  const buildStallCtx = (vendorId, activeTab) => {
    const vendor = allVendors.find(v => v._id === vendorId);
    const primaryType = vendor?.vendor_type || 'market';
    const grantedTypes = (vendor?.vendor_types && vendor.vendor_types.length > 0)
      ? vendor.vendor_types
      : [primaryType];
    const tab = activeTab || primaryType;
    const meta = TYPE_META[tab] || TYPE_META.market;
    return {
      vendor,
      vendorId,
      primaryType,
      grantedTypes,
      activeTab: tab,
      hasAccess: grantedTypes.includes(tab),
      label: `${meta.plural} & Locations`,
      singular: meta.singular,
      plural: meta.plural.toLowerCase(),
      selectLabel: `Select ${meta.plural}`,
      searchPlaceholder: `Search ${meta.plural.toLowerCase()}...`,
      emptyText: `No ${meta.plural.toLowerCase()} yet`,
      items: meta.items,
    };
  };
  const newStallCtx = buildStallCtx(newStall.vendorId, adminNewStallTab);
  const editStallCtx = buildStallCtx(editStallVendorId, adminEditStallTab);

  // Panel shown in the admin's Add/Edit Stall modal when the current tab's type is not granted to the vendor.
  // Includes a quick-grant button so the admin can enable access on the spot.
  const NoTypeAccessPanel = ({ vendor, typeKey, onGrant }) => {
    const meta = TYPE_META[typeKey];
    return (
      <div className="bg-gray-100 dark:bg-[#1a1a1a] border-2 border-dashed border-gray-300 dark:border-gray-700 p-5 rounded-xl">
        <div className="flex items-start gap-3">
          <MapPin className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-gray-600 dark:text-gray-400 mb-1">
              {meta.plural} & Locations <span className="text-xs font-normal text-gray-500">(no access)</span>
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-500 mb-3">
              {vendor?.name || 'This vendor'} does not currently have access to add {meta.plural.toLowerCase()}. Grant access below to enable it.
            </p>
            {vendor?._id ? (
              <Button
                type="button"
                onClick={() => onGrant && onGrant(vendor._id, typeKey)}
                variant="outline"
                data-testid={`admin-grant-${typeKey}`}
                className="rounded-full border-[#FFD12D] text-[#252525] dark:text-white hover:bg-[#FFD12D] hover:text-[#252525]"
              >
                + Grant {meta.singular} Access
              </Button>
            ) : (
              <p className="text-xs text-orange-600 dark:text-orange-400">Assign a vendor first to grant access.</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-[#1a1a1a] sm:rounded-2xl sm:border-4 border-[#FFD12D] w-full h-full sm:h-auto sm:max-w-6xl sm:max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Sticky Header Section */}
        <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a]">
          {/* Title Bar */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-3 py-3 sm:p-6 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#FFD12D] flex items-center justify-center">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-[#252525]" />
              </div>
              <h2 className="text-base sm:text-2xl font-bold text-[#252525] dark:text-white">
                Admin Dashboard
              </h2>
            </div>
            <Button
              onClick={onClose}
              variant="ghost"
              className="w-8 h-8 sm:w-10 sm:h-10 p-0 rounded-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] transition-all duration-300 shadow-md"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="px-3 py-4 sm:p-6 min-w-0">
            {message.text && (
              <Alert
                className={`mb-4 relative ${
                  message.type === 'success'
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : message.type === 'warning'
                      ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
                      : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                }`}
              >
                <AlertDescription
                  className={`pr-8 ${
                    message.type === 'success'
                      ? 'text-green-800 dark:text-green-200'
                      : message.type === 'warning'
                        ? 'text-orange-800 dark:text-orange-200'
                        : 'text-red-800 dark:text-red-200'
                  }`}
                >
                  {message.text}
                </AlertDescription>
                <button
                  onClick={() => setMessage({ type: '', text: '' })}
                  className={`absolute top-2 right-2 p-1 rounded-full hover:bg-black/10 transition-colors ${
                    message.type === 'success'
                      ? 'text-green-600 dark:text-green-400'
                      : message.type === 'warning'
                        ? 'text-orange-600 dark:text-orange-400'
                        : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  <X className="w-4 h-4" />
                </button>
              </Alert>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#FFD12D]" />
              </div>
            ) : (
              <Tabs defaultValue="pending" className="w-full">
                {/* Sticky Tabs within scroll area */}
                <div className="sticky top-0 z-10 bg-white dark:bg-[#1a1a1a] -mx-3 px-3 sm:-mx-6 sm:px-6 pt-0 pb-3 sm:pb-4 border-b border-gray-200 dark:border-gray-700">
                  <TabsList className="flex w-full h-auto p-1 sm:p-1.5 bg-gray-100 dark:bg-[#252525] rounded-full overflow-x-auto gap-1 no-scrollbar">
                    <TabsTrigger value="pending" className="data-[state=active]:bg-[#FFD12D] data-[state=active]:text-[#252525] text-xs py-2 px-2 sm:px-3 flex items-center justify-center gap-1 rounded-full whitespace-nowrap flex-shrink-0 flex-1 min-w-0">
                      <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="hidden sm:inline text-xs">Pending</span>
                      {(() => {
                        const pendingCount = allVendors.filter(v => v.status === 'pending' || v.status === 'declined').length + typeAccessRequests.length + attentionVendors.length;
                        return pendingCount > 0 ? (
                          <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="pending-tab-badge">
                            {pendingCount}
                          </span>
                        ) : null;
                      })()}
                    </TabsTrigger>
                    <TabsTrigger value="options" className="data-[state=active]:bg-[#FFD12D] data-[state=active]:text-[#252525] text-xs py-2 px-2 sm:px-3 flex items-center justify-center gap-1 rounded-full whitespace-nowrap flex-shrink-0 flex-1 min-w-0">
                      <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="hidden sm:inline text-xs">Options</span>
                    </TabsTrigger>
                    <TabsTrigger value="analytics" className="data-[state=active]:bg-[#FFD12D] data-[state=active]:text-[#252525] text-xs py-2 px-2 sm:px-3 flex items-center justify-center gap-1 rounded-full whitespace-nowrap flex-shrink-0 flex-1 min-w-0">
                      <BarChart3 className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="hidden sm:inline text-xs">Analytics</span>
                    </TabsTrigger>
                    <TabsTrigger value="vendors" className="data-[state=active]:bg-[#FFD12D] data-[state=active]:text-[#252525] text-xs py-2 px-2 sm:px-3 flex items-center justify-center gap-1 rounded-full whitespace-nowrap flex-shrink-0 flex-1 min-w-0">
                      <UserCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="hidden sm:inline text-xs">Users</span>
                    </TabsTrigger>
                    <TabsTrigger value="stalls" className="data-[state=active]:bg-[#FFD12D] data-[state=active]:text-[#252525] text-xs py-2 px-2 sm:px-3 flex items-center justify-center gap-1 rounded-full whitespace-nowrap flex-shrink-0 flex-1 min-w-0">
                      <Store className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="hidden sm:inline text-xs">Stalls</span>
                    </TabsTrigger>
                    <TabsTrigger value="media" onClick={fetchMedia} className="data-[state=active]:bg-[#FFD12D] data-[state=active]:text-[#252525] text-xs py-2 px-2 sm:px-3 flex items-center justify-center gap-1 rounded-full whitespace-nowrap flex-shrink-0 flex-1 min-w-0">
                      <ImageIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="hidden sm:inline text-xs">Media</span>
                    </TabsTrigger>
                    <TabsTrigger value="payfast" onClick={fetchRecentTransactions} className="data-[state=active]:bg-[#FFD12D] data-[state=active]:text-[#252525] text-xs py-2 px-2 sm:px-3 flex items-center justify-center gap-1 rounded-full whitespace-nowrap flex-shrink-0 flex-1 min-w-0">
                      <CreditCard style={{ width: '14px', height: '14px', minWidth: '14px', minHeight: '14px' }} className="flex-shrink-0 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline text-xs">PayFast</span>
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* Pending Vendors Tab (includes pending vendor signups + type-access requests) */}
                <TabsContent value="pending" className="space-y-4 mt-4">
                  {/* Type Access Requests section */}
                  {typeAccessRequests.length > 0 && (
                    <div className="space-y-3" data-testid="type-access-requests-section">
                      <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-[#FFD12D]" />
                        Access Requests ({typeAccessRequests.length})
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Vendors requesting access to add locations at a new vendor type.
                      </p>
                      {typeAccessRequests.map(req => (
                        <div
                          key={req.id}
                          className="bg-white dark:bg-[#252525] border-2 border-[#FFD12D]/40 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                          data-testid={`type-access-request-${req.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-[#252525] dark:text-white truncate">
                              {req.vendor_name || 'Unknown'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {req.vendor_email}
                            </p>
                            <p className="text-sm mt-1">
                              Requesting access to <span className="font-bold capitalize text-[#FFD12D]">{req.requested_type}s</span>
                            </p>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              onClick={() => handleTypeAccessDecision(req.id, 'approve')}
                              className="rounded-full bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`approve-access-${req.id}`}
                            >
                              Approve
                            </Button>
                            <Button
                              onClick={() => handleTypeAccessDecision(req.id, 'reject')}
                              variant="outline"
                              className="rounded-full border-red-500 text-red-600 hover:bg-red-50"
                              data-testid={`reject-access-${req.id}`}
                            >
                              Reject
                            </Button>
                          </div>
                        </div>
                      ))}
                      <Separator className="bg-gray-200 dark:bg-gray-700 my-4" />
                    </div>
                  )}

                  {/* Vendors needing attention (failed / expired payments, suspended) */}
                  <div className="space-y-3" data-testid="attention-vendors-section">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-red-500" />
                          Needs Attention ({attentionVendors.length})
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Vendors flagged with failed payments, expired subscriptions, or already suspended.
                        </p>
                      </div>
                      <Button
                        onClick={() => { setPayFastSyncResult(null); setShowPayFastSyncModal(true); }}
                        className="rounded-full bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] text-xs sm:text-sm whitespace-nowrap"
                        data-testid="import-payfast-sync-btn"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Sync from PayFast
                      </Button>
                    </div>

                    {/* Manual suspend by email — for PayFast failures that never reached the ITN webhook */}
                    <div className="flex flex-col sm:flex-row gap-2 p-3 rounded-xl border-2 border-dashed border-red-400 bg-red-50 dark:bg-red-950/20">
                      <input
                        type="email"
                        value={manualSuspendEmail}
                        onChange={(e) => setManualSuspendEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleManualSuspendByEmail()}
                        placeholder="vendor@example.com"
                        className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#252525] text-sm text-[#252525] dark:text-white"
                        data-testid="manual-suspend-email-input"
                      />
                      <Button
                        onClick={handleManualSuspendByEmail}
                        disabled={manualSuspending || !manualSuspendEmail.trim()}
                        className="rounded-full bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm whitespace-nowrap"
                        data-testid="manual-suspend-submit"
                      >
                        {manualSuspending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Suspend for Payment'}
                      </Button>
                    </div>

                    {attentionVendors.length === 0 ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400 italic p-4 text-center">
                        No vendors currently flagged — the app hasn't detected any failed payments.
                      </div>
                    ) : (
                      attentionVendors.map(v => (
                        <div
                          key={v.user_id}
                          className="bg-white dark:bg-[#252525] border-2 border-red-500/40 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                          data-testid={`attention-vendor-${v.user_id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-[#252525] dark:text-white truncate">
                              {v.name || v.email}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{v.email}</p>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {v.reasons.map(r => (
                                <span key={r} className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                                  {r.replaceAll('_', ' ')}
                                </span>
                              ))}
                              {v.stall && (
                                <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                                  stall: {v.stall.status}
                                </span>
                              )}
                            </div>
                            {v.last_payment && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Last payment: <span className="font-semibold">{v.last_payment.status}</span>
                                {typeof v.last_payment.amount === 'number' && <> · R{v.last_payment.amount.toFixed(2)}</>}
                                {v.last_payment.plan && <> · {v.last_payment.plan}</>}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              onClick={() => handleSuspendForPayment(v.user_id, v.email)}
                              disabled={v.status === 'suspended' && v.stall?.status === 'draft'}
                              className="rounded-full bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm"
                              data-testid={`suspend-attention-${v.user_id}`}
                            >
                              {v.status === 'suspended' ? 'Already Suspended' : 'Suspend for Payment'}
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                    <Separator className="bg-gray-200 dark:bg-gray-700 my-4" />
                  </div>

                  {/* Pending vendor signups */}
                  {allVendors.filter(v => v.status === 'pending' || v.status === 'declined').length === 0 ? (
                    typeAccessRequests.length === 0 && (
                      <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                        <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No pending vendor applications</p>
                      </div>
                    )
                  ) : (
                    <>
                      <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                        <Users className="w-5 h-5 text-[#FFD12D]" />
                        Vendor Applications
                      </h3>
                      {allVendors
                        .filter(v => v.status === 'pending' || v.status === 'declined')
                        .map((vendor) => (
                          <VendorCard key={vendor._id} vendor={vendor} showActions={true} showDelete={false} />
                        ))}
                    </>
                  )}
                </TabsContent>

              {/* Vendors Tab */}
              <TabsContent value="vendors" className="space-y-4">
                <AdminVendorsTab
                  allVendors={allVendors}
                  allStalls={allStalls}
                  userFilter={userFilter} setUserFilter={setUserFilter}
                  userSearch={userSearch} setUserSearch={setUserSearch}
                  userSortOrder={userSortOrder} setUserSortOrder={setUserSortOrder}
                  usersPerPage={usersPerPage} setUsersPerPage={setUsersPerPage}
                  usersCurrentPage={usersCurrentPage} setUsersCurrentPage={setUsersCurrentPage}
                  setShowAddUserModal={setShowAddUserModal}
                  setShowBulkUploadVendors={setShowBulkUploadVendors}
                  setShowExportModal={setShowExportModal}
                  setShowBulkDeleteModal={setShowBulkDeleteModal}
                  VendorCard={VendorCard}
                />
              </TabsContent>


              {/* Options Tab - Card Layout */}
              <TabsContent value="options" className="space-y-4">
                <AdminOptionsTab
                  categories={categories} tags={tags} markets={markets}
                  expos={expos} festivals={festivals}
                  setShowCategoriesModal={setShowCategoriesModal}
                  setShowTagsModal={setShowTagsModal}
                  setShowMarketsModal={setShowMarketsModal}
                  setShowExposModal={setShowExposModal}
                  setShowFestivalsModal={setShowFestivalsModal}
                  setMessage={setMessage}
                  fetchCategoriesAndTags={fetchCategoriesAndTags}
                  fetchStalls={fetchStalls}
                  fetchVendors={fetchVendors}
                  fetchLegalContent={fetchLegalContent}
                  setShowLegalModal={setShowLegalModal}
                  setShowEmailDashboard={setShowEmailDashboard}
                  setShowProvincesModal={setShowProvincesModal}
                />
              </TabsContent>

              {/* Analytics Tab */}
              <TabsContent value="analytics" className="space-y-6">
                <AdminAnalyticsTab
                  analyticsSummary={analyticsSummary}
                  refreshAnalytics={refreshAnalytics}
                  handleClearAnalytics={handleClearAnalytics}
                  linkClickAnalytics={linkClickAnalytics}
                  showAllDirections={showAllDirections}
                  setShowAllDirections={setShowAllDirections}
                />
              </TabsContent>

              {/* Stalls Management Tab */}
              <TabsContent value="stalls" className="space-y-3">
                <AdminStallsTab
                  allStalls={allStalls}
                  allVendors={allVendors}
                  stallFilter={stallFilter} setStallFilter={setStallFilter}
                  stallSearch={stallSearch} setStallSearch={setStallSearch}
                  stallSortOrder={stallSortOrder} setStallSortOrder={setStallSortOrder}
                  stallsPerPage={stallsPerPage} setStallsPerPage={setStallsPerPage}
                  stallsCurrentPage={stallsCurrentPage} setStallsCurrentPage={setStallsCurrentPage}
                  setShowAddStallModal={setShowAddStallModal}
                  setShowBulkUploadStalls={setShowBulkUploadStalls}
                  setShowExportStallsModal={setShowExportStallsModal}
                  setShowBulkDeleteStallsModal={setShowBulkDeleteStallsModal}
                  handleDeleteSingleStall={handleDeleteSingleStall}
                  openEditStallModal={openEditStallModal}
                  processing={processing}
                />
              </TabsContent>

              {/* Media Management Tab */}
              {/* Media Tab */}
              <TabsContent value="media" className="space-y-4">
                <AdminMediaTab
                  mediaFiles={mediaFiles}
                  mediaFilter={mediaFilter}
                  setMediaFilter={setMediaFilter}
                  mediaSearch={mediaSearch}
                  setMediaSearch={setMediaSearch}
                  loadingMedia={loadingMedia}
                  fetchMedia={fetchMedia}
                  handleScanBroken={handleScanBroken}
                  handleCleanupOrphaned={handleCleanupOrphaned}
                  handleDeleteMedia={handleDeleteMedia}
                  orphanedCount={orphanedCount}
                  API_URL={API_URL}
                />
              </TabsContent>

              {/* PayFast Configuration Tab */}
              <TabsContent value="payfast" className="space-y-6">
                <AdminPayFastTab
                  allVendors={allVendors}
                  recentTransactions={recentTransactions}
                  loadingTransactions={loadingTransactions}
                  fetchRecentTransactions={fetchRecentTransactions}
                  fetchVendors={fetchVendors}
                  token={token}
                />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>

      {/* Add Stall Modal - Full Form (Matches VendorDashboard) */}
      <Dialog open={showAddStallModal} onOpenChange={setShowAddStallModal}>
        <DialogContent className="sm:max-w-3xl bg-white dark:bg-[#1a1a1a] border-4 border-[#FFD12D] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#252525] dark:text-white flex items-center gap-2">
              <Store className="w-6 h-6 text-[#FFD12D]" />
              Add New Stall
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            
            {/* Images Section */}
            <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
              <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                <Image className="w-5 h-5 text-[#FFD12D]" />
                Images
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <FileImage className="w-4 h-4 text-[#FFD12D]" />
                    Logo <span className="text-red-500">*</span>
                  </Label>
                  {newStallFiles.logo ? (
                    <div className="w-32 h-32 rounded-xl border-4 border-[#FFD12D] mb-2 flex items-center justify-center bg-green-50 dark:bg-green-900/20">
                      <span className="text-xs text-green-600 text-center px-2">✓ {newStallFiles.logo.name}</span>
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-xl border-4 border-dashed border-red-300 dark:border-red-500 mb-2 flex items-center justify-center bg-red-50 dark:bg-red-900/20">
                      <span className="text-xs text-red-500 text-center px-2">Logo required</span>
                    </div>
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setNewStallFiles({ ...newStallFiles, logo: e.target.files[0] })}
                    className="dark:bg-[#1a1a1a] dark:text-white cursor-pointer"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <FileImage className="w-4 h-4 text-[#FFD12D]" />
                    Banner <span className="text-red-500">*</span>
                  </Label>
                  {newStallFiles.banner ? (
                    <div className="w-full h-32 rounded-xl border-2 border-[#FFD12D] mb-2 flex items-center justify-center bg-green-50 dark:bg-green-900/20">
                      <span className="text-sm text-green-600">✓ {newStallFiles.banner.name}</span>
                    </div>
                  ) : (
                    <div className="w-full h-32 rounded-xl border-2 border-dashed border-red-300 dark:border-red-500 mb-2 flex items-center justify-center bg-red-50 dark:bg-red-900/20">
                      <span className="text-sm text-red-500">Banner image required</span>
                    </div>
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setNewStallFiles({ ...newStallFiles, banner: e.target.files[0] })}
                    className="dark:bg-[#1a1a1a] dark:text-white cursor-pointer"
                  />
                </div>
              </div>
            </div>

            <Separator className="bg-gray-200 dark:bg-gray-700" />

            {/* Basic Information Section */}
            <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
              <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                <Store className="w-5 h-5 text-[#FFD12D]" />
                Basic Information
              </h3>
              
              <div className="space-y-2">
                <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                  <Store className="w-4 h-4 text-[#FFD12D]" />
                  Company Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={newStall.stallName}
                  onChange={(e) => setNewStall({ ...newStall, stallName: e.target.value })}
                  placeholder="e.g., Fresh Farm Produce"
                  className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                  <Tag className="w-4 h-4 text-[#FFD12D]" />
                  Category <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={newStall.category}
                  onValueChange={(value) => setNewStall({ ...newStall, category: value })}
                >
                  <SelectTrigger className="dark:bg-[#1a1a1a] dark:text-white">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#252525] dark:text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-[#FFD12D]" />
                    Tags <span className="text-gray-400 text-sm">(Select up to 10)</span>
                  </span>
                  <span className="text-xs text-gray-500">
                    {newStallSelectedTags.length}/10 selected
                  </span>
                </Label>
                <CheckboxListWithSearch
                  items={tags}
                  selected={newStallSelectedTags}
                  onToggle={handleNewStallTagToggle}
                  maxSelected={10}
                  Icon={Tag}
                  testId="admin-create-stall-tags"
                  searchPlaceholder="Search tags..."
                  emptyText="Loading tags..."
                />
                {newStallSelectedTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 bg-[#FFD12D]/10 rounded-lg border border-[#FFD12D]/30">
                    <span className="text-xs font-semibold text-[#252525] dark:text-[#FFD12D]">Selected:</span>
                    {newStallSelectedTags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-[#FFD12D] text-[#252525] text-xs font-semibold rounded-full flex items-center gap-1"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleNewStallTagToggle(tag)}
                          className="hover:bg-[#252525] hover:text-[#FFD12D] rounded-full w-4 h-4 flex items-center justify-center"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[#FFD12D]" />
                  Slogan <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={newStall.slogan}
                  onChange={(e) => setNewStall({ ...newStall, slogan: e.target.value })}
                  placeholder="e.g., Farm Fresh, Daily Delivered"
                  className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#252525] dark:text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#FFD12D]" />
                    About <span className="text-red-500">*</span>
                  </span>
                  <span className="text-xs text-gray-500">
                    {newStall.about?.length || 0}/250
                  </span>
                </Label>
                <Textarea
                  value={newStall.about}
                  onChange={(e) => {
                    if (e.target.value.length <= 250) {
                      setNewStall({ ...newStall, about: e.target.value });
                    }
                  }}
                  placeholder="Tell customers about your business (max 250 characters)"
                  maxLength={250}
                  rows={4}
                  className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                />
              </div>
            </div>

            <Separator className="bg-gray-200 dark:bg-gray-700" />

            {/* Contact Information Section */}
            <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
              <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                <Phone className="w-5 h-5 text-[#FFD12D]" />
                Contact Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#FFD12D]" />
                    Phone <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={newStall.phone}
                    onChange={(e) => setNewStall({ ...newStall, phone: e.target.value })}
                    placeholder="+27 12 345 6789"
                    className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#FFD12D]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    WhatsApp <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={newStall.whatsapp}
                    onChange={(e) => setNewStall({ ...newStall, whatsapp: e.target.value })}
                    placeholder="+27 12 345 6789"
                    className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#FFD12D]" />
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="email"
                  value={newStall.email}
                  onChange={(e) => setNewStall({ ...newStall, email: e.target.value })}
                  placeholder="contact@example.com"
                  className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                  <Globe className="w-4 h-4 text-[#FFD12D]" />
                  Website <span className="text-gray-400 text-sm">(Optional)</span>
                </Label>
                <Input
                  value={newStall.website}
                  onChange={(e) => setNewStall({ ...newStall, website: e.target.value })}
                  placeholder="https://www.yourwebsite.com"
                  className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                />
              </div>
            </div>

            <Separator className="bg-gray-200 dark:bg-gray-700" />

            {/* Social Media Section */}
            <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
              <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                <svg className="w-5 h-5 text-[#FFD12D]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
                </svg>
                Social Media
                <span className="text-gray-400 text-sm font-normal ml-2">(Optional)</span>
              </h3>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#FFD12D]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
                    </svg>
                    Facebook
                  </Label>
                  <Input
                    value={newStall.socialMedia.facebook}
                    onChange={(e) => setNewStall({ ...newStall, socialMedia: { ...newStall.socialMedia, facebook: e.target.value } })}
                    placeholder="https://facebook.com/yourpage"
                    className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#FFD12D]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"/>
                    </svg>
                    Instagram
                  </Label>
                  <Input
                    value={newStall.socialMedia.instagram}
                    onChange={(e) => setNewStall({ ...newStall, socialMedia: { ...newStall.socialMedia, instagram: e.target.value } })}
                    placeholder="https://instagram.com/yourprofile"
                    className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#FFD12D]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                    TikTok
                  </Label>
                  <Input
                    value={newStall.socialMedia.tiktok}
                    onChange={(e) => setNewStall({ ...newStall, socialMedia: { ...newStall.socialMedia, tiktok: e.target.value } })}
                    placeholder="https://tiktok.com/@yourprofile"
                    className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#FFD12D]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    X (Twitter)
                  </Label>
                  <Input
                    value={newStall.socialMedia.twitter}
                    onChange={(e) => setNewStall({ ...newStall, socialMedia: { ...newStall.socialMedia, twitter: e.target.value } })}
                    placeholder="https://x.com/yourhandle"
                    className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#FFD12D]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    LinkedIn
                  </Label>
                  <Input
                    value={newStall.socialMedia.linkedin}
                    onChange={(e) => setNewStall({ ...newStall, socialMedia: { ...newStall.socialMedia, linkedin: e.target.value } })}
                    placeholder="https://linkedin.com/in/yourprofile"
                    className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                  />
                </div>
              </div>
            </div>

            <Separator className="bg-gray-200 dark:bg-gray-700" />

            {/* Markets & Locations Section — tabbed by vendor type */}
            <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-6">
              <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#FFD12D]" />
                {newStallCtx.label}
              </h3>

              {/* Tab bar */}
              <div className="flex flex-wrap gap-2 border-b-2 border-gray-200 dark:border-gray-700 pb-3" data-testid="admin-new-stall-location-tabs">
                {['market', 'expo', 'festival'].map(t => {
                  const isActive = adminNewStallTab === t;
                  const meta = TYPE_META[t];
                  const hasAccess = newStallCtx.grantedTypes.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setAdminNewStallTab(t)}
                      data-testid={`admin-new-stall-tab-${t}`}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-2 ${
                        isActive
                          ? 'bg-[#FFD12D] text-[#252525] shadow-md'
                          : hasAccess
                            ? 'bg-white dark:bg-[#1a1a1a] text-[#252525] dark:text-white hover:bg-gray-100 dark:hover:bg-[#252525]'
                            : 'bg-white dark:bg-[#1a1a1a] text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      <MapPin className={`w-4 h-4 ${isActive ? 'text-[#252525]' : hasAccess ? 'text-[#FFD12D]' : 'text-gray-400'}`} />
                      {meta.plural}
                      {!hasAccess && <span className="text-[10px] uppercase tracking-wider">Locked</span>}
                    </button>
                  );
                })}
              </div>

              {newStallCtx.hasAccess ? (
                <>
                  {/* Market Selection — checkbox list matching the Vendor dashboard style */}
                  <div className="space-y-3">
                    <Label className="text-[#252525] dark:text-white font-semibold">
                      {newStallCtx.selectLabel} <span className="text-red-500">*</span>
                    </Label>
                    <CheckboxListWithSearch
                      items={newStallCtx.items}
                      selected={newStallSelectedMarkets.filter(name => newStallCtx.items.includes(name))}
                      onToggle={handleNewStallMarketToggle}
                      Icon={MapPin}
                      testId={`admin-create-stall-markets-${adminNewStallTab}`}
                      searchPlaceholder={newStallCtx.searchPlaceholder}
                      emptyText={newStallCtx.emptyText}
                    />
                    {newStallSelectedMarkets.length === 0 && (
                      <p className="text-sm text-orange-600 dark:text-orange-400">
                        Please select at least one {newStallCtx.singular.toLowerCase()}
                      </p>
                    )}
                  </div>

                  {/* Location Details for the current tab's selected items */}
                  {newStallSelectedMarkets.filter(name => newStallCtx.items.includes(name)).length > 0 && (
                    <div className="space-y-6 mt-6">
                      <div className="border-t-2 border-[#FFD12D] pt-4">
                        <h4 className="font-semibold text-[#252525] dark:text-white mb-4">
                          Location Details for Each {newStallCtx.singular}:
                        </h4>
                      </div>

                      {newStallSelectedMarkets.filter(name => newStallCtx.items.includes(name)).map((market) => (
                    <div key={market} className="bg-white dark:bg-[#1a1a1a] p-5 rounded-lg border-2 border-[#FFD12D]/30 space-y-4">
                      <div className="flex items-center gap-2 mb-3">
                        <MapPin className="w-5 h-5 text-[#FFD12D]" />
                        <h5 className="font-bold text-[#252525] dark:text-white text-lg">{market}</h5>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                          <Hash className="w-4 h-4 text-[#FFD12D]" />
                          Stall Number <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={newStallMarketLocations[market]?.stallNumber || ''}
                          onChange={(e) => updateNewStallMarketLocation(market, 'stallNumber', e.target.value)}
                          placeholder="e.g., A-12"
                          className="dark:bg-[#252525] dark:text-white placeholder:text-gray-400"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-[#252525] dark:text-white">
                            Latitude <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            type="number"
                            step="0.000001"
                            value={newStallMarketLocations[market]?.lat || ''}
                            onChange={(e) => updateNewStallMarketLocation(market, 'lat', e.target.value)}
                            placeholder="-26.204100"
                            className="dark:bg-[#252525] dark:text-white placeholder:text-gray-400"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#252525] dark:text-white">
                            Longitude <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            type="number"
                            step="0.000001"
                            value={newStallMarketLocations[market]?.lng || ''}
                            onChange={(e) => updateNewStallMarketLocation(market, 'lng', e.target.value)}
                            placeholder="28.047300"
                            className="dark:bg-[#252525] dark:text-white placeholder:text-gray-400"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
                </>
              ) : (
                <NoTypeAccessPanel
                  vendor={newStallCtx.vendor}
                  typeKey={adminNewStallTab}
                  onGrant={handleAdminGrantType}
                />
              )}
            </div>

            <Separator className="bg-gray-200 dark:bg-gray-700" />

            {/* Vendor Assignment */}
            <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
              <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[#FFD12D]" />
                Vendor Assignment
                <span className="text-gray-400 text-sm font-normal ml-2">(Optional)</span>
              </h3>
              <Select
                value={newStall.vendorId || "unassigned"}
                onValueChange={(value) => setNewStall({ ...newStall, vendorId: value === "unassigned" ? "" : value })}
              >
                <SelectTrigger className="dark:bg-[#1a1a1a] dark:text-white">
                  <SelectValue placeholder="-- Unassigned --" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">-- Unassigned --</SelectItem>
                  {allVendors.filter(v => v.status === 'approved' && v.role === 'vendor').map((vendor) => (
                    <SelectItem key={vendor._id} value={vendor._id}>
                      {vendor.name} ({vendor.email}){vendor.stall_id ? ' - Already has stall' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {allVendors.filter(v => v.status === 'approved' && v.role === 'vendor').length === 0 
                  ? 'No approved vendors available' 
                  : `${allVendors.filter(v => v.status === 'approved' && v.role === 'vendor').length} vendor(s) available`}
              </p>
            </div>

            {/* Stall Visibility */}
            <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
              <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#FFD12D]" />
                Stall Visibility
              </h3>
              <Select
                value={newStall.status || "draft"}
                onValueChange={(value) => setNewStall({ ...newStall, status: value })}
              >
                <SelectTrigger className="dark:bg-[#1a1a1a] dark:text-white">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                      Draft
                    </span>
                  </SelectItem>
                  <SelectItem value="published">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      Published
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {newStall.status === 'published' 
                  ? 'Stall will be visible to customers immediately' 
                  : 'Stall will be hidden from customers until published'}
              </p>
            </div>

            <Button 
              onClick={handleAddStall} 
              disabled={loading} 
              className="w-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-semibold py-6 transition-all duration-300"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Creating Stall...
                </>
              ) : (
                'Create Stall'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add User Modal */}
      <Dialog open={showAddUserModal} onOpenChange={setShowAddUserModal}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-[#1a1a1a] border-4 border-[#FFD12D]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#252525] dark:text-white flex items-center gap-2">
              <UserPlus className="w-6 h-6 text-[#FFD12D]" />
              Add New
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label>User Type <span className="text-red-500">*</span></Label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
              >
                <option value="vendor">Vendor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {newUser.role === 'vendor' && (
              <div>
                <Label>Vendor Type <span className="text-red-500">*</span></Label>
                <select
                  value={newUser.vendorType}
                  onChange={(e) => setNewUser({ ...newUser, vendorType: e.target.value })}
                  data-testid="add-user-vendor-type"
                  className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                >
                  <option value="market">Market Vendor</option>
                  <option value="expo">Expo Vendor</option>
                  <option value="festival">Festival Vendor</option>
                </select>
              </div>
            )}
            {newUser.role === 'vendor' && (
              <div>
                <Label>Subscription <span className="text-red-500">*</span></Label>
                <select
                  value={newUser.subscription}
                  onChange={(e) => setNewUser({ ...newUser, subscription: e.target.value })}
                  data-testid="add-user-subscription"
                  className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                >
                  <option value="cash">Cash</option>
                  <option value="manual">Manual</option>
                  <option value="trial">7 Day Trial</option>
                  <option value="trial_expired">7 Day Trial (Expired)</option>
                  <option value="monthly">Monthly (R25/month)</option>
                  <option value="yearly">Yearly (R300/year)</option>
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name <span className="text-red-500">*</span></Label>
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="First name"
                  className="dark:bg-[#252525] dark:text-white"
                />
              </div>
              <div>
                <Label>Surname <span className="text-red-500">*</span></Label>
                <Input
                  value={newUser.surname}
                  onChange={(e) => setNewUser({ ...newUser, surname: e.target.value })}
                  placeholder="Last name"
                  className="dark:bg-[#252525] dark:text-white"
                />
              </div>
            </div>
            {newUser.role === 'vendor' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Stall Number <span className="text-red-500">*</span></Label>
                    <Input
                      value={newUser.stallNumber}
                      onChange={(e) => setNewUser({ ...newUser, stallNumber: e.target.value })}
                      placeholder="e.g., A-15"
                      className="dark:bg-[#252525] dark:text-white"
                    />
                  </div>
                  <div>
                    <Label>Phone <span className="text-red-500">*</span></Label>
                    <Input
                      value={newUser.phone}
                      onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                      placeholder="+27 74 809 4378"
                      className="dark:bg-[#252525] dark:text-white"
                    />
                  </div>
                </div>
                <div>
                  <Label>Company Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={newUser.companyName}
                    onChange={(e) => setNewUser({ ...newUser, companyName: e.target.value })}
                    placeholder="Business/Company name"
                    className="dark:bg-[#252525] dark:text-white"
                  />
                </div>
              </>
            )}
            <div>
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="user@example.com"
                className="dark:bg-[#252525] dark:text-white"
              />
            </div>
            <div>
              <Label>Password <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Input
                  type={showNewUserPassword ? "text" : "password"}
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="••••••••"
                  className="dark:bg-[#252525] dark:text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewUserPassword(!showNewUserPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#FFD12D]"
                >
                  {showNewUserPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              onClick={handleAddUser}
              disabled={loading}
              className="w-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-bold"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      {showEditUserModal && editingUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl border-4 border-[#FFD12D]">
            {/* Header */}
            <div className="bg-[#FFD12D] p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#252525] rounded-full flex items-center justify-center">
                  <Edit2 className="w-6 h-6 text-[#FFD12D]" />
                </div>
                <h2 className="text-2xl font-bold text-[#252525]">
                  Edit User
                </h2>
              </div>
              <Button
                onClick={() => {
                  setShowEditUserModal(false);
                  setEditingUser(null);
                }}
                variant="ghost"
                className="text-[#252525] hover:bg-[#252525] hover:text-[#FFD12D] rounded-full p-2"
              >
                <X className="w-6 h-6" />
              </Button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
              {/* Account Information Section */}
              <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
                <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#FFD12D]" />
                  Account Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#252525] dark:text-white">Name <span className="text-red-500">*</span></Label>
                    <Input
                      value={editUserData.firstName}
                      onChange={(e) => setEditUserData({...editUserData, firstName: e.target.value})}
                      placeholder="First name"
                      className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#252525] dark:text-white">Surname <span className="text-red-500">*</span></Label>
                    <Input
                      value={editUserData.lastName}
                      onChange={(e) => setEditUserData({...editUserData, lastName: e.target.value})}
                      placeholder="Last name"
                      className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#252525] dark:text-white">Company Name</Label>
                    <Input
                      value={editUserData.companyName}
                      onChange={(e) => setEditUserData({...editUserData, companyName: e.target.value})}
                      placeholder="Company name"
                      className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#252525] dark:text-white">Email</Label>
                    <Input
                      value={editUserData.email}
                      onChange={(e) => setEditUserData({...editUserData, email: e.target.value})}
                      placeholder="email@example.com"
                      className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#252525] dark:text-white">Phone</Label>
                    <Input
                      value={editUserData.phone}
                      onChange={(e) => setEditUserData({...editUserData, phone: e.target.value})}
                      placeholder="+27-74-809-4378"
                      className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                    />
                  </div>
                </div>
              </div>

              {/* Account Section */}
              <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
                <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#FFD12D]" />
                  Account
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#252525] dark:text-white">Role</Label>
                    <Select
                      value={editUserData.role}
                      onValueChange={(value) => setEditUserData({...editUserData, role: value})}
                    >
                      <SelectTrigger className="dark:bg-[#1a1a1a] dark:text-white">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vendor">
                          <span className="flex items-center gap-2">
                            <Store className="w-4 h-4" />
                            Vendor
                          </span>
                        </SelectItem>
                        <SelectItem value="admin">
                          <span className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Admin
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {editUserData.role === 'vendor' && (
                    <div className="space-y-2">
                      <Label className="text-[#252525] dark:text-white">Vendor Type</Label>
                      <Select
                        value={editUserData.vendorType || 'market'}
                        onValueChange={(value) => setEditUserData({...editUserData, vendorType: value})}
                      >
                        <SelectTrigger data-testid="edit-user-vendor-type" className="dark:bg-[#1a1a1a] dark:text-white">
                          <SelectValue placeholder="Select vendor type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="market">
                            <span className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" /> Market Vendor
                            </span>
                          </SelectItem>
                          <SelectItem value="expo">
                            <span className="flex items-center gap-2">
                              <Tent className="w-4 h-4" /> Expo Vendor
                            </span>
                          </SelectItem>
                          <SelectItem value="festival">
                            <span className="flex items-center gap-2">
                              <PartyPopper className="w-4 h-4" /> Festival Vendor
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {editUserData.role === 'vendor' && editingUser && (
                    <div className="space-y-2 sm:col-span-2">
                      <Label className="text-[#252525] dark:text-white">Additional Type Access</Label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Grant this vendor access to add locations at additional vendor types. Their primary type ({editUserData.vendorType || 'market'}) cannot be revoked.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {['market', 'expo', 'festival'].map(t => {
                          const primary = editUserData.vendorType || 'market';
                          const currentTypes = editingUser.vendor_types && editingUser.vendor_types.length > 0
                            ? editingUser.vendor_types
                            : [primary];
                          const hasAccess = currentTypes.includes(t);
                          const isPrimary = t === primary;
                          return (
                            <button
                              key={t}
                              type="button"
                              disabled={isPrimary}
                              onClick={async () => {
                                if (hasAccess) {
                                  await handleAdminRevokeType(editingUser._id, t);
                                } else {
                                  await handleAdminGrantType(editingUser._id, t);
                                }
                                // Refetch the editing user's data optimistically
                                const updated = { ...editingUser };
                                if (hasAccess) {
                                  updated.vendor_types = currentTypes.filter(x => x !== t);
                                } else {
                                  updated.vendor_types = [...currentTypes, t];
                                }
                                setEditingUser(updated);
                              }}
                              data-testid={`toggle-access-${t}`}
                              className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-colors capitalize ${
                                isPrimary
                                  ? 'bg-[#FFD12D] text-[#252525] border-[#FFD12D] cursor-not-allowed opacity-80'
                                  : hasAccess
                                    ? 'bg-green-100 text-green-800 border-green-500 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-600'
                                    : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200 dark:bg-[#1a1a1a] dark:text-gray-400 dark:border-gray-700'
                              }`}
                            >
                              {isPrimary ? `${t}s (primary)` : hasAccess ? `✓ ${t}s` : `+ Grant ${t}s`}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-[#252525] dark:text-white">Account Status</Label>
                    <Select
                      value={editUserData.status}
                      onValueChange={(value) => setEditUserData({...editUserData, status: value})}
                    >
                      <SelectTrigger className="dark:bg-[#1a1a1a] dark:text-white">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approved">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                            Approved
                          </span>
                        </SelectItem>
                        <SelectItem value="pending">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                            Pending
                          </span>
                        </SelectItem>
                        <SelectItem value="declined">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-red-500"></span>
                            Declined
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* New Password Section */}
              <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
                <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-[#FFD12D]" />
                  New Password
                </h3>

                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white">Set a new password</Label>
                  <div className="relative">
                    <Input
                      type={showEditUserNewPassword ? "text" : "password"}
                      value={editUserData.newPassword}
                      onChange={(e) => setEditUserData({ ...editUserData, newPassword: e.target.value })}
                      placeholder="Leave blank to keep current password"
                      data-testid="edit-user-new-password"
                      className="dark:bg-[#1a1a1a] dark:text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowEditUserNewPassword(!showEditUserNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#FFD12D]"
                      data-testid="edit-user-new-password-toggle"
                    >
                      {showEditUserNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Minimum 6 characters. The user will be able to sign in immediately with the new password.
                  </p>
                </div>
              </div>

              {/* Subscription Section */}
              <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
                <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#FFD12D]" />
                  Subscription
                </h3>
                
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white">Subscription Plan</Label>
                  <Select
                    value={editUserData.subscription}
                    onValueChange={(value) => setEditUserData({...editUserData, subscription: value})}
                  >
                    <SelectTrigger className="dark:bg-[#1a1a1a] dark:text-white">
                      <SelectValue placeholder="Select subscription" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-gray-500"></span>
                          Cash
                        </span>
                      </SelectItem>
                      <SelectItem value="manual">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                          Manual
                        </span>
                      </SelectItem>
                      <SelectItem value="trial">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                          7 Day Trial
                        </span>
                      </SelectItem>
                      <SelectItem value="trial_expired">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-red-500"></span>
                          7 Day Trial (Expired)
                        </span>
                      </SelectItem>
                      <SelectItem value="monthly">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                          Monthly (R25/month)
                        </span>
                      </SelectItem>
                      <SelectItem value="yearly">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                          Yearly (R300/year)
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {(editUserData.subscription === 'cash') && 'Cash subscription - stall will remain active'}
                    {editUserData.subscription === 'manual' && 'Manual subscription — admin-controlled, no auto-billing'}
                    {editUserData.subscription === 'trial' && 'User is on a 7-day free trial'}
                    {editUserData.subscription === 'trial_expired' && 'Trial has expired - stall set to draft, user needs to subscribe'}
                    {editUserData.subscription === 'monthly' && 'User is billed R25 monthly via PayFast'}
                    {editUserData.subscription === 'yearly' && 'User is billed R300 annually via PayFast'}
                  </p>

                  {/* Payment status override (Netflix model — vendor can still log in and update payment) */}
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="suspend-toggle-label">
                      <input
                        type="checkbox"
                        checked={editUserData.status === 'suspended'}
                        onChange={(e) => setEditUserData({
                          ...editUserData,
                          status: e.target.checked ? 'suspended' : 'approved'
                        })}
                        className="mt-1 w-5 h-5 rounded border-2 border-red-500 text-red-600 focus:ring-red-500 accent-red-600 cursor-pointer flex-shrink-0"
                        data-testid="suspend-vendor-checkbox"
                      />
                      <div className="min-w-0">
                        <p className="font-semibold text-red-700 dark:text-red-400 text-sm">Mark payment as failed</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Tick this if the vendor's PayFast payment failed. Their stall is hidden from the public (draft), but the vendor can still log in and update their payment. Uncheck once payment succeeds.
                        </p>
                        {editUserData.status === 'suspended' && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-semibold">
                            ⚠ On save: stall → Draft. Vendor can still log in to retry payment.
                          </p>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="border-t border-gray-200 dark:border-gray-700 p-6 flex justify-end gap-3">
              <Button
                onClick={() => {
                  setShowEditUserModal(false);
                  setEditingUser(null);
                }}
                variant="outline"
                className="border-gray-300 dark:border-gray-600 dark:text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEditUser}
                disabled={savingEditUser}
                className="bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] font-semibold"
              >
                {savingEditUser ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stall Edit Modal - Shows vendor dashboard with location editing */}
      {showEditModal && editingStallData && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-[#FFD12D] p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#252525] rounded-full flex items-center justify-center">
                  <Edit2 className="w-6 h-6 text-[#FFD12D]" />
                </div>
                <h2 className="text-2xl font-bold text-[#252525]">
                  Edit Stall: {editingStallData.stallName}
                </h2>
              </div>
              <Button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingStallData(null);
                  setEditStallFiles({ logo: null, banner: null });
                  setEditStallSelectedTags([]);
                }}
                variant="ghost"
                className="w-10 h-10 p-0 rounded-full bg-[#252525] hover:bg-white text-[#FFD12D] hover:text-[#252525]"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
              {/* Images - Now at the top */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <Image className="w-4 h-4 text-[#FFD12D]" />
                    Logo
                  </Label>
                  {editingStallData.logo && !editStallFiles.logo && (
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-[#FFD12D] mb-2 group">
                      <img src={getThumbnailUrl(editingStallData.logo)} alt="Current logo" className="w-full h-full object-cover" loading="lazy" />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-1 right-1 w-6 h-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={async () => {
                          if (window.confirm('Delete this logo?')) {
                            try {
                              const res = await fetch(`${API_URL}/api/admin/stalls/${editingStallData._id}/delete-logo`, {
                                method: 'DELETE',
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              if (res.ok) {
                                setEditingStallData({...editingStallData, logo: null});
                                setMessage({ type: 'success', text: 'Logo deleted' });
                              }
                            } catch (e) {
                              setMessage({ type: 'error', text: 'Failed to delete logo' });
                            }
                          }
                        }}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                  {editStallFiles.logo && (
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-green-500 mb-2">
                      <img src={URL.createObjectURL(editStallFiles.logo)} alt="New logo" className="w-full h-full object-cover" />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-1 right-1 w-6 h-6 p-0"
                        onClick={() => setEditStallFiles({...editStallFiles, logo: null})}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                  <Input
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp"
                    onChange={(e) => setEditStallFiles({...editStallFiles, logo: e.target.files[0]})}
                    className="dark:bg-[#252525] dark:text-white"
                  />
                  <p className="text-xs text-gray-500">Leave empty to keep current logo</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <FileImage className="w-4 h-4 text-[#FFD12D]" />
                    Banner
                  </Label>
                  {editingStallData.banner && !editStallFiles.banner && (
                    <div className="relative w-full h-24 rounded-lg overflow-hidden border-2 border-[#FFD12D] mb-2 group">
                      <img src={getThumbnailUrl(editingStallData.banner)} alt="Current banner" className="w-full h-full object-cover" loading="lazy" />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-1 right-1 w-6 h-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={async () => {
                          if (window.confirm('Delete this banner?')) {
                            try {
                              const res = await fetch(`${API_URL}/api/admin/stalls/${editingStallData._id}/delete-banner`, {
                                method: 'DELETE',
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              if (res.ok) {
                                setEditingStallData({...editingStallData, banner: null});
                                setMessage({ type: 'success', text: 'Banner deleted' });
                              }
                            } catch (e) {
                              setMessage({ type: 'error', text: 'Failed to delete banner' });
                            }
                          }
                        }}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                  {editStallFiles.banner && (
                    <div className="relative w-full h-24 rounded-lg overflow-hidden border-2 border-green-500 mb-2">
                      <img src={URL.createObjectURL(editStallFiles.banner)} alt="New banner" className="w-full h-full object-cover" />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-1 right-1 w-6 h-6 p-0"
                        onClick={() => setEditStallFiles({...editStallFiles, banner: null})}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                  <Input
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp"
                    onChange={(e) => setEditStallFiles({...editStallFiles, banner: e.target.files[0]})}
                    className="dark:bg-[#252525] dark:text-white"
                  />
                  <p className="text-xs text-gray-500">Leave empty to keep current banner</p>
                </div>
              </div>

              {/* Gallery Media */}
              <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="font-semibold text-[#252525] dark:text-white flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-[#FFD12D]" />
                  Gallery Media
                </h4>
                <p className="text-xs text-gray-500">Up to 10 photos, max 2MB each (PNG, JPG, JPEG, WEBP)</p>

                {editingStallData.gallery && editingStallData.gallery.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {editingStallData.gallery.map((img, idx) => (
                      <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                        <img src={getThumbnailUrl(img)} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                        <div className="absolute top-0.5 left-0.5 bg-black/60 text-white text-[10px] px-1 rounded">{idx + 1}</div>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm('Delete this gallery image?')) return;
                            const res = await fetch(`${API_URL}/api/admin/stalls/${editingStallData._id}/gallery/${idx}`, {
                              method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
                            });
                            if (res.ok) {
                              const data = await res.json();
                              setEditingStallData({...editingStallData, gallery: data.gallery});
                              setMessage({ type: 'success', text: 'Gallery image deleted' });
                            }
                          }}
                          className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`admin-gallery-delete-${idx}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {(!editingStallData.gallery || editingStallData.gallery.length < 10) && (
                  <div className="flex items-center gap-3">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        className="hidden"
                        data-testid="admin-gallery-upload-input"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          if (file.size > 2 * 1024 * 1024) {
                            setMessage({ type: 'error', text: 'File size must be less than 2MB' });
                            return;
                          }
                          const formData = new FormData();
                          formData.append('file', file);
                          const res = await fetch(`${API_URL}/api/admin/stalls/${editingStallData._id}/upload-gallery`, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${token}` },
                            body: formData,
                          });
                          if (res.ok) {
                            const data = await res.json();
                            setEditingStallData({...editingStallData, gallery: data.gallery});
                            setMessage({ type: 'success', text: 'Gallery image uploaded' });
                          } else {
                            const err = await res.json().catch(() => ({}));
                            setMessage({ type: 'error', text: err.detail || 'Failed to upload' });
                          }
                          e.target.value = '';
                        }}
                      />
                      <Button type="button" asChild className="h-9 bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] font-semibold text-sm">
                        <span><Upload className="w-3.5 h-3.5 mr-1.5" /> Add Photo</span>
                      </Button>
                    </label>
                    <span className="text-xs text-gray-500">{editingStallData.gallery?.length || 0}/10</span>
                  </div>
                )}
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <Store className="w-4 h-4 text-[#FFD12D]" />
                    Stall Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={editingStallData.stallName}
                    onChange={(e) => setEditingStallData({...editingStallData, stallName: e.target.value})}
                    placeholder="Artisan Crafts Co"
                    className="dark:bg-[#252525] dark:text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <FolderTree className="w-4 h-4 text-[#FFD12D]" />
                    Category <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={editingStallData.category}
                    onValueChange={(value) => setEditingStallData({...editingStallData, category: value})}
                  >
                    <SelectTrigger className="dark:bg-[#252525] dark:text-white">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <Eye className="w-4 h-4 text-[#FFD12D]" />
                    Stall Visibility <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={editingStallData.status || 'draft'}
                    onValueChange={(value) => setEditingStallData({...editingStallData, status: value})}
                  >
                    <SelectTrigger className="dark:bg-[#252525] dark:text-white">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                          Draft (Hidden)
                        </span>
                      </SelectItem>
                      <SelectItem value="published">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                          Published (Visible)
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tags - before Slogan */}
              <div className="space-y-2">
                <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                  <Tag className="w-4 h-4 text-[#FFD12D]" />
                  Tags (Select up to 10)
                </Label>
                <CheckboxListWithSearch
                  items={tags}
                  selected={editStallSelectedTags}
                  onToggle={handleEditStallTagToggle}
                  maxSelected={10}
                  Icon={Tag}
                  testId="admin-edit-stall-tags"
                  searchPlaceholder="Search tags..."
                  emptyText="No tags yet"
                />
                <p className="text-xs text-gray-500">{editStallSelectedTags.length}/10 tags selected</p>
              </div>

              {/* Slogan & About */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#FFD12D]" />
                    Slogan
                  </Label>
                  <Input
                    value={editingStallData.slogan || ''}
                    onChange={(e) => setEditingStallData({...editingStallData, slogan: e.target.value})}
                    placeholder="Your catchy slogan here"
                    className="dark:bg-[#252525] dark:text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#252525] dark:text-white">About</Label>
                  <Textarea
                    value={editingStallData.about || ''}
                    onChange={(e) => setEditingStallData({...editingStallData, about: e.target.value})}
                    placeholder="Tell customers about your stall..."
                    className="dark:bg-[#252525] dark:text-white min-h-[100px]"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-gray-50 dark:bg-[#252525] p-4 rounded-xl space-y-4">
                <h3 className="font-bold text-[#252525] dark:text-white flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#FFD12D]" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#252525] dark:text-white">Phone</Label>
                    <Input
                      value={editingStallData.phone || ''}
                      onChange={(e) => setEditingStallData({...editingStallData, phone: e.target.value})}
                      placeholder="+27 12 345 6789"
                      className="dark:bg-[#1a1a1a] dark:text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#252525] dark:text-white">WhatsApp</Label>
                    <Input
                      value={editingStallData.whatsapp || ''}
                      onChange={(e) => setEditingStallData({...editingStallData, whatsapp: e.target.value})}
                      placeholder="+27 12 345 6789"
                      className="dark:bg-[#1a1a1a] dark:text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#252525] dark:text-white">Email</Label>
                    <Input
                      type="email"
                      value={editingStallData.email || ''}
                      onChange={(e) => setEditingStallData({...editingStallData, email: e.target.value})}
                      placeholder="stall@example.com"
                      className="dark:bg-[#1a1a1a] dark:text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#252525] dark:text-white">Website</Label>
                    <Input
                      value={editingStallData.website || ''}
                      onChange={(e) => setEditingStallData({...editingStallData, website: e.target.value})}
                      placeholder="https://www.example.com"
                      className="dark:bg-[#1a1a1a] dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-gray-50 dark:bg-[#252525] p-4 rounded-xl space-y-4">
                <h3 className="font-bold text-[#252525] dark:text-white flex items-center gap-2">
                  <Globe className="w-4 h-4 text-[#FFD12D]" />
                  Social Media Links
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    value={editingStallData.socialMedia?.facebook || ''}
                    onChange={(e) => setEditingStallData({...editingStallData, socialMedia: {...editingStallData.socialMedia, facebook: e.target.value}})}
                    placeholder="Facebook URL"
                    className="dark:bg-[#1a1a1a] dark:text-white"
                  />
                  <Input
                    value={editingStallData.socialMedia?.instagram || ''}
                    onChange={(e) => setEditingStallData({...editingStallData, socialMedia: {...editingStallData.socialMedia, instagram: e.target.value}})}
                    placeholder="Instagram URL"
                    className="dark:bg-[#1a1a1a] dark:text-white"
                  />
                  <Input
                    value={editingStallData.socialMedia?.tiktok || ''}
                    onChange={(e) => setEditingStallData({...editingStallData, socialMedia: {...editingStallData.socialMedia, tiktok: e.target.value}})}
                    placeholder="TikTok URL"
                    className="dark:bg-[#1a1a1a] dark:text-white"
                  />
                  <Input
                    value={editingStallData.socialMedia?.twitter || ''}
                    onChange={(e) => setEditingStallData({...editingStallData, socialMedia: {...editingStallData.socialMedia, twitter: e.target.value}})}
                    placeholder="X (Twitter) URL"
                    className="dark:bg-[#1a1a1a] dark:text-white"
                  />
                </div>
              </div>

              {/* Markets & Locations — tabbed by vendor type */}
              <div className="bg-gray-50 dark:bg-[#252525] p-4 rounded-xl space-y-4">
                <h3 className="font-bold text-[#252525] dark:text-white flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#FFD12D]" />
                  {editStallCtx.label}
                </h3>

                {/* Tab bar */}
                <div className="flex flex-wrap gap-2 border-b-2 border-gray-200 dark:border-gray-700 pb-3" data-testid="admin-edit-stall-location-tabs">
                  {['market', 'expo', 'festival'].map(t => {
                    const isActive = adminEditStallTab === t;
                    const meta = TYPE_META[t];
                    const hasAccess = editStallCtx.grantedTypes.includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setAdminEditStallTab(t)}
                        data-testid={`admin-edit-stall-tab-${t}`}
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-2 ${
                          isActive
                            ? 'bg-[#FFD12D] text-[#252525] shadow-md'
                            : hasAccess
                              ? 'bg-white dark:bg-[#1a1a1a] text-[#252525] dark:text-white hover:bg-gray-100 dark:hover:bg-[#252525]'
                              : 'bg-white dark:bg-[#1a1a1a] text-gray-400 dark:text-gray-500'
                        }`}
                      >
                        <MapPin className={`w-4 h-4 ${isActive ? 'text-[#252525]' : hasAccess ? 'text-[#FFD12D]' : 'text-gray-400'}`} />
                        {meta.plural}
                        {!hasAccess && <span className="text-[10px] uppercase tracking-wider">Locked</span>}
                      </button>
                    );
                  })}
                </div>

                {editStallCtx.hasAccess ? (
                  <>
                    {/* Market Selection — checkbox list matching the Vendor dashboard style */}
                    <div className="space-y-3">
                      <Label className="text-[#252525] dark:text-white font-semibold">
                        {editStallCtx.selectLabel}
                      </Label>
                      <CheckboxListWithSearch
                        items={editStallCtx.items}
                        selected={Object.keys(editStallMarketLocations).filter(name => editStallCtx.items.includes(name))}
                        onToggle={(market) => {
                          const currentMarkets = Object.keys(editStallMarketLocations);
                          if (currentMarkets.includes(market)) {
                            const newLocations = { ...editStallMarketLocations };
                            delete newLocations[market];
                            setEditStallMarketLocations(newLocations);
                          } else {
                            setEditStallMarketLocations({
                              ...editStallMarketLocations,
                              [market]: { stallNumber: '', lat: '', lng: '' }
                            });
                          }
                        }}
                        Icon={MapPin}
                        testId={`admin-edit-stall-markets-${adminEditStallTab}`}
                        searchPlaceholder={editStallCtx.searchPlaceholder}
                        emptyText={editStallCtx.emptyText}
                      />
                    </div>

                    {/* Location Details for Each Selected Market (current tab only) */}
                    {Object.keys(editStallMarketLocations).filter(name => editStallCtx.items.includes(name)).length > 0 && (
                      <div className="space-y-4 mt-4 border-t-2 border-[#FFD12D] pt-4">
                        <h4 className="font-semibold text-[#252525] dark:text-white">
                          Location Details for Each {editStallCtx.singular}:
                        </h4>

                        {Object.keys(editStallMarketLocations).filter(name => editStallCtx.items.includes(name)).map((market) => (
                      <div key={market} className="bg-white dark:bg-[#1a1a1a] p-4 rounded-lg border-2 border-[#FFD12D]/30 space-y-3">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#FFD12D]" />
                          <h5 className="font-bold text-[#252525] dark:text-white">{market}</h5>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                            <Hash className="w-4 h-4 text-[#FFD12D]" />
                            Stall Number
                          </Label>
                          <Input
                            value={editStallMarketLocations[market]?.stallNumber || ''}
                            onChange={(e) => setEditStallMarketLocations({
                              ...editStallMarketLocations,
                              [market]: { ...editStallMarketLocations[market], stallNumber: e.target.value }
                            })}
                            placeholder="e.g., A-12"
                            className="dark:bg-[#252525] dark:text-white"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-[#252525] dark:text-white">Latitude</Label>
                            <Input
                              type="number"
                              step="0.000001"
                              value={editStallMarketLocations[market]?.lat || ''}
                              onChange={(e) => setEditStallMarketLocations({
                                ...editStallMarketLocations,
                                [market]: { ...editStallMarketLocations[market], lat: e.target.value }
                              })}
                              placeholder="-25.7461"
                              className="dark:bg-[#252525] dark:text-white"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#252525] dark:text-white">Longitude</Label>
                            <Input
                              type="number"
                              step="0.000001"
                              value={editStallMarketLocations[market]?.lng || ''}
                              onChange={(e) => setEditStallMarketLocations({
                                ...editStallMarketLocations,
                                [market]: { ...editStallMarketLocations[market], lng: e.target.value }
                              })}
                              placeholder="28.1881"
                              className="dark:bg-[#252525] dark:text-white"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                  </>
                ) : (
                  <NoTypeAccessPanel
                    vendor={editStallCtx.vendor}
                    typeKey={adminEditStallTab}
                    onGrant={handleAdminGrantType}
                  />
                )}
              </div>

              {/* Vendor Assignment */}
              <div className="bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-300 dark:border-purple-700 p-4 rounded-xl space-y-4">
                <h3 className="font-bold text-[#252525] dark:text-white flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-600" />
                  Vendor Assignment
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Assign a vendor to manage this stall. Only one vendor can be assigned per stall.
                  Leave unassigned for admin-managed stalls.
                </p>
                <Select
                  value={editStallVendorId || "unassigned"}
                  onValueChange={(value) => setEditStallVendorId(value === "unassigned" ? null : value)}
                >
                  <SelectTrigger className="dark:bg-[#1a1a1a] dark:text-white">
                    <SelectValue placeholder="-- Unassigned --" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                        -- Unassigned --
                      </span>
                    </SelectItem>
                    {allVendors
                      .filter(v => v.status === 'approved' && v.role === 'vendor')
                      .map((vendor) => (
                        <SelectItem key={vendor._id} value={vendor._id}>
                          <span className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${
                              vendor.stall_id && vendor.stall_id !== editingStallData?._id
                                ? 'bg-orange-500' 
                                : vendor._id === editStallVendorId
                                  ? 'bg-green-500'
                                  : 'bg-blue-500'
                            }`}></span>
                            {vendor.name} ({vendor.email})
                            {vendor.stall_id && vendor.stall_id !== editingStallData?._id && ' ⚠️ Has another stall'}
                          </span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {editStallVendorId && (
                  <div className="flex items-center gap-2 p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <span className="text-green-700 dark:text-green-300 text-sm">
                      ✓ Assigned to: {allVendors.find(v => v._id === editStallVendorId)?.name || 'Unknown'}
                    </span>
                  </div>
                )}
                {!editStallVendorId && (
                  <div className="flex items-center gap-2 p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">
                      ⚠ Unassigned - Admin has full control over this stall
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 bg-gray-50 dark:bg-[#252525] border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <Button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingStallData(null);
                  setEditStallFiles({ logo: null, banner: null });
                  setEditStallSelectedTags([]);
                  setEditStallVendorId(null);
                }}
                variant="outline"
                className="border-gray-300 dark:border-gray-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEditStall}
                disabled={savingEditStall}
                className="bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-bold"
              >
                {savingEditStall ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Vendors Modal */}
      {showBulkUploadVendors && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-[#252525] dark:text-white">Bulk Upload Vendors</h3>
              <button
                onClick={() => {
                  setShowBulkUploadVendors(false);
                  resetBulkUpload();
                }}
                className="text-gray-500 hover:text-[#FFD12D]"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {uploadStep === 1 && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                  <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">Upload CSV file with vendor data</p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[#FFD12D] file:text-[#252525] hover:file:bg-[#252525] hover:file:text-[#FFD12D] file:cursor-pointer"
                  />
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">CSV Format:</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300">Required columns: Name, Email, Phone, Role (vendor/admin), Stall Number (optional)</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">Example: John Doe,john@example.com,+27123456789,vendor,A-101</p>
                </div>
              </div>
            )}

            {uploadStep === 2 && (
              <div className="space-y-4">
                <h4 className="font-semibold text-[#252525] dark:text-white">Map CSV Columns to Fields</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Found {csvData.length} rows</p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name *</Label>
                    <select
                      value={fieldMapping.firstName || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, firstName: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Surname / Last Name</Label>
                    <select
                      value={fieldMapping.lastName || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, lastName: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Not Used --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Stall Number</Label>
                    <select
                      value={fieldMapping.stall_number || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, stall_number: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Company Name (Stall Name)</Label>
                    <select
                      value={fieldMapping.companyName || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, companyName: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Not Used (will use personal name) --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">If not mapped, First Name + Surname will be used as company name</p>
                  </div>

                  <div>
                    <Label>Phone Number</Label>
                    <select
                      value={fieldMapping.phone || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, phone: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Email *</Label>
                    <select
                      value={fieldMapping.email || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, email: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>User Type (Role)</Label>
                    <select
                      value={fieldMapping.role || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, role: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Default: Vendor --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Vendor Type</Label>
                    <select
                      value={fieldMapping.vendorType || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, vendorType: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Default: Market --</option>
                      <option value="__market">Set All: Market Vendor</option>
                      <option value="__expo">Set All: Expo Vendor</option>
                      <option value="__festival">Set All: Festival Vendor</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Password (defaults to DefaultPassword123)</Label>
                    <select
                      value={fieldMapping.password || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, password: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Use Default --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Status</Label>
                    <select
                      value={fieldMapping.status || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, status: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Default: Approved --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Subscription</Label>
                    <select
                      value={fieldMapping.subscription || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, subscription: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                      data-testid="bulk-upload-subscription-select"
                    >
                      <option value="">-- Default: Cash --</option>
                      <option value="__yearly">-- Set All: Yearly --</option>
                      <option value="__monthly">-- Set All: Monthly --</option>
                      <option value="__cash">-- Set All: Cash --</option>
                      <option value="__manual">-- Set All: Manual --</option>
                      <option value="__trial">-- Set All: Trial --</option>
                      {csvHeaders.length > 0 && <option disabled>──── Map from CSV ────</option>}
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Pick a set-all default OR map a CSV column with values: yearly, monthly, cash, manual, trial</p>
                  </div>
                </div>

                <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg max-h-48 overflow-auto">
                  <p className="text-sm font-semibold mb-2">Preview (First 3 rows):</p>
                  {csvData.slice(0, 3).map((row, idx) => {
                    const firstName = row[fieldMapping.firstName] || '';
                    const lastName = row[fieldMapping.lastName] || '';
                    const personalName = [firstName, lastName].filter(Boolean).join(' ').trim();
                    const companyName = row[fieldMapping.companyName] || '';
                    const displayName = companyName || personalName;
                    return (
                      <div key={idx} className="text-xs mb-2 p-2 bg-white dark:bg-gray-700 rounded">
                        <p><strong>Name:</strong> {firstName} {lastName}</p>
                        <p><strong>Company/Stall Name:</strong> {displayName || 'Not mapped'}</p>
                        <p><strong>Email:</strong> {row[fieldMapping.email] || 'Not mapped'}</p>
                        <p><strong>Phone:</strong> {row[fieldMapping.phone] || 'Not mapped'}</p>
                        <p><strong>Stall #:</strong> {row[fieldMapping.stall_number] || 'Not mapped'}</p>
                        <p><strong>Status:</strong> {row[fieldMapping.status] || 'Approved (default)'}</p>
                        <p><strong>Vendor Type:</strong> {
                          fieldMapping.vendorType?.startsWith('__') 
                            ? fieldMapping.vendorType.replace('__', '') 
                            : (row[fieldMapping.vendorType] || 'market (default)')
                        }</p>
                      </div>
                    );
                  })}
                </div>

                {/* Progress indicator during upload */}
                {processing === 'bulk-upload' && uploadProgress.total > 0 && (
                  <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                        {uploadProgress.status}
                      </span>
                      <span className="text-sm text-blue-600 dark:text-blue-300">
                        {uploadProgress.current} / {uploadProgress.total}
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-3">
                      <div 
                        className="bg-[#FFD12D] h-3 rounded-full transition-all duration-300"
                        style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 text-center">
                      {Math.round((uploadProgress.current / uploadProgress.total) * 100)}% complete
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    onClick={() => setUploadStep(1)}
                    variant="outline"
                    disabled={processing === 'bulk-upload'}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleBulkUploadVendors}
                    disabled={!fieldMapping.firstName || !fieldMapping.email || processing === 'bulk-upload'}
                    className="flex-1 bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D]"
                  >
                    {processing === 'bulk-upload' ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading {uploadProgress.current}/{uploadProgress.total}...
                      </>
                    ) : (
                      <>Upload {csvData.length} Vendors</>
                    )}
                  </Button>
                </div>

                {/* Inline error list — shown after upload if any rows failed */}
                {uploadErrors.length > 0 && (
                  <BulkUploadErrorsPanel errors={uploadErrors} fileNameHint="failed-vendors" />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Upload Stalls Modal */}
      {/* PayFast Subscriptions Sync Modal */}
      {showPayFastSyncModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" data-testid="payfast-sync-modal">
          <div className="bg-white dark:bg-[#1a1a1a] w-full max-w-2xl max-h-[90vh] rounded-2xl border-2 border-[#FFD12D] flex flex-col overflow-hidden shadow-2xl">
            <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-[#FFD12D]" />
                  Sync Subscriptions from PayFast
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Paste the PayFast Subscriptions Report — CSV, TSV, or JSON. Active rows publish stalls; Stalled / Cancelled / Retry rows suspend accounts and draft stalls. Cash and manual vendors are left untouched.
                </p>
              </div>
              <button
                onClick={() => { setShowPayFastSyncModal(false); setPayFastSyncText(''); setPayFastSyncResult(null); setPayFastLiveSyncResult(null); }}
                className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center flex-shrink-0"
                data-testid="close-payfast-sync-modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* ── Primary: live PayFast API sync (no paste) ─────────── */}
              <div className="rounded-xl border-2 border-purple-500 bg-purple-50 dark:bg-purple-950/30 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-[#252525] dark:text-white">One-Click Live Sync <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-500 text-white ml-1">RECOMMENDED</span></p>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 mb-3">
                      Fetches every vendor's current subscription status directly from PayFast&rsquo;s API — no CSV, no paste, no pasteboard errors. Uses the <code className="text-[10px] font-mono">subscription_token</code> stored during their original checkout.
                    </p>
                    <Button
                      onClick={handlePayFastLiveSync}
                      disabled={payFastLiveSyncing}
                      className="rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                      data-testid="payfast-live-sync-btn"
                    >
                      {payFastLiveSyncing ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing from PayFast…</>
                      ) : (
                        <><Zap className="w-4 h-4 mr-2" />Sync live from PayFast API</>
                      )}
                    </Button>
                    {payFastLiveSyncResult && (
                      <div className="mt-3 bg-white dark:bg-[#1a1a1a] rounded-lg p-3 text-xs space-y-1">
                        <p className="font-semibold text-[#252525] dark:text-white">Live sync complete — checked {payFastLiveSyncResult.checked} vendors</p>
                        <p className="text-gray-700 dark:text-gray-300">Suspended: <strong>{payFastLiveSyncResult.suspended}</strong> · Published: <strong>{payFastLiveSyncResult.published}</strong> · Unchanged: <strong>{payFastLiveSyncResult.unchanged}</strong></p>
                        {payFastLiveSyncResult.api_errors?.length > 0 && (
                          <p className="text-orange-600 dark:text-orange-400">API errors: <strong>{payFastLiveSyncResult.api_errors.length}</strong> — first: {payFastLiveSyncResult.api_errors[0]?.email} → {payFastLiveSyncResult.api_errors[0]?.error?.slice(0, 80)}</p>
                        )}
                        {payFastLiveSyncResult.skipped_manual_or_cash?.length > 0 && (
                          <p className="text-gray-500">Skipped Cash/Manual: {payFastLiveSyncResult.skipped_manual_or_cash.length}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
                <span className="text-xs uppercase tracking-wide text-gray-400">or paste a CSV as a fallback</span>
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
              </div>

              <p className="text-xs text-gray-500">
                Expected columns: <strong>Email</strong>, <strong>Status</strong> (Active/Stalled/Cancelled/Retry/Failed/Paused), optional <strong>Frequency</strong> (Monthly/Annually).
              </p>
              <textarea
                value={payFastSyncText}
                onChange={(e) => setPayFastSyncText(e.target.value)}
                placeholder={"email,status,frequency\nvendor@example.com,Active,Monthly\nother@example.com,Stalled,Monthly"}
                rows={10}
                className="w-full p-3 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#252525] text-xs font-mono text-[#252525] dark:text-white"
                data-testid="payfast-sync-textarea"
              />
              {payFastSyncResult && (
                <div className="bg-green-50 dark:bg-green-950/30 border border-green-500 rounded-lg p-3 text-sm space-y-1">
                  <p className="font-semibold text-green-800 dark:text-green-300">Sync complete</p>
                  <p className="text-green-700 dark:text-green-400">Suspended: <strong>{payFastSyncResult.suspended}</strong> · Published: <strong>{payFastSyncResult.published}</strong> · Unchanged: <strong>{payFastSyncResult.unchanged}</strong></p>
                  {payFastSyncResult.unknown_emails?.length > 0 && (
                    <p className="text-orange-700 dark:text-orange-400 text-xs">Unknown emails (no WAYO account): {payFastSyncResult.unknown_emails.join(', ')}</p>
                  )}
                  {payFastSyncResult.skipped_manual_or_cash?.length > 0 && (
                    <p className="text-gray-600 text-xs">Skipped (Cash/Manual): {payFastSyncResult.skipped_manual_or_cash.length} vendors</p>
                  )}
                </div>
              )}
            </div>
            <div className="p-5 border-t border-gray-200 dark:border-gray-700 flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => { setShowPayFastSyncModal(false); setPayFastSyncText(''); setPayFastSyncResult(null); setPayFastLiveSyncResult(null); }}
                className="rounded-full"
              >
                Close
              </Button>
              <Button
                onClick={handlePayFastSync}
                disabled={payFastSyncing || !payFastSyncText.trim()}
                className="rounded-full bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] font-semibold"
                data-testid="payfast-sync-submit"
              >
                {payFastSyncing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing…</> : 'Run Sync'}
              </Button>
            </div>
          </div>
        </div>
      )}


      {showBulkUploadStalls && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-[#252525] dark:text-white">Bulk Upload Stalls</h3>
              <button
                onClick={() => {
                  setShowBulkUploadStalls(false);
                  resetBulkUpload();
                }}
                className="text-gray-500 hover:text-[#FFD12D]"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {uploadStep === 1 && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                  <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">Upload CSV file with stall data</p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[#FFD12D] file:text-[#252525] hover:file:bg-[#252525] hover:file:text-[#FFD12D] file:cursor-pointer"
                  />
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">CSV Format:</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300"><strong>Required:</strong> Stall Name, Stall Number</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300"><strong>Basic Info:</strong> Category, Market, Slogan, About</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300"><strong>Contact:</strong> Phone, WhatsApp, Email, Website</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300"><strong>Social Media:</strong> Facebook, Instagram, TikTok, Twitter, LinkedIn</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300"><strong>Other:</strong> Tags (semicolon-separated), Latitude, Longitude, Address, Vendor Email, Status (draft/published)</p>
                </div>
              </div>
            )}

            {uploadStep === 2 && (
              <div className="space-y-4">
                <h4 className="font-semibold text-[#252525] dark:text-white">Map CSV Columns to Fields</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Found {csvData.length} rows</p>
                
                <div className="grid grid-cols-2 gap-4 max-h-96 overflow-y-auto p-2">
                  <div>
                    <Label>Stall Name *</Label>
                    <select
                      value={fieldMapping.stallName || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, stallName: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Stall Number *</Label>
                    <select
                      value={fieldMapping.stallNumber || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, stallNumber: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Market / Expos / Festivals</Label>
                    <select
                      value={fieldMapping.market || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, market: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Category</Label>
                    <select
                      value={fieldMapping.category || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, category: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Slogan</Label>
                    <select
                      value={fieldMapping.slogan || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, slogan: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>About</Label>
                    <select
                      value={fieldMapping.about || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, about: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Phone</Label>
                    <select
                      value={fieldMapping.phone || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, phone: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>WhatsApp</Label>
                    <select
                      value={fieldMapping.whatsapp || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, whatsapp: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Email</Label>
                    <select
                      value={fieldMapping.email || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, email: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Website</Label>
                    <select
                      value={fieldMapping.website || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, website: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Tags (semicolon-separated)</Label>
                    <select
                      value={fieldMapping.tags || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, tags: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Latitude</Label>
                    <select
                      value={fieldMapping.latitude || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, latitude: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Longitude</Label>
                    <select
                      value={fieldMapping.longitude || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, longitude: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Address</Label>
                    <select
                      value={fieldMapping.address || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, address: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Vendor Email</Label>
                    <select
                      value={fieldMapping.vendorId || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, vendorId: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Status (draft/published)</Label>
                    <select
                      value={fieldMapping.status || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, status: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div className="col-span-2 border-t border-gray-200 dark:border-gray-700 pt-4 mt-2">
                    <p className="text-sm font-semibold text-[#252525] dark:text-white mb-2">Social Media</p>
                  </div>

                  <div>
                    <Label>Facebook URL</Label>
                    <select
                      value={fieldMapping.facebook || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, facebook: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Instagram URL</Label>
                    <select
                      value={fieldMapping.instagram || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, instagram: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>TikTok URL</Label>
                    <select
                      value={fieldMapping.tiktok || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, tiktok: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>Twitter URL</Label>
                    <select
                      value={fieldMapping.twitter || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, twitter: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label>LinkedIn URL</Label>
                    <select
                      value={fieldMapping.linkedin || ''}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, linkedin: e.target.value })}
                      className="w-full p-2 border rounded dark:bg-[#252525] dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                </div>

                <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg max-h-48 overflow-auto">
                  <p className="text-sm font-semibold mb-2">Preview (First 2 rows):</p>
                  {csvData.slice(0, 2).map((row, idx) => (
                    <div key={idx} className="text-xs mb-2 p-2 bg-white dark:bg-gray-700 rounded">
                      <p><strong>Stall:</strong> {row[fieldMapping.stallName] || 'Not mapped'} - {row[fieldMapping.stallNumber] || 'Not mapped'}</p>
                      <p><strong>Category:</strong> {row[fieldMapping.category] || 'Not mapped'}</p>
                      <p><strong>Location:</strong> {row[fieldMapping.latitude]}, {row[fieldMapping.longitude]}</p>
                    </div>
                  ))}
                </div>

                {/* Progress indicator during upload */}
                {processing === 'bulk-upload' && uploadProgress.total > 0 && (
                  <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                        {uploadProgress.status}
                      </span>
                      <span className="text-sm text-blue-600 dark:text-blue-300">
                        {uploadProgress.current} / {uploadProgress.total}
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-3">
                      <div
                        className="bg-[#FFD12D] h-3 rounded-full transition-all duration-300"
                        style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 text-center">
                      {Math.round((uploadProgress.current / uploadProgress.total) * 100)}% complete
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    onClick={() => setUploadStep(1)}
                    variant="outline"
                    disabled={processing === 'bulk-upload'}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleBulkUploadStalls}
                    disabled={!fieldMapping.stallName || !fieldMapping.stallNumber || processing === 'bulk-upload'}
                    className="flex-1 bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D]"
                  >
                    {processing === 'bulk-upload' ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading {uploadProgress.current}/{uploadProgress.total}...
                      </>
                    ) : (
                      <>Upload {csvData.length} Stalls</>
                    )}
                  </Button>
                </div>

                {/* Inline error list — shown after upload if any rows failed */}
                {uploadErrors.length > 0 && (
                  <BulkUploadErrorsPanel errors={uploadErrors} fileNameHint="failed-stalls" />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-bold text-[#252525] dark:text-white">Export Users</h3>
                <p className="text-sm text-gray-500">{exportFilteredVendors.length} users matching filters</p>
              </div>
              <button
                onClick={() => {
                  setShowExportModal(false);
                  setSelectedVendors([]);
                  setExportFilter('all');
                  setExportSearch('');
                }}
                className="text-gray-500 hover:text-[#FFD12D]"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, email, company..."
                value={exportSearch}
                onChange={(e) => { setExportSearch(e.target.value); setSelectedVendors([]); }}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-[#252525] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#FFD12D]"
                data-testid="export-search-input"
              />
            </div>

            {/* Filter pills */}
            <div className="flex flex-wrap gap-1.5 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
              {[
                { key: 'all', label: 'All' },
                { key: 'vendor', label: 'Vendors' },
                { key: 'admin', label: 'Admins' },
                { key: 'market-vendor', label: 'Market' },
                { key: 'expo-vendor', label: 'Expo' },
                { key: 'festival-vendor', label: 'Festival' },
                { key: 'yearly', label: 'Yearly' },
                { key: 'monthly', label: 'Monthly' },
                { key: 'cash', label: 'Cash' },
                { key: 'manual', label: 'Manual' },
                { key: 'active-trial', label: 'Active Trial' },
                { key: 'expired-trial', label: 'Expired Trial' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => { setExportFilter(key); setSelectedVendors([]); }}
                  data-testid={`export-filter-${key}`}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                    exportFilter === key
                      ? 'bg-[#FFD12D] border-[#FFD12D] text-[#252525] font-semibold'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-[#FFD12D] hover:text-[#FFD12D]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Select All */}
            <div className="mb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedVendors.length === exportFilteredVendors.length && exportFilteredVendors.length > 0}
                  onChange={() => {
                    if (selectedVendors.length === exportFilteredVendors.length) {
                      setSelectedVendors([]);
                    } else {
                      setSelectedVendors(exportFilteredVendors.map(v => v._id));
                    }
                  }}
                  className="w-4 h-4 accent-[#FFD12D]"
                />
                <span className="font-semibold text-sm text-[#252525] dark:text-white">Select All ({exportFilteredVendors.length})</span>
              </label>
            </div>

            {/* User list */}
            <div className="space-y-2 mb-4 flex-1 overflow-y-auto max-h-[35vh]">
              {exportFilteredVendors.map((vendor) => {
                const stall = vendor.stall_id ? allStalls.find(s => s._id === vendor.stall_id) : null;
                const companyName = stall?.stallName || vendor.company_name;
                return (
                <label key={vendor._id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedVendors.includes(vendor._id)}
                    onChange={() => toggleVendorSelection(vendor._id)}
                    className="w-4 h-4 accent-[#FFD12D]"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-[#252525] dark:text-white">{companyName || vendor.name}</p>
                      {vendor.vendor_type && (
                        <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${
                          vendor.vendor_type === 'expo' ? 'bg-orange-100 text-orange-700' 
                          : vendor.vendor_type === 'festival' ? 'bg-pink-100 text-pink-700' 
                          : 'bg-teal-100 text-teal-700'
                        }`}>
                          {vendor.vendor_type === 'expo' ? 'Expo' : vendor.vendor_type === 'festival' ? 'Festival' : 'Market'}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{vendor.email}</p>
                  </div>
                </label>
                );
              })}
              {exportFilteredVendors.length === 0 && (
                <p className="text-center py-6 text-gray-500 text-sm">No users match the current filters</p>
              )}
            </div>

            <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={handleExport}
                className="flex-1 bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D]"
              >
                <Download className="w-4 h-4 mr-2" />
                Export {selectedVendors.length > 0 ? `(${selectedVendors.length})` : 'All'}
              </Button>
              <Button
                onClick={() => {
                  setShowExportModal(false);
                  setSelectedVendors([]);
                  setExportFilter('all');
                  setExportSearch('');
                }}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-bold text-[#252525] dark:text-white">Bulk Delete Users</h3>
                <p className="text-sm text-gray-500">{exportFilteredVendors.length} users matching filters</p>
              </div>
              <button
                onClick={() => {
                  setShowBulkDeleteModal(false);
                  setShowDeleteConfirm(false);
                  setSelectedVendors([]);
                  setExportFilter('all');
                  setExportSearch('');
                }}
                className="text-gray-500 hover:text-[#FFD12D]"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {!showDeleteConfirm ? (
              <>
                {/* Search */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search by name, email, company..."
                    value={exportSearch}
                    onChange={(e) => { setExportSearch(e.target.value); setSelectedVendors([]); }}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-[#252525] dark:text-white focus:outline-none focus:ring-2 focus:ring-red-400"
                    data-testid="delete-search-input"
                  />
                </div>

                {/* Filter pills */}
                <div className="flex flex-wrap gap-1.5 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'vendor', label: 'Vendors' },
                    { key: 'admin', label: 'Admins' },
                    { key: 'market-vendor', label: 'Market' },
                    { key: 'expo-vendor', label: 'Expo' },
                    { key: 'festival-vendor', label: 'Festival' },
                    { key: 'yearly', label: 'Yearly' },
                    { key: 'monthly', label: 'Monthly' },
                    { key: 'cash', label: 'Cash' },
                    { key: 'manual', label: 'Manual' },
                    { key: 'active-trial', label: 'Active Trial' },
                    { key: 'expired-trial', label: 'Expired Trial' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => { setExportFilter(key); setSelectedVendors([]); }}
                      data-testid={`delete-filter-${key}`}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        exportFilter === key
                          ? 'bg-red-500 border-red-500 text-white font-semibold'
                          : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-red-400 hover:text-red-500'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Select All */}
                <div className="mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedVendors.length === exportFilteredVendors.length && exportFilteredVendors.length > 0}
                      onChange={() => {
                        if (selectedVendors.length === exportFilteredVendors.length) {
                          setSelectedVendors([]);
                        } else {
                          setSelectedVendors(exportFilteredVendors.map(v => v._id));
                        }
                      }}
                      className="w-4 h-4 accent-red-500"
                    />
                    <span className="font-semibold text-sm text-[#252525] dark:text-white">Select All ({exportFilteredVendors.length})</span>
                  </label>
                </div>

                <div className="space-y-2 mb-4 flex-1 overflow-y-auto max-h-[30vh]">
                  {exportFilteredVendors.map((vendor) => {
                    const stall = vendor.stall_id ? allStalls.find(s => s._id === vendor.stall_id) : null;
                    const companyName = stall?.stallName || vendor.company_name;
                    return (
                    <label key={vendor._id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedVendors.includes(vendor._id)}
                        onChange={() => toggleVendorSelection(vendor._id)}
                        className="w-4 h-4 accent-red-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-[#252525] dark:text-white">{companyName || vendor.name}</p>
                          {vendor.vendor_type && (
                            <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${
                              vendor.vendor_type === 'expo' ? 'bg-orange-100 text-orange-700'
                              : vendor.vendor_type === 'festival' ? 'bg-pink-100 text-pink-700'
                              : 'bg-teal-100 text-teal-700'
                            }`}>
                              {vendor.vendor_type === 'expo' ? 'Expo' : vendor.vendor_type === 'festival' ? 'Festival' : 'Market'}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{vendor.email}</p>
                      </div>
                    </label>
                    );
                  })}
                  {exportFilteredVendors.length === 0 && (
                    <p className="text-center py-6 text-gray-500 text-sm">No users match the current filters</p>
                  )}
                </div>

                <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    onClick={() => {
                      if (selectedVendors.length === 0) {
                        setMessage({ type: 'error', text: 'Please select at least one vendor to delete' });
                      } else {
                        setShowDeleteConfirm(true);
                      }
                    }}
                    variant="destructive"
                    className="flex-1"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Selected ({selectedVendors.length})
                  </Button>
                  <Button
                    onClick={() => {
                      setShowBulkDeleteModal(false);
                      setSelectedVendors([]);
                      setExportFilter('all');
                      setExportSearch('');
                    }}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-8 h-8 text-red-600" />
                </div>
                <h4 className="text-lg font-bold text-[#252525] dark:text-white mb-2">
                  Confirm Deletion
                </h4>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Are you sure you want to delete {selectedVendors.length} vendor{selectedVendors.length > 1 ? 's' : ''}?
                  This action cannot be undone.
                </p>
                <div className="flex gap-2 justify-center">
                  <Button
                    onClick={handleBulkDelete}
                    disabled={processing === 'bulk-delete'}
                    variant="destructive"
                  >
                    {processing === 'bulk-delete' ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Yes, Delete
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => setShowDeleteConfirm(false)}
                    variant="outline"
                    disabled={processing === 'bulk-delete'}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Export Stalls Modal */}
      {showExportStallsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl p-6 max-w-2xl w-full border-4 border-[#FFD12D] max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-[#252525] dark:text-white flex items-center gap-2">
                  <Download className="w-5 h-5 text-[#FFD12D]" />
                  Export Stalls
                </h3>
                <p className="text-sm text-gray-500">{exportFilteredStalls.length} stalls matching filters</p>
              </div>
              <button onClick={() => { setShowExportStallsModal(false); setSelectedStallsForExport([]); setStallExportFilter('all'); setStallExportSearch(''); }} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, number, category, market..."
                value={stallExportSearch}
                onChange={(e) => { setStallExportSearch(e.target.value); setSelectedStallsForExport([]); }}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-[#252525] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#FFD12D]"
                data-testid="export-stalls-search-input"
              />
            </div>

            {/* Filter pills */}
            <div className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
              {[
                { key: 'all', label: 'All' },
                { key: 'assigned', label: 'Assigned' },
                { key: 'unassigned', label: 'Unassigned' },
                { key: 'published', label: 'Published' },
                { key: 'draft', label: 'Draft' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => { setStallExportFilter(key); setSelectedStallsForExport([]); }}
                  data-testid={`export-stall-filter-${key}`}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                    stallExportFilter === key
                      ? 'bg-[#FFD12D] border-[#FFD12D] text-[#252525] font-semibold'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-[#FFD12D] hover:text-[#FFD12D]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Select All */}
            <div className="flex items-center gap-2 p-2 mb-2">
              <input
                type="checkbox"
                id="selectAllStalls"
                checked={selectedStallsForExport.length === exportFilteredStalls.length && exportFilteredStalls.length > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedStallsForExport(exportFilteredStalls.map(s => s._id));
                  } else {
                    setSelectedStallsForExport([]);
                  }
                }}
                className="w-4 h-4 accent-[#FFD12D]"
              />
              <label htmlFor="selectAllStalls" className="font-semibold text-sm text-[#252525] dark:text-white cursor-pointer">
                Select All ({exportFilteredStalls.length})
              </label>
            </div>

            {/* Stalls List */}
            <div className="flex-1 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg mb-3 max-h-[35vh]">
              {exportFilteredStalls.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No stalls match the current filters
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {exportFilteredStalls.map((stall) => (
                    <div
                      key={stall._id}
                      className={`flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-[#252525]/50 cursor-pointer transition-colors ${
                        selectedStallsForExport.includes(stall._id) ? 'bg-[#FFD12D]/10' : ''
                      }`}
                      onClick={() => {
                        if (selectedStallsForExport.includes(stall._id)) {
                          setSelectedStallsForExport(selectedStallsForExport.filter(id => id !== stall._id));
                        } else {
                          setSelectedStallsForExport([...selectedStallsForExport, stall._id]);
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedStallsForExport.includes(stall._id)}
                        onChange={() => {}}
                        className="w-4 h-4 accent-[#FFD12D]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[#252525] dark:text-white truncate">{stall.stallName}</span>
                          <span className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded text-gray-600 dark:text-gray-400">#{stall.stallNumber}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            stall.status === 'published' 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          }`}>{stall.status || 'draft'}</span>
                        </div>
                        <p className="text-sm text-gray-500 truncate">{stall.category} {stall.market && `\u2022 ${stall.market}`}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={handleExportStalls}
                className="flex-1 bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D]"
              >
                <Download className="w-4 h-4 mr-2" />
                Export {selectedStallsForExport.length > 0 ? `(${selectedStallsForExport.length})` : 'All'}
              </Button>
              <Button
                onClick={() => { setShowExportStallsModal(false); setSelectedStallsForExport([]); setStallExportFilter('all'); setStallExportSearch(''); }}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Stalls Modal */}
      {showBulkDeleteStallsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl p-6 max-w-2xl w-full border-4 border-red-500 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-[#252525] dark:text-white flex items-center gap-2">
                  <Trash2 className="w-5 h-5 text-red-500" />
                  Bulk Delete Stalls
                </h3>
                <p className="text-sm text-gray-500">{exportFilteredStalls.length} stalls matching filters</p>
              </div>
              <button onClick={() => { setShowBulkDeleteStallsModal(false); setSelectedStallsForDelete([]); setStallExportFilter('all'); setStallExportSearch(''); }} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 rounded-lg mb-3">
              <p className="text-sm text-red-600 dark:text-red-400">
                Warning: This action cannot be undone. Deleted stalls will be permanently removed.
              </p>
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, number, category, market..."
                value={stallExportSearch}
                onChange={(e) => { setStallExportSearch(e.target.value); setSelectedStallsForDelete([]); }}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-[#252525] dark:text-white focus:outline-none focus:ring-2 focus:ring-red-400"
                data-testid="delete-stalls-search-input"
              />
            </div>

            {/* Filter pills */}
            <div className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
              {[
                { key: 'all', label: 'All' },
                { key: 'assigned', label: 'Assigned' },
                { key: 'unassigned', label: 'Unassigned' },
                { key: 'published', label: 'Published' },
                { key: 'draft', label: 'Draft' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => { setStallExportFilter(key); setSelectedStallsForDelete([]); }}
                  data-testid={`delete-stall-filter-${key}`}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                    stallExportFilter === key
                      ? 'bg-red-500 border-red-500 text-white font-semibold'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-red-400 hover:text-red-500'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Select All */}
            <div className="flex items-center gap-2 p-2 mb-2">
              <input
                type="checkbox"
                id="selectAllStallsDelete"
                checked={selectedStallsForDelete.length === exportFilteredStalls.length && exportFilteredStalls.length > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedStallsForDelete(exportFilteredStalls.map(s => s._id));
                  } else {
                    setSelectedStallsForDelete([]);
                  }
                }}
                className="w-4 h-4 accent-red-500"
              />
              <label htmlFor="selectAllStallsDelete" className="font-semibold text-sm text-red-600 dark:text-red-400 cursor-pointer">
                Select All ({exportFilteredStalls.length})
              </label>
            </div>

            {/* Stalls List */}
            <div className="flex-1 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg mb-3 max-h-[28vh]">
              {exportFilteredStalls.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No stalls match the current filters
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {exportFilteredStalls.map((stall) => (
                    <div
                      key={stall._id}
                      className={`flex items-center gap-3 p-3 hover:bg-red-50 dark:hover:bg-red-900/10 cursor-pointer transition-colors ${
                        selectedStallsForDelete.includes(stall._id) ? 'bg-red-100 dark:bg-red-900/20' : ''
                      }`}
                      onClick={() => {
                        if (selectedStallsForDelete.includes(stall._id)) {
                          setSelectedStallsForDelete(selectedStallsForDelete.filter(id => id !== stall._id));
                        } else {
                          setSelectedStallsForDelete([...selectedStallsForDelete, stall._id]);
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedStallsForDelete.includes(stall._id)}
                        onChange={() => {}}
                        className="w-4 h-4 accent-red-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[#252525] dark:text-white truncate">{stall.stallName}</span>
                          <span className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded text-gray-600 dark:text-gray-400">#{stall.stallNumber}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            stall.status === 'published' 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          }`}>{stall.status || 'draft'}</span>
                        </div>
                        <p className="text-sm text-gray-500 truncate">{stall.category} {stall.market && `\u2022 ${stall.market}`}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={handleBulkDeleteStalls}
                disabled={processing === 'bulk-delete-stalls' || selectedStallsForDelete.length === 0}
                variant="destructive"
                className="flex-1"
              >
                {processing === 'bulk-delete-stalls' ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete {selectedStallsForDelete.length > 0 ? `(${selectedStallsForDelete.length})` : 'Selected'}
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setShowBulkDeleteStallsModal(false);
                  setSelectedStallsForDelete([]);
                  setStallExportFilter('all');
                  setStallExportSearch('');
                }}
                variant="outline"
                disabled={processing === 'bulk-delete-stalls'}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Categories Modal */}
      {showCategoriesModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="bg-[#FFD12D] p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#252525] flex items-center gap-2">
                <FolderTree className="w-6 h-6" />
                Manage Categories
              </h2>
              <Button variant="ghost" size="sm" onClick={() => { setShowCategoriesModal(false); setCategoryModalMessage(null); }} className="text-[#252525] hover:bg-[#252525]/10">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              {/* Message display inside modal */}
              {categoryModalMessage && (
                <div className={`p-3 rounded-lg text-sm ${
                  categoryModalMessage.type === 'error' 
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700' 
                    : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'
                }`}>
                  {categoryModalMessage.text}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Enter categories (comma separated)"
                  className="dark:bg-[#252525] dark:text-white"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                />
                <Button onClick={handleAddCategory} className="bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D]">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Tip: Add multiple categories at once by separating them with commas
              </p>
              <SortModePicker
                kind="categories"
                mode={sortModes.categories}
                onModeChange={(m) => handleSetSortMode('categories', m)}
                onShuffle={() => handleShuffleKind('categories')}
                accent="bg-[#FFD12D] text-[#252525]"
                hideCustom
              />
              {categories.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No categories yet</p>
              ) : (
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <div key={cat} className="flex items-center justify-between px-4 py-3 bg-[#FFD12D]/10 rounded-lg border border-[#FFD12D]/30">
                      <div className="flex items-center gap-3">
                        <EditableName
                          value={cat}
                          onSave={(nn) => handleRenameItem('categories', cat, nn)}
                          textClassName="font-medium text-[#252525] dark:text-white"
                          testIdPrefix={`cat-${cat}`}
                        />
                        <span className="text-xs bg-[#FFD12D] text-[#252525] px-2 py-1 rounded-full font-bold">{categoryUsage[cat] || 0}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteCategory(cat)} className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tags Modal */}
      {showTagsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="bg-purple-500 p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Tag className="w-6 h-6" />
                Manage Tags
              </h2>
              <Button variant="ghost" size="sm" onClick={() => { setShowTagsModal(false); setTagModalMessage(null); }} className="text-white hover:bg-white/10">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              {/* Message display inside modal */}
              {tagModalMessage && (
                <div className={`p-3 rounded-lg text-sm ${
                  tagModalMessage.type === 'error' 
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700' 
                    : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'
                }`}>
                  {tagModalMessage.text}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Enter tags (comma separated)"
                  className="dark:bg-[#252525] dark:text-white"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                />
                <Button onClick={handleAddTag} className="bg-purple-500 hover:bg-purple-600 text-white">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Tip: Add multiple tags at once by separating them with commas
              </p>
              <SortModePicker
                kind="tags"
                mode={sortModes.tags}
                onModeChange={(m) => handleSetSortMode('tags', m)}
                onShuffle={() => handleShuffleKind('tags')}
                accent="bg-purple-500"
                hideCustom
              />
              {tags.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No tags yet</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <div key={tag} className="flex items-center gap-2 px-3 py-2 bg-purple-100 dark:bg-purple-900/30 text-[#252525] dark:text-white rounded-full">
                      <EditableName
                        value={tag}
                        onSave={(nn) => handleRenameItem('tags', tag, nn)}
                        textClassName=""
                        testIdPrefix={`tag-${tag}`}
                      />
                      <span className="text-xs bg-purple-500 text-white px-2 py-0.5 rounded-full font-bold">{tagUsage[tag] || 0}</span>
                      <button onClick={() => handleDeleteTag(tag)} className="hover:text-red-500 transition-colors">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Markets Modal */}
      {showMarketsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="bg-blue-500 p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <MapPin className="w-6 h-6" />
                Manage Markets
              </h2>
              <Button variant="ghost" size="sm" onClick={() => { setShowMarketsModal(false); setMarketModalMessage(null); }} className="text-white hover:bg-white/10">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              {/* Message display inside modal */}
              {marketModalMessage && (
                <div className={`p-3 rounded-lg text-sm ${
                  marketModalMessage.type === 'error' 
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700' 
                    : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'
                }`}>
                  {marketModalMessage.text}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  value={newMarket}
                  onChange={(e) => setNewMarket(e.target.value)}
                  placeholder="Enter markets (comma separated)"
                  className="dark:bg-[#252525] dark:text-white"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddMarket()}
                />
                <Button onClick={handleAddMarket} className="bg-blue-500 hover:bg-blue-600 text-white">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Tip: Add multiple markets at once by separating them with commas
              </p>
              <SortModePicker
                kind="markets"
                mode={sortModes.markets}
                onModeChange={(m) => handleSetSortMode('markets', m)}
                onShuffle={() => handleShuffleKind('markets')}
                accent="bg-blue-500"
              />
              {markets.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No markets yet</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Drag markets to reorder them
                  </p>
                  {markets.map((market, index) => (
                    <div 
                      key={market} 
                      draggable
                      onDragStart={(e) => handleMarketDragStart(e, market)}
                      onDragOver={(e) => handleMarketDragOver(e, market)}
                      onDrop={(e) => handleMarketDrop(e, market)}
                      onDragEnd={handleMarketDragEnd}
                      className={`flex items-center justify-between px-4 py-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 cursor-move transition-all ${
                        draggedMarket === market ? 'opacity-50 scale-95' : ''
                      } ${draggedMarket && draggedMarket !== market ? 'border-dashed border-blue-400' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-5 h-5 text-gray-400 cursor-grab active:cursor-grabbing" />
                        <span className="text-xs text-gray-400 font-mono w-6">{index + 1}</span>
                        <MapPin className="w-4 h-4 text-blue-500" />
                        <EditableName
                          value={market}
                          onSave={(nn) => handleRenameItem('markets', market, nn)}
                          textClassName="font-medium text-[#252525] dark:text-white"
                          testIdPrefix={`market-${market}`}
                        />
                        <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full font-bold">{marketUsage[market] || 0}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteMarket(market)} className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Expos Modal */}
      {showExposModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="bg-purple-500 p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Tent className="w-6 h-6" />
                Manage Expos
              </h2>
              <Button variant="ghost" size="sm" onClick={() => { setShowExposModal(false); setExpoModalMessage(null); }} className="text-white hover:bg-white/10">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              {expoModalMessage && (
                <div className={`p-3 rounded-lg text-sm ${expoModalMessage.type === 'error' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'}`}>
                  {expoModalMessage.text}
                </div>
              )}
              <div className="flex gap-2">
                <Input value={newExpo} onChange={(e) => setNewExpo(e.target.value)} placeholder="Add expos (comma separated)" className="dark:bg-[#252525] dark:text-white" onKeyPress={(e) => e.key === 'Enter' && handleAddExpo()} />
                <Button onClick={handleAddExpo} className="bg-purple-500 hover:bg-purple-600 text-white"><Plus className="w-4 h-4" /></Button>
              </div>
              <SortModePicker
                kind="expos"
                mode={sortModes.expos}
                onModeChange={(m) => handleSetSortMode('expos', m)}
                onShuffle={() => handleShuffleKind('expos')}
                accent="bg-purple-500"
              />
              {expos.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No expos yet</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Drag expos to reorder them</p>
                  {expos.map((expo, index) => (
                    <div key={expo} draggable onDragStart={(e) => handleExpoDragStart(e, expo)} onDragOver={(e) => handleExpoDragOver(e, expo)} onDragEnd={handleExpoDragEnd}
                      className={`flex items-center justify-between px-4 py-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800 cursor-move transition-all ${draggedExpo === expo ? 'opacity-50 scale-95' : ''} ${draggedExpo && draggedExpo !== expo ? 'border-dashed border-purple-400' : ''}`}>
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-5 h-5 text-gray-400 cursor-grab active:cursor-grabbing" />
                        <span className="text-xs text-gray-400 font-mono w-6">{index + 1}</span>
                        <Tent className="w-4 h-4 text-purple-500" />
                        <EditableName
                          value={expo}
                          onSave={(nn) => handleRenameItem('expos', expo, nn)}
                          textClassName="font-medium text-[#252525] dark:text-white"
                          testIdPrefix={`expo-${expo}`}
                        />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteExpo(expo)} className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Festivals Modal */}
      {showFestivalsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="bg-orange-500 p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <PartyPopper className="w-6 h-6" />
                Manage Festivals
              </h2>
              <Button variant="ghost" size="sm" onClick={() => { setShowFestivalsModal(false); setFestivalModalMessage(null); }} className="text-white hover:bg-white/10">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              {festivalModalMessage && (
                <div className={`p-3 rounded-lg text-sm ${festivalModalMessage.type === 'error' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'}`}>
                  {festivalModalMessage.text}
                </div>
              )}
              <div className="flex gap-2">
                <Input value={newFestival} onChange={(e) => setNewFestival(e.target.value)} placeholder="Add festivals (comma separated)" className="dark:bg-[#252525] dark:text-white" onKeyPress={(e) => e.key === 'Enter' && handleAddFestival()} />
                <Button onClick={handleAddFestival} className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4" /></Button>
              </div>
              <SortModePicker
                kind="festivals"
                mode={sortModes.festivals}
                onModeChange={(m) => handleSetSortMode('festivals', m)}
                onShuffle={() => handleShuffleKind('festivals')}
                accent="bg-orange-500"
              />
              {festivals.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No festivals yet</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Drag festivals to reorder them</p>
                  {festivals.map((festival, index) => (
                    <div key={festival} draggable onDragStart={(e) => handleFestivalDragStart(e, festival)} onDragOver={(e) => handleFestivalDragOver(e, festival)} onDragEnd={handleFestivalDragEnd}
                      className={`flex items-center justify-between px-4 py-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800 cursor-move transition-all ${draggedFestival === festival ? 'opacity-50 scale-95' : ''} ${draggedFestival && draggedFestival !== festival ? 'border-dashed border-orange-400' : ''}`}>
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-5 h-5 text-gray-400 cursor-grab active:cursor-grabbing" />
                        <span className="text-xs text-gray-400 font-mono w-6">{index + 1}</span>
                        <PartyPopper className="w-4 h-4 text-orange-500" />
                        <EditableName
                          value={festival}
                          onSave={(nn) => handleRenameItem('festivals', festival, nn)}
                          textClassName="font-medium text-[#252525] dark:text-white"
                          testIdPrefix={`festival-${festival}`}
                        />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteFestival(festival)} className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Legal Content Modal */}
      {showLegalModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="bg-emerald-500 p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <FileText className="w-6 h-6" />
                Manage Legal Content
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowLegalModal(false)} className="text-white hover:bg-white/10">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* Document Type Selector */}
              <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 pb-4">
                {[
                  { key: 'terms', label: 'Terms & Conditions' },
                  { key: 'privacy', label: 'Privacy Policy' },
                  { key: 'other', label: 'Other' }
                ].map(({ key, label }) => (
                  <Button
                    key={key}
                    onClick={() => setSelectedLegalType(key)}
                    variant={selectedLegalType === key ? 'default' : 'outline'}
                    className={selectedLegalType === key 
                      ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
                      : 'border-gray-300 dark:border-gray-600 dark:text-white'
                    }
                  >
                    {label}
                  </Button>
                ))}
              </div>

              {legalLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Title Input */}
                  <div>
                    <label className="block text-sm font-semibold text-[#252525] dark:text-white mb-2">
                      Title
                    </label>
                    <Input
                      value={legalContent[selectedLegalType]?.title || ''}
                      onChange={(e) => setLegalContent(prev => ({
                        ...prev,
                        [selectedLegalType]: {
                          ...prev[selectedLegalType],
                          title: e.target.value
                        }
                      }))}
                      placeholder="Enter title"
                      className="dark:bg-[#252525] dark:text-white"
                    />
                  </div>

                  {/* Content Rich Text Editor */}
                  <div>
                    <label className="block text-sm font-semibold text-[#252525] dark:text-white mb-2">
                      Content
                    </label>
                    <SimpleRichTextEditor
                      value={legalContent[selectedLegalType]?.content || ''}
                      onChange={(value) => setLegalContent(prev => ({
                        ...prev,
                        [selectedLegalType]: {
                          ...prev[selectedLegalType],
                          content: value
                        }
                      }))}
                      placeholder="Enter legal content here..."
                    />
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      onClick={() => setShowLegalModal(false)}
                      variant="outline"
                      className="border-gray-300 dark:border-gray-600 dark:text-white"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        if (window.confirm('Reset this content to the original default text? This will discard any changes.')) {
                          setLegalContent(prev => ({
                            ...prev,
                            [selectedLegalType]: defaultLegalContent[selectedLegalType]
                          }));
                        }
                      }}
                      variant="outline"
                      className="border-orange-400 text-orange-600 hover:bg-orange-50 dark:border-orange-500 dark:text-orange-400 dark:hover:bg-orange-900/20"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reset to Default
                    </Button>
                    <Button
                      onClick={handleSaveLegalContent}
                      disabled={legalLoading}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    >
                      {legalLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Management Dashboard */}
      <EmailDashboardModal
        isOpen={showEmailDashboard}
        onClose={() => setShowEmailDashboard(false)}
        token={token}
        allVendors={allVendors}
        setMessage={setMessage}
      />

      {/* Provinces */}
      <ProvincesModal
        isOpen={showProvincesModal}
        onClose={() => setShowProvincesModal(false)}
        token={token}
        setMessage={setMessage}
      />

      </div>
    </div>
  );
};

export default AdminDashboard;
